import clamp from 'lodash/clamp';
import omit from 'lodash/omit';
import {type Draft, produce} from 'immer';
import {increment} from '@packages/utilities/string-utils';
import {getRelativePath} from '@packages/utilities/platform';
import {
  Interaction,
  Timeline,
  TimelineAction,
  ActionPreset,
  TimelineControlType,
  isValidControlType,
  RawTimeline,
  RawInteraction,
  RawActionPreset,
  type Trigger,
  type TargetDefinition,
  type SecondsOrMs,
  PageScope,
  TimelineId,
  PERCENT_CANVAS_DURATION_S,
  DEFAULTS,
  TweenType,
  InteractionId,
} from '@packages/systems/ix3/core';
import {
  PAGE_SWITCHED,
  TAB_SELECTED,
  TAB_SELECTED_WITH_HIGHLIGHT,
  RAW_SITE_DATA_RECEIVED,
  RAW_BRANCHED_DATA_RECEIVED,
  DESIGNER_LOADED_IN_VIEW_MODE,
  DESIGNER_LOADED_IN_EDIT_MODE,
  ROOT_SYMBOL_NODE_REMOVED,
  COMPONENT_ROOT_STYLE_VARIANT_DELETED,
  SYMBOL_NODE_UNFOCUSED,
  NODE_CLICKED,
  PARENT_SYMBOL_OF_NODE_IN_INSTANCE_FOCUSED,
  type ParentSymbolOfNodeInInstanceFocusedPayload,
  UNDO,
  REDO,
} from '@packages/systems/designer/actions';

import {
  type Tab,
  RIGHT_TABS,
} from '@packages/systems/design-system/constants/DesignerUI';
import type {
  DesignerAction as _DesignerAction,
  DesignerStores,
} from '@packages/systems/designer/flux';

import {
  getColorOfNextTimelineAction,
  getNextPositionForTimelineAction,
  sortActionsByStartTime,
  sortActionsByIndex,
  generateTimelineActionId,
  generateTimelineId,
  sortConditionalPlayback,
  createMultiTimelineSet,
  generateGroupId,
  createTrigger,
  triggersAfterDelete,
  triggersAfterUpdate,
} from './utils';

import {type IX3Action, ix3ActionTypes} from './actions';
import {
  IX3_ACTION_ANIMATION_PROPS,
  IX3_ACTION_DURATION,
  IX3_MAX_TIMELINE_GROUPS,
  IX3_TIMELINE_HEIGHT,
  IX3_TIMELINE_HEIGHT_MIN,
  IX3_TIMELINE_HEIGHT_MAX,
} from './constants';
import {BUILT_IN_DEFAULTS, BUILT_IN_PRESETS} from './defaultPresets';
import {
  selectInteractions,
  getInteractionsAffectedByVariantDeletion,
} from './selectors';

import type {ComponentInteractionPatch, IX3State} from './types';
import {selectCurrentPageId} from '@packages/systems/designer/page';
import {type NativeId} from '@packages/systems/core/utils/NativeIdUtils';
import {
  actionPresetTransforms,
  DEFAULT_FILTER_CONTEXT,
  getMultiTimelineRoles,
  interactionTransforms,
  timelineTransforms,
  MAX_CANVAS_DURATION_S,
  TRIGGER_REQUIRES_TARGET_KEYS,
  triggerRoutesByCallbackRole,
  triggerRoutesByContinuousRole,
  timelineRolesForTriggerKey,
  isNewModelRoleOwner,
  RESERVED_PLUGIN_CONFIG_KEY_PREFIX,
  ROLE_ROUTED_NO_PLAYBACK_KEYS,
  findComponentBackdropRoleError,
  INSTANCE_TRIGGER_OWNERS,
  type OverlayTriggerOwner,
  ONE_SHOT_TRIGGER_KEYS,
  isOneShotFirstWinsShown,
} from '@packages/systems/ix3/schema';
import {
  detectTargetFormat,
  shouldAutoMigrate,
  migrateTarget,
  migrateIdToSelector,
  migrateFilterByRecursive,
  repairIncorrectlyMigratedTarget,
  repairMissingFilterBy,
  getComponentInstancePaths,
} from '@packages/systems/ix3/shared';
import {
  instPathKey,
  readInstPathsFromTriggers,
} from '@packages/systems/core-constants';
import {
  applyOnActionPresetActionStoredHooks,
  applyOnTimelinesLoadedHooks,
  applyOnTimelineActionCreatedHooks,
  applyOnTimelineActionCreatedDraftHooks,
  applyOnTimelineActionDeletedDraftHooks,
  applyOnTimelineActionDuplicatedHooks,
  applyOnTimelineActionEditedDraftHooks,
  applyOnTimelinesDuplicatedHooks,
  isContinuousTimelineAction,
} from './reducer-hooks';
import {
  IX3_WF_EXTENSION_KEYS,
  isComponentInstanceTriggerKey,
} from '@packages/systems/ix3-wf-plugins/shared';

type DesignerAction<T, P = unknown> = _DesignerAction<T, P>;

/**
 * Seeding a role-less timeline into percent mode must also clear its
 * timeline-level playback defaults (delay/repeat/repeatDelay/stagger). The
 * runtime applies those to every tween via `convertToGsapDefaults`, but the
 * percent clamps only account for per-action timing — a leftover default
 * delay/repeat would extend tweens past the 100% marker, and a leftover default
 * stagger spreads a multi-target action (which has no per-action stagger of its
 * own to budget) past it too, rescaling the scrubbed/published timeline. The
 * editor hides the playback gear for continuous timelines, so these defaults are
 * otherwise stranded and invisible.
 */
function clearTimelinePlaybackDefaults(
  timeline: Draft<NonNullable<IX3State['timelines'][string]>>
): void {
  if (timeline.settings == null) {
    return;
  }
  timeline.settings.delay = undefined;
  timeline.settings.repeat = undefined;
  timeline.settings.repeatDelay = undefined;
  timeline.settings.stagger = undefined;
}

/**
 * Seeding a role-less timeline into percent mode reinterprets its existing
 * action starts/durations as fractions of the new `canvasDuration`. Two
 * problems follow for actions authored before the toggle:
 *  - A concrete start (or base + repeat tail) sized on the old seconds canvas
 *    can sit past 100%, growing/relabeling the percent canvas.
 *  - The schema permits relative/alignment starts (e.g. `'+=0.8'`, `'<'`). The
 *    percent track geometry and inputs read those strings as 0, but GSAP still
 *    resolves them relatively at build time — so the scrubbed/published
 *    timeline plays at a later percent than the editor shows.
 *
 * Normalize each role-less action the same way the create path clamps a new
 * one: cap the stored base (and an over-budget `repeatDelay`) so a single cycle
 * fits even at start 0, materialize every start to a concrete number, and slide
 * it back so the action's full footprint (base + bounded stagger + finite
 * repeat tail) ends at or before the gesture end. An infinite repeat or an
 * unbounded per-item (`each`) stagger pins the start to 0.
 */
function clampTimelineActionsToPercentCanvas(
  timeline: Draft<NonNullable<IX3State['timelines'][string]>>,
  canvasDuration: number
): void {
  for (const action of timeline.actions) {
    const {timing} = action;
    const rawPosition =
      typeof timing.position === 'string' && timing.position.endsWith('ms')
        ? parseFloat(timing.position) / 1000
        : Number(timing.position);
    const positionUnsupported = !Number.isFinite(rawPosition);
    const newStart = positionUnsupported ? 0 : rawPosition;

    const rawDuration =
      typeof timing.duration === 'string' && timing.duration.endsWith('ms')
        ? parseFloat(timing.duration) / 1000
        : Number(timing.duration);
    // A Set action is instantaneous (no base); a non-Set with no explicit
    // duration renders at DEFAULTS.DURATION, so clamp against that.
    const newExtent =
      action.tt === TweenType.Set
        ? 0
        : Number.isFinite(rawDuration)
          ? rawDuration
          : DEFAULTS.DURATION;

    // A total ('amount') stagger spread is fixed; a per-item ('each') offset
    // scales with the unresolved target count, so treat it as unbounded and pin
    // the start to 0 below.
    const staggerEachUnbounded = timing.stagger?.each != null;
    const staggerSpreadSeconds =
      !staggerEachUnbounded && timing.stagger?.amount != null
        ? typeof timing.stagger.amount === 'string' &&
          timing.stagger.amount.endsWith('ms')
          ? parseFloat(timing.stagger.amount) / 1000
          : Number(timing.stagger.amount) || 0
        : 0;

    let fullExtent = 0;
    // GSAP applies `repeat`/`repeatDelay` to a zero-duration `tl.set` too, and a
    // bounded total stagger spreads even an instantaneous Set across its targets —
    // so a Set carrying a repeat or stagger enters the folding branch even though
    // `newExtent` is 0. Mirrors the add-action create path.
    const repeats = timing.repeat ?? 0;
    if (newExtent > 0 || repeats !== 0 || staggerSpreadSeconds > 0) {
      let repeatDelaySeconds =
        typeof timing.repeatDelay === 'string' &&
        timing.repeatDelay.endsWith('ms')
          ? parseFloat(timing.repeatDelay) / 1000
          : Number(timing.repeatDelay) || 0;
      // The repeat-delay budget alone can overrun the canvas (e.g. repeat 2 +
      // repeatDelay 0.6 on a 1s canvas → a 1.2s tail). Clamp the delay — rounded
      // down to the persisted precision — so the footprint fits even at base 0.
      if (repeats > 0 && repeatDelaySeconds * repeats > canvasDuration) {
        repeatDelaySeconds = Math.floor((canvasDuration / repeats) * 100) / 100;
        timing.repeatDelay = repeatDelaySeconds;
      }
      // The largest single cycle (base + stagger spread) whose full repeated
      // footprint still fits at start 0. An infinite repeat (-1) pins the start to
      // 0 below, but its first cycle still renders, so it gets the whole canvas as
      // its cycle budget; a finite repeat divides the canvas across `repeat + 1`.
      const maxSingleCycle =
        repeats < 0
          ? canvasDuration
          : (canvasDuration - repeatDelaySeconds * repeats) / (repeats + 1);
      const maxBase = Math.max(0, maxSingleCycle - staggerSpreadSeconds);
      const cappedExtent =
        newExtent > maxBase ? Math.floor(maxBase * 100) / 100 : newExtent;
      if (cappedExtent < newExtent) {
        timing.duration = cappedExtent;
      }
      // Cap a bounded stagger spread to whatever the cycle budget leaves after the
      // (capped) base, so base + spread fits a cycle even when the base is 0 — a
      // Set with a large stagger amount, or a normal action whose `stagger.amount`
      // alone overruns the canvas (e.g. duration 0.2 + amount 1.2 on a 1s canvas).
      // Without this the seeded action keeps a stagger tail past 100% that
      // Start-slide can't rescue. Materialize the reduced amount so the saved bar
      // matches the clamp.
      const maxSpread = Math.max(
        0,
        Math.floor((maxSingleCycle - cappedExtent) * 100) / 100
      );
      const cappedSpread = Math.min(staggerSpreadSeconds, maxSpread);
      if (
        cappedSpread < staggerSpreadSeconds &&
        timing.stagger?.amount != null
      ) {
        timing.stagger = {
          ...timing.stagger,
          amount: cappedSpread,
        };
      }
      fullExtent =
        repeats < 0
          ? canvasDuration
          : (cappedExtent + cappedSpread) * (repeats + 1) +
            repeatDelaySeconds * repeats;
    }

    const maxStart = staggerEachUnbounded
      ? 0
      : Math.max(0, canvasDuration - fullExtent);
    if (newStart > maxStart || positionUnsupported) {
      // Floor to the persisted 2dp precision (not half-up): `maxStart` is the
      // safe ceiling, so rounding it up would store an end past the 100% marker.
      timing.position =
        Math.floor(Math.min(newStart, maxStart) * 100 + 1e-9) / 100;
    }
  }
}

export const initialState: IX3State = {
  // Action Presets
  actionPresetGroups: {},
  actionPresets: {},

  // Interactions
  interactions: {},
  timelines: {},

  selectedInteractionId: null,
  selectedTimelineActionIds: [],

  // UI
  uiShowAddAction: false,
  uiShowCanvasTargetOutlines: false,
  uiShowTimeline: true,
  uiTimelineHeight: IX3_TIMELINE_HEIGHT,
  uiIsScrubbing: false,
  uiScrollPreviewMode: false,
  uiSelectedTimelineIndex: 0,
};

type AllActions =
  | IX3Action
  | DesignerAction<typeof UNDO>
  | DesignerAction<typeof REDO>
  | DesignerAction<typeof PAGE_SWITCHED>
  | DesignerAction<typeof DESIGNER_LOADED_IN_VIEW_MODE>
  | DesignerAction<typeof DESIGNER_LOADED_IN_EDIT_MODE>
  | DesignerAction<typeof ROOT_SYMBOL_NODE_REMOVED, {payload: string}>
  | DesignerAction<
      typeof COMPONENT_ROOT_STYLE_VARIANT_DELETED,
      {payload: {variantFieldId: string; variantId: string}}
    >
  | DesignerAction<typeof SYMBOL_NODE_UNFOCUSED>
  | DesignerAction<typeof NODE_CLICKED, {payload: {nativeIdPath?: NativeId[]}}>
  | DesignerAction<
      typeof PARENT_SYMBOL_OF_NODE_IN_INSTANCE_FOCUSED,
      {
        payload: ParentSymbolOfNodeInInstanceFocusedPayload;
      }
    >
  | DesignerAction<typeof TAB_SELECTED, {tab: Tab}>
  | DesignerAction<typeof TAB_SELECTED_WITH_HIGHLIGHT, {tab: Tab}>
  | {
      type: typeof RAW_SITE_DATA_RECEIVED;
      siteData: {
        ix3Interactions?: RawInteraction[];
        ix3Timelines?: RawTimeline[];
        ix3ActionPresets?: RawActionPreset[];
      };
    }
  | {
      type: typeof RAW_BRANCHED_DATA_RECEIVED;
      payload: {
        branchedData: {
          ix3Interactions?: RawInteraction[];
          ix3Timelines?: RawTimeline[];
          ix3ActionPresets?: RawActionPreset[];
        };
      };
    };

/**
 * In multi-timeline mode (2+ action groups) the only supported trigger Control
 * is `play`. Rewrite any non-Play control on the interaction's STANDARD routed
 * triggers so stale values don't survive the transition. Mutates the (immer
 * draft) trigger tuples in place. See IX3TriggerEditor, which locks the Control
 * dropdown to Play.
 *
 * Only STANDARD routed triggers are coerced: their control flows through
 * getEffectivePlaybackConfig for grouped timelines, so a stale non-Play value
 * would mis-drive a group. LOAD/SCROLL/CONTINUOUS triggers ignore group routing
 * but still iterate every timeline at runtime, so rewriting their control to
 * `play` would make e.g. a Page Load set to None/Reverse silently start playing
 * every group — leave those untouched. Mirrors isStandardRoutedTrigger gating
 * elsewhere in this reducer.
 */
function coerceTriggerControlsToPlay(triggers: Trigger[]): void {
  for (const trigger of triggers) {
    if (!isStandardRoutedTrigger(trigger)) continue;
    if (trigger[1].control !== 'play') {
      trigger[1] = {...trigger[1], control: 'play'};
    }
  }
}

/**
 * Collect the playback timing (delay/jump/speed) to migrate onto a group's
 * timeline settings when a single-timeline interaction transitions into
 * multi-timeline mode. A single-timeline interaction can carry more than one
 * trigger, and each reads its own Delay/Jump/Speed from the trigger config at
 * runtime (getEffectivePlaybackConfig's single-timeline branch). Once the
 * timeline gains a `groupId` the runtime reads timing from `timeline.settings`
 * and the transition strips timing from STANDARD triggers, so timing authored
 * on any STANDARD trigger other than the first would be lost if we only read
 * `triggers[0]`. Merge across all STANDARD triggers with first-defined-wins
 * per field so authored timing survives regardless of which one carried it.
 *
 * Only STANDARD triggers are scanned: grouped timelines read timing from
 * `timeline.settings`, and only STANDARD triggers route to a group
 * (isStandardRoutedTrigger). LOAD/SCROLL/CONTINUOUS keep their own
 * trigger-level/binding-level timing and are left untouched — folding their
 * timing into group settings would silently apply it to the STANDARD triggers
 * routed to that group (e.g. a Page Load delay leaking onto an undelayed
 * Click).
 */
function collectMigratedTriggerTiming(
  triggers: Trigger[]
): Pick<Trigger[1], 'delay' | 'jump' | 'speed'> {
  const timing: Pick<Trigger[1], 'delay' | 'jump' | 'speed'> = {};
  for (const trigger of triggers) {
    if (!isStandardRoutedTrigger(trigger)) continue;
    const {delay, jump, speed} = trigger[1];
    if (timing.delay == null && delay != null) timing.delay = delay;
    if (timing.jump == null && jump != null) timing.jump = jump;
    if (timing.speed == null && speed != null) timing.speed = speed;
  }
  return timing;
}

/**
 * Migrate Delay/Jump/Speed authored on the interaction's STANDARD triggers onto
 * a group timeline's `settings`, then strip those fields from those triggers.
 * Used when a single-timeline interaction transitions into multi-timeline mode
 * (IX3_TIMELINE_GROUP_ADDED).
 *
 * The single timeline read its timing from the trigger config
 * (getEffectivePlaybackConfig's single-timeline branch). Once it has a groupId
 * the runtime reads timing from `timeline.settings` instead — Jump/Speed
 * silently stop applying and the trigger Delay keeps firing but the UI hides it
 * — so timing is moved to the group and cleared from the triggers so nothing
 * double-applies or resurfaces. Timing is merged across all STANDARD triggers
 * (see collectMigratedTriggerTiming); LOAD/SCROLL/CONTINUOUS keep their own
 * trigger-level timing. The apply and strip halves must stay in lockstep, so
 * they live together here rather than being copy-pasted per caller. No-op apply
 * when `timeline` is undefined; the strip still runs (mirrors the prior inline
 * behavior, where an absent first timeline would never occur in practice).
 */
function migrateStandardTriggerTimingToTimeline(
  triggers: Trigger[],
  timeline: Draft<NonNullable<IX3State['timelines'][string]>> | undefined
): void {
  const {delay, jump, speed} = collectMigratedTriggerTiming(triggers);
  if (timeline && (delay != null || jump != null || speed != null)) {
    timeline.settings = {
      ...timeline.settings,
      ...(delay != null ? {delay} : {}),
      ...(jump != null ? {jump} : {}),
      ...(speed != null ? {speed} : {}),
    };
  }
  for (const trigger of triggers) {
    if (!isStandardRoutedTrigger(trigger)) continue;
    if (
      trigger[1].delay != null ||
      trigger[1].jump != null ||
      trigger[1].speed != null
    ) {
      const {delay: _d, jump: _j, speed: _s, ...rest} = trigger[1];
      trigger[1] = rest;
    }
  }
}

/**
 * Apply `mapOutcome` to both branches of a trigger's conditional logic
 * (`ifTrue`, and `ifFalse` when present), returning a new conditionalLogic
 * object. Centralizes the identical ifTrue/ifFalse traversal used by the
 * group-removal and split-group flows, each of which only differs in how a
 * single outcome's `targetTimelineId` is rewritten when a group timeline is
 * removed/remapped.
 */
export function mapConditionalOutcomes<
  O extends {targetTimelineId?: string},
  CL extends {ifTrue: O; ifFalse?: O},
>(cl: CL, mapOutcome: (outcome: O) => O): CL {
  return {
    ...cl,
    ifTrue: mapOutcome(cl.ifTrue),
    ...(cl.ifFalse ? {ifFalse: mapOutcome(cl.ifFalse)} : {}),
  };
}

/**
 * Pin a trigger's targetless conditional outcomes to `timelineId`, for a trigger
 * that is about to lose the static role route they were relative to.
 *
 * A targetless outcome means "the timeline of the matching role" only while the
 * trigger is pinned to a role (`findConditionalOutcomeError`). Once the pin is
 * gone the outcome has no target, which the invariant rejects, leaving the
 * interaction uncommittable. Pinned to the role's timeline it stays valid and,
 * with the trigger parked, inert, which is what unassigned means. `undefined`
 * (no live timeline carries the role) leaves the logic alone.
 */
function pinRoleRelativeOutcomes<
  O extends {targetTimelineId?: string; control: string},
  CL extends {ifTrue: O; ifFalse?: O},
>(cl: CL, timelineId: string | undefined): CL {
  if (timelineId === undefined) return cl;
  return mapConditionalOutcomes<O, CL>(cl, (outcome) =>
    outcome.control !== 'none' && outcome.targetTimelineId == null
      ? {...outcome, targetTimelineId: timelineId}
      : outcome
  );
}

/** The first live timeline of `interaction` that carries `role`. */
function liveRoleTimelineId(
  interaction: {timelineIds: readonly string[]},
  timelines: IX3State['timelines'],
  role: string | undefined
): string | undefined {
  if (role == null) return undefined;
  return interaction.timelineIds.find((id) => {
    const timeline = timelines[id];
    return (
      timeline != null &&
      !timeline.deleted &&
      timeline.triggerMetadata?.role === role
    );
  });
}

/**
 * The config `IX3_TRIGGER_TIMELINE_ASSIGNED` persists when `trigger` is assigned
 * to `groupId` (`undefined` is "All groups"): a group that no longer exists
 * falls back to `null`, and a conditional outcome is retargeted into the new
 * group. Exported so the Designer's Assign guard validates the config the
 * reducer will write, not the one the trigger has now. `timelines` are the
 * interaction's, in `timelineIds` order.
 */
export function resolveTriggerGroupAssignment(
  trigger: Trigger,
  groupId: string | null | undefined,
  timelines: readonly (Timeline | undefined)[]
): Trigger[1] {
  // Validate the target group still exists among the interaction's live
  // timelines before persisting the assignment. In a multiplayer race —
  // a peer removes group X while this "assign to X" is in flight — a blind
  // write would leave a dangling `assignedGroupId` that routes to "play
  // nothing" at runtime and shows an orphan warning the Assign control
  // can't clear (no matching option). Fall back to the explicit `null`
  // ("unassigned") in that case; an intentional `null`/`undefined` passes
  // through unchanged. See the removal path's null-parking (below), which
  // only fixes triggers assigned *before* the group was removed.
  const groupExists =
    groupId != null &&
    timelines.some((tl) => tl != null && !tl.deleted && tl.groupId === groupId);
  const nextGroupId = groupId == null || groupExists ? groupId : null;

  // A trigger's conditionalLogic may still target a timeline from its
  // previous group. At runtime a STANDARD trigger assigned to a concrete
  // group is narrowed to that group's timelines
  // (resolveAssignedTimelineIds), and executeConditionalOutcome
  // intersects `targetTimelineId` with that narrowed set, so an outcome
  // pointing at the old group's timeline resolves to nothing and the
  // condition silently does nothing after the reassign. Only remap when
  // landing on a concrete group: `undefined` ("All groups") drives every
  // timeline with no narrowing at all, so an existing target is still
  // valid and must be left untouched — clearing it here would silently
  // broaden a condition the user intentionally scoped to one group.
  // `null` ("explicit unassigned") drives nothing regardless, so
  // remapping there would be moot anyway. Mirrors the group-removal /
  // split-group outcome remaps below.
  const cl = trigger[1].conditionalLogic;
  if (nextGroupId != null && cl) {
    const nextGroupTimelines = timelines.filter(
      (tl): tl is Timeline =>
        tl != null && !tl.deleted && tl.groupId === nextGroupId
    );
    const nextGroupTimelineIds = new Set<string>(
      nextGroupTimelines.map((tl) => tl.id)
    );
    // Repoint at a live timeline in the destination group rather than
    // clearing the pin. A targetless non-`none` outcome is only valid for
    // a role-relative trigger (findOutcomeError / the conditions editor
    // both require a target otherwise), and this path only runs for a
    // concrete `assignedGroupId` — the generic model. Clearing would leave
    // the Assign operation with state the Designer immediately flags as
    // invalid while the runtime silently broadened the condition to every
    // timeline in the destination group. `none` outcomes keep no target:
    // the editor doesn't ask for one and the invariant forbids it. Mirrors
    // the collapse path's `remapOutcomeTarget`.
    const nextGroupPrimaryTimelineId = nextGroupTimelines[0]?.id;
    const retargetForeignOutcome = <
      O extends {targetTimelineId?: string; control?: string},
    >(
      outcome: O
    ): O => {
      // A callback-routed trigger is exempt from BOTH repoints below. The
      // emitted `timeline-role` precedes the static fields at runtime, so
      // the `assignedGroupId` being written here never narrows anything —
      // the outcome stays pinned to its role timeline and keeps running.
      // Retargeting it to the destination group's primary is what breaks
      // it: the role's callback then intersects the pin to an empty set,
      // or worse matches a DIFFERENT role's timeline and runs the outcome
      // for the wrong one. Covers `foreign` as well as `targetless`, for
      // mixed data — role timelines that also carry `groupId`s.
      if (triggerRoutesByCallbackRole(trigger[0], trigger[1])) {
        return outcome;
      }
      // An ABSENT target needs the same repoint, not just a foreign one.
      // A trigger pinned to `assignedTimelineRole` with no group is
      // allowed a targetless outcome — it means "the matching role" — but
      // static routing checks the group FIRST, so the concrete group being
      // assigned here strips that meaning: `findConditionalOutcomeError`
      // rejects the result and the Designer flags it the moment the assign
      // lands.
      const targetless =
        outcome.targetTimelineId == null && outcome.control !== 'none';
      const foreign =
        outcome.targetTimelineId != null &&
        !nextGroupTimelineIds.has(outcome.targetTimelineId);
      if (!foreign && !targetless) return outcome;
      return {
        ...outcome,
        targetTimelineId:
          outcome.control === 'none' ? undefined : nextGroupPrimaryTimelineId,
      };
    };

    return {
      ...trigger[1],
      assignedGroupId: nextGroupId,
      assignedTimelineRole: undefined,
      conditionalLogic: mapConditionalOutcomes(cl, retargetForeignOutcome),
    };
  }
  return {
    ...trigger[1],
    assignedGroupId: nextGroupId,
    assignedTimelineRole: undefined,
  };
}

/**
 * Whether the interaction is in multi-timeline mode: 2+ live timelines carry a
 * user-managed `groupId`. Mirrors `selectTimelineGroups` (selectors.ts) for use
 * inside reducers, which have no selector access.
 */
function isMultiTimelineInteraction(
  interaction: {timelineIds: readonly TimelineId[]},
  timelines: IX3State['timelines']
): boolean {
  let groupCount = 0;
  for (const id of interaction.timelineIds) {
    const timeline = timelines[id];
    if (timeline && !timeline.deleted && timeline.groupId != null) {
      groupCount++;
      if (groupCount > 1) return true;
    }
  }
  return false;
}

/**
 * A trigger's `assignedInstances`, narrowed to the instances the interaction
 * actually names.
 *
 * Two callers: the author assigning from the field, and the prune that runs when
 * a component trigger is deleted or retargeted. A path the interaction no longer
 * names routes to nothing, can't be cleared from the field, and is rejected
 * outright by `findAssignedInstancesError` on the API path — so a stale one is
 * not a cosmetic problem.
 *
 * `undefined` means every instance the interaction names, which is the default a
 * newly added trigger arrives with and what keeps an instance added later
 * included. The author's own assignment collapses back to it when the survivors
 * cover the whole set (`collapseFullSet`), so selecting everything and leaving
 * the default are one representation. The prune does not: a narrowing that
 * happens to cover every surviving instance after a delete is still the author's
 * explicit choice, and widening it to "every instance" would make a popover added
 * later fire on a trigger they never assigned it to.
 */
function narrowAssignedInstances(
  assigned: ReadonlyArray<[string, string]> | undefined,
  triggers: Interaction['triggers'],
  collapseFullSet = true
): Array<[string, string]> | undefined {
  if (assigned == null) return undefined;

  // No component trigger AT ALL is the opposite case to the `named.size > 0`
  // guard below, and wants the opposite answer: there is nothing to narrow to,
  // so the field is meaningless and belongs cleared. Deleting the last
  // `wf:popover` trigger runs the prune over the post-delete list and used to
  // leave a sibling at `[]`, which `findAssignedInstancesError` then rejects
  // outright — failing a later DE/MCP/code-views write of the whole
  // interaction, the very outcome the prune exists to prevent.
  if (!triggers.some(([key]) => isComponentInstanceTriggerKey(key))) {
    return undefined;
  }

  const named = new Set(
    getComponentInstancePaths(triggers).map(([componentId, elementId]) =>
      instPathKey(componentId, elementId)
    )
  );
  // Deduped by key before the count is compared, so a list that repeats a path
  // cannot reach `named.size` without covering the set.
  const kept = [
    ...new Map(
      assigned
        .filter(([componentId, elementId]) =>
          named.has(instPathKey(componentId, elementId))
        )
        .map((path) => [instPathKey(path[0], path[1]), path])
    ).values(),
  ];

  // `named.size > 0` guard: with nothing named, `kept` is empty and would
  // collapse to "drives every instance". That happens on a real path — a freshly
  // added `wf:popover` trigger has no target yet, so `getComponentInstancePaths`
  // returns nothing — and it would silently flip a trigger the author narrowed
  // to "none" back to driving every popover the moment the popover trigger got
  // a target.
  return collapseFullSet && named.size > 0 && kept.length === named.size
    ? undefined
    : kept;
}

/**
 * Re-narrow every trigger's `assignedInstances` after the set of named instances
 * changed, so none is left pointing at an instance the interaction dropped.
 *
 * Mutates the draft triggers in place, and only where the narrowing actually
 * moved — an untouched trigger keeps its object identity.
 */
function pruneAssignedInstances(interaction: {
  triggers: Interaction['triggers'];
}): void {
  for (const trigger of interaction.triggers) {
    const assigned = trigger[1].assignedInstances;
    if (assigned == null) continue;
    const narrowed = narrowAssignedInstances(
      assigned,
      interaction.triggers,
      false
    );
    // By contents, not identity: `narrowAssignedInstances` always returns
    // `undefined` or a freshly built array, so a reference check could never be
    // true and every prune would replace every narrowed trigger's config.
    const unchanged =
      narrowed != null &&
      narrowed.length === assigned.length &&
      narrowed.every(
        ([componentId, elementId], index) =>
          componentId === assigned[index]?.[0] &&
          elementId === assigned[index]?.[1]
      );
    if (unchanged) continue;
    trigger[1] = {...trigger[1], assignedInstances: narrowed};
  }
}

/** The instance paths the owner's own triggers name, in trigger order. */
function getOwnerPathsFromTriggers(
  owner: OverlayTriggerOwner,
  triggers: Interaction['triggers']
) {
  return readInstPathsFromTriggers(
    triggers,
    owner.triggerKey,
    IX3_WF_EXTENSION_KEYS.INST
  );
}

/**
 * Whether `timelineId` is still driven by another live interaction's
 * owner trigger (`wf:popover`, `wf:modal`). Timelines can be shared across
 * interactions (see `collectRetainedTimelineIds`), so resetting a shared
 * timeline's stored popover path when ONE interaction loses its popover would
 * strand that OTHER interaction's own path even though its trigger survives.
 */
function timelineSharedWithLiveOwner(
  interactions: Record<string, Interaction>,
  timelineId: string,
  excludeInteractionId: string,
  ownerTriggerKey: string
): boolean {
  return Object.values(interactions).some((other) => {
    if (other.deleted || other.id === excludeInteractionId) return false;
    if (!other.timelineIds.includes(timelineId)) return false;
    return other.triggers.some(
      ([triggerKey]) => triggerKey === ownerTriggerKey
    );
  });
}

/**
 * Whether the interaction routes triggers by timeline ROLE: 2+ live timelines
 * carry a `triggerMetadata.role`. Role-based interactions — a Popover's
 * open/close, a multi-timeline Hover's in/out — have no user-managed groups, so
 * `isMultiTimelineInteraction` misses them, yet an added trigger needs the same
 * parking: "play all" would fire both halves of a pair that only makes sense
 * one at a time. Mirrors `selectTimelineRoles` (selectors.ts) for reducer use.
 */
function isRoleRoutedInteraction(
  interaction: {timelineIds: readonly TimelineId[]},
  timelines: IX3State['timelines']
): boolean {
  let roleCount = 0;
  for (const id of interaction.timelineIds) {
    const timeline = timelines[id];
    if (
      timeline &&
      !timeline.deleted &&
      timeline.triggerMetadata?.role != null
    ) {
      roleCount++;
      if (roleCount > 1) return true;
    }
  }
  return false;
}

/**
 * Whether a trigger is routed by `assignedGroupId` at runtime. Only STANDARD
 * triggers (click/hover/focus, via StandardTriggerStrategy) read the group
 * assignment. LOAD/SCROLL/CONTINUOUS strategies iterate every timeline and
 * ignore it, so they must keep `assignedGroupId` undefined ("play all") rather
 * than be parked at the explicit `null` "unassigned" sentinel — otherwise the
 * Designer shows an orphan warning the user can't clear (the Assign control is
 * hidden for non-STANDARD triggers). Mirrors the controlType resolution in
 * IX3TriggerSection.
 */
function isStandardRoutedTrigger(trigger: Trigger): boolean {
  const {controlType} = trigger[1];
  const effective =
    controlType && isValidControlType(controlType)
      ? controlType
      : TimelineControlType.STANDARD;
  return effective === TimelineControlType.STANDARD;
}

type DraftTimeline = Draft<NonNullable<IX3State['timelines'][string]>>;

/**
 * Pin the percent canvas before applying a timing edit. Above the nominal
 * marker the canvas is derived from content (see `useEffectiveCanvasDuration`),
 * so an action whose footprint exceeds the marker *is* the canvas — shrinking it
 * would drag the 100% tick and the Scroll/Move end marker left and rescale every
 * other action. Persisting the pre-edit canvas makes the gesture end durable,
 * and the runtime honors it the same way (`padTimelineToCanvas` pads up to the
 * marker), so the shrunk action genuinely finishes early on playback.
 * Grow-only: a smaller anchor can never pull the marker back in.
 */
function pinCanvasDuration(
  timeline: DraftTimeline,
  canvasDurationAnchor: number | undefined
): void {
  if (timeline.canvasDuration != null && canvasDurationAnchor != null) {
    timeline.canvasDuration = Math.min(
      MAX_CANVAS_DURATION_S,
      Math.max(timeline.canvasDuration, canvasDurationAnchor)
    );
  }
}

/**
 * Drop every trace of GENERIC (`groupId`) routing from a collapsed interaction:
 * the surviving timeline's group id and name, and every trigger's
 * `assignedGroupId`. The role collapse needs this for heterogeneous imported
 * data — a role-only timeline removed while the SURVIVOR is `groupId`-tagged
 * selects the role branch (`removedGroupId == null`), which migrates the
 * survivor's timing onto the triggers and strips `settings`, but would leave
 * the group id behind. `getEffectivePlaybackConfig` would then still read the
 * survivor as grouped and take jump/speed from the settings just stripped,
 * losing the authored timing. Control is deliberately untouched — the caller
 * has already restored the single-group value. Mutates the immer draft.
 */
function clearGenericRouting(
  triggers: Trigger[],
  remainingTimeline: DraftTimeline
): void {
  remainingTimeline.groupId = undefined;
  remainingTimeline.name = undefined;
  for (const trigger of triggers) {
    if (!('assignedGroupId' in trigger[1])) continue;
    const {assignedGroupId: _assignedGroup, ...rest} = trigger[1];
    trigger[1] = rest;
  }
}

/**
 * Where the add-group transition parks the single-group-only plugin settings it
 * has to clear (`clearPluginConfigOnMultiGroup`), so the collapse back to one
 * timeline can restore them. `pluginConfig` is an open JSON record and plugins
 * read their own known keys, so an extra key here is inert at runtime.
 *
 * `__wf`-prefixed precisely BECAUSE the record is open: a plain name like
 * `clearedOnMultiGroup` is one a DE/API client could schema-validly author on a
 * Hover itself, and both halves of this round trip would then treat that
 * client-owned value as our stash — the add overwriting it, and the collapse
 * deleting the key and hoisting its nested entries into the top-level config.
 * The reserved prefix takes the stash out of the namespace clients author in —
 * built from the shared constant rather than spelled out, because the prefix is
 * only actually reserved by `findReservedPluginConfigKeyError` refusing to let a
 * DE/API client author it, and a local copy could drift out from under that.
 */
const MULTI_GROUP_PLUGIN_CONFIG_STASH =
  `${RESERVED_PLUGIN_CONFIG_KEY_PREFIX}ClearedOnMultiGroup` as const;

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * The trigger that OWNS an interaction's role groups. `ownerExtensionKey` comes
 * from the Designer, which resolved the group config off a specific trigger's
 * schema; the reducer has no registry access and can't rediscover it. Trigger
 * order is authored data, so the owner is not always `triggers[0]` — a
 * schema-valid interaction can list a Click first and the role-owning Hover
 * second, and two instances of the same plugin can coexist (a legacy Hover and
 * a new-model one) — so the key lookup below also requires the new-model
 * discriminator. With no key at all (legacy/direct callers) the owner is
 * inferred from the data — the first trigger that routes by callback role —
 * never positionally.
 * An EXPLICIT key that matches nothing resolves to
 * undefined rather than the first surviving trigger: the named owner is gone (a
 * concurrent edit deleted the Hover between dispatch and reduce), and treating
 * a Click or Page load as the owner would tag timelines with that plugin's roles
 * and write its `multiTimeline`/control onto a foreign trigger. Callers treat
 * undefined as "no owner" and skip the transition.
 */
function resolveRoleOwnerTrigger(
  triggers: Trigger[],
  ownerExtensionKey: string | undefined,
  ownerAlreadyInRoleMode = false
): Trigger | undefined {
  if (ownerExtensionKey == null) {
    // No key at all: legacy/direct callers, and the orphan-removal UI. Tearing
    // role mode DOWN, the owner is identifiable from the data — it is by
    // definition still routing by callback role — so resolve it that way rather
    // than assuming `triggers[0]`. A legacy Hover left behind when the
    // role-grouped new-model one is deleted is then never promoted: writing
    // `multiTimeline: false` onto it would switch `buildHover` off its saved
    // `type`/`hover` behavior onto the new single-group path. No match means no
    // owner, and callers skip the trigger-side half of the transition.
    //
    // Setting role mode UP can't use that signal — the owner is precisely the
    // trigger that does NOT route by role yet — so it keeps the positional
    // fallback.
    return ownerAlreadyInRoleMode
      ? triggers.find((trigger) =>
          triggerRoutesByCallbackRole(trigger[0], trigger[1])
        )
      : triggers[0];
  }
  // Plugins whose roles are fixed by their schema (Navbar/Dropdown) carry no
  // `multiTimeline` discriminator at all, so for those the key IS the identity.
  const rolesAreSchemaFixed =
    ROLE_ROUTED_NO_PLAYBACK_KEYS.has(ownerExtensionKey);
  // Key lookup. The Designer names the owner by key alone
  // (`findTimelineGroupOwner` returns the first new-model instance of the
  // owning key), so this resolves to the same trigger it picked. Two
  // instances of the same plugin can coexist — a legacy Hover and a new-model
  // one — and only the new-model instance can own role groups: the legacy one
  // declares the same roles in its schema but runs the `type`/`hover` path that
  // emits no role callbacks. Writing `multiTimeline`/role controls onto it would
  // switch it off its saved legacy semantics, so prefer a new-model instance and
  // fall back to any match only for plugins with no discriminator at all
  // (Navbar/Dropdown, whose roles are schema-fixed).
  // Tearing role mode down narrows this further: among several new-model
  // instances of the key, only one is still routing by role, and that one is
  // the owner.
  const byKey = (trigger: Trigger) => trigger[0] === ownerExtensionKey;
  return (
    triggers.find(
      (trigger) =>
        byKey(trigger) &&
        isNewModelRoleOwner(trigger[1].pluginConfig) &&
        (!ownerAlreadyInRoleMode ||
          triggerRoutesByCallbackRole(trigger[0], trigger[1]))
    ) ?? (rolesAreSchemaFixed ? triggers.find(byKey) : undefined)
  );
}

/**
 * The trigger whose trigger-level Control an IX3_TRIGGER_CONTROL_CHANGED
 * dispatch is editing. Unlike `resolveRoleOwnerTrigger` this takes the
 * dispatch-time index, because the author edited one specific trigger row. The
 * index is a snapshot of the trigger list, so it is validated against the key
 * and falls back to a key lookup when a concurrent edit reordered or deleted a
 * trigger. Deliberately WITHOUT the new-model (`multiTimeline`) test that
 * `resolveRoleOwnerTrigger` applies.
 *
 * The group Control stays visible in the SINGLE-group state, where the owner
 * has no roles yet and therefore no discriminator; demanding one would drop
 * every edit made before the second group is added. Two instances of the same
 * key are still distinguished by the index, which is the case the discriminator
 * exists to disambiguate; if the index has gone stale the first instance of the
 * key is the same answer the Designer's own trigger list would give.
 *
 * No key match at all means the named trigger is gone (a concurrent edit
 * deleted it between dispatch and reduce) — undefined, never a positional
 * fallback, so the Control is not written onto a foreign trigger.
 */
function resolveControlOwnerTrigger(
  triggers: Trigger[],
  ownerExtensionKey: string,
  ownerTriggerIndex: number
): Trigger | undefined {
  const atIndex = triggers[ownerTriggerIndex];
  if (atIndex && atIndex[0] === ownerExtensionKey) return atIndex;
  return triggers.find((trigger) => trigger[0] === ownerExtensionKey);
}

/**
 * Drop every trace of ROLE routing from a collapsed interaction: the surviving
 * timeline's `triggerMetadata.role` and the group-owning plugin's
 * `multiTimeline` discriminator. Shared by both collapse branches — generic
 * (`groupId`) collapse needs it too for schema-valid imported data that tags a
 * timeline with BOTH a `groupId` and a role (see shared/src/structuralIssues.ts),
 * where the branch is chosen by `groupId` and would otherwise clear only half
 * the routing, leaving a single-timeline Hover still locked in role mode with
 * one mouse event dead. Mutates the immer draft in place.
 */
function clearRoleRouting(
  triggers: Trigger[],
  remainingTimeline: DraftTimeline,
  ownerExtensionKey?: string
): void {
  if (remainingTimeline.triggerMetadata) {
    const {role: _role, ...restMeta} = remainingTimeline.triggerMetadata;
    // Clear the field entirely when only `role` was present, rather than
    // leaving an empty `{}`. `getEffectivePlaybackConfig` checks
    // `timelineDef?.triggerMetadata` by truthiness — an empty object is
    // truthy, so the collapsed single timeline would still be treated as
    // role-based and read its (now cleared) `settings.control`, ignoring the
    // restored trigger-level `singleGroupControl`.
    remainingTimeline.triggerMetadata =
      Object.keys(restMeta).length > 0 ? restMeta : undefined;
  }
  // The survivor's role is gone, so a trigger still pinned to it via
  // `assignedTimelineRole` narrows to a role no timeline has and goes inert
  // against the lone remaining timeline (`resolveAssignedTimelineIds` returns
  // an empty list). The role collapse strips this on its own way out; the
  // GENERIC collapse reaches here for mixed data (a timeline carrying both a
  // `groupId` and a role) and clears only `assignedGroupId`, so without this
  // the static role pin survives the collapse. Every trigger, not just the
  // owner's key — the pin is schema-valid on any of them.
  for (let i = 0; i < triggers.length; i++) {
    const trigger = triggers[i];
    if (!trigger || trigger[1].assignedTimelineRole === undefined) continue;
    const {assignedTimelineRole: _assignedRole, ...config} = trigger[1];
    trigger[1] = config;
  }
  // `multiTimeline` is the group-owning plugin's discriminator, so clearing it
  // is scoped to that plugin: the primary trigger always, plus any other
  // trigger of the SAME extension key that already carries the field. Writing
  // it onto every trigger would put a foreign field on a Click or Page load
  // added alongside a Hover, which never had it; clearing only the primary
  // would leave a second trigger of the owning type still declaring
  // `multiTimeline: true` after the roles are gone, so it would keep emitting
  // role callbacks that now match no timeline and stop firing entirely. The
  // `isNewModelRoleOwner` guard keeps a legacy sibling on its legacy runtime
  // path rather than promoting it to the new model — boolean, not merely
  // defined, and matching the add-group side exactly: a `null` discriminator
  // never got flipped on the way in, so writing `multiTimeline: false` onto it
  // here would move it to the new model on the way OUT instead.
  const primaryTrigger = resolveRoleOwnerTrigger(
    triggers,
    ownerExtensionKey,
    // Tearing role mode down: with no explicit key the owner is the trigger
    // still routing by callback role, not whichever one happens to be first.
    true
  );
  const primaryKey = primaryTrigger?.[0];
  for (const trigger of triggers) {
    if (trigger[0] !== primaryKey) continue;
    if (
      trigger !== primaryTrigger &&
      !isNewModelRoleOwner(trigger[1].pluginConfig)
    ) {
      continue;
    }
    // Put back whatever the add-group transition parked (Hover: a narrowed
    // `eventMode`). It can't be re-derived from the surviving role — 'enter'
    // and 'both' both keep the mouseEnter role, so reading the role back would
    // silently narrow a "Mouse enter & leave" Hover to enter-only. The stash
    // itself is what has to survive the round trip. It only fills keys that
    // are still absent: one set live since the park (a DE/API update, or a
    // Designer session without the locked Type selector) is the newer intent.
    const {[MULTI_GROUP_PLUGIN_CONFIG_STASH]: stashed, ...rest} =
      trigger[1].pluginConfig ?? {};
    trigger[1] = {
      ...trigger[1],
      pluginConfig: {
        ...(isPlainRecord(stashed) ? stashed : {}),
        ...rest,
        multiTimeline: false,
      },
    };
  }
}

/**
 * Role-based collapse back to a single timeline (e.g. Hover): clear the
 * remaining timeline's role so its label falls back to "Actions", drop the
 * role-driven control (the single timeline reads trigger.control), take the
 * triggers out of multi-timeline mode, and revert their control to the
 * single-group value. Extracted from IX3_TIMELINE_GROUP_REMOVED; mutates the
 * immer draft in place.
 */
function collapseRoleGroupsToSingle(
  triggers: Trigger[],
  remainingTimeline: DraftTimeline | undefined,
  singleControl: Trigger[1]['control'],
  multiControl: Trigger[1]['control'],
  ownerExtensionKey?: string
): void {
  // Playback timing authored on the surviving role timeline's settings. A
  // role-based timeline reads delay/jump/speed from `timeline.settings`
  // (getEffectivePlaybackConfig's role branch), but once the role is cleared
  // below the interaction is single-timeline and the runtime reads timing from
  // the trigger config instead — so timing left only on `settings` would
  // silently stop affecting playback and vanish from the trigger UI. Capture it
  // before stripping and migrate it back onto the triggers below. Mirrors
  // collapseGenericGroupsToSingle.
  const survivingJump = remainingTimeline?.settings?.jump;
  const survivingSpeed = remainingTimeline?.settings?.speed;
  const survivingDelay = remainingTimeline?.settings?.delay;
  // The role-group owner, by the same convention clearRoleRouting uses.
  const owningTrigger = resolveRoleOwnerTrigger(
    triggers,
    ownerExtensionKey,
    true
  );

  if (remainingTimeline) {
    clearRoleRouting(triggers, remainingTimeline, ownerExtensionKey);
    // A renamed role group keeps its name otherwise, and `getTimelineActionLabel`
    // prefers it over the "Actions" fallback. Mirrors clearGenericRouting.
    remainingTimeline.name = undefined;
    if (remainingTimeline.settings) {
      // Strip the group-era playback fields single-timeline mode can't honor:
      // `control` (single-timeline reads trigger.control) and delay/jump/speed
      // (migrated back onto the STANDARD triggers below). Keep everything else —
      // repeat/repeatDelay/yoyo/duration/ease/stagger still apply to a lone
      // timeline via buildSubTimeline → convertToGsapDefaults. Mirrors
      // collapseGenericGroupsToSingle.
      const {
        control: _control,
        delay: _delay,
        jump: _jump,
        speed: _speed,
        ...restSettings
      } = remainingTimeline.settings;
      remainingTimeline.settings =
        Object.keys(restSettings).length > 0 ? restSettings : undefined;
    }
  }
  for (const trigger of triggers) {
    const isStandard = isStandardRoutedTrigger(trigger);
    // The surviving timeline's role was cleared above, so a trigger still
    // carrying `assignedTimelineRole` narrows to a role nothing has:
    // `resolveAssignedTimelineIds` returns an empty list and the trigger goes
    // inert against the lone timeline. Only API/import-authored data reaches
    // here — the Designer routes the group owner by callback — but the field is
    // schema-valid, so drop it.
    //
    // `assignedGroupId` goes with it, on the same reasoning and for the same
    // authoring path. This branch only runs when the REMOVED timeline had no
    // `groupId`, and a survivor that has one is handed to
    // `clearGenericRouting` by the caller — so after this collapse no timeline
    // in the interaction carries a group id, and a trigger still pinned to one
    // resolves to an empty list exactly like the stale role pin. An explicit
    // `assignedGroupId: null` ("play nothing") is the same story: it survives
    // the collapse as live routing state and leaves the lone timeline undriven.
    // The generic collapse strips the field unconditionally too.
    const {
      assignedTimelineRole: _assignedRole,
      assignedGroupId: _assignedGroup,
      ...config
    } = trigger[1];
    // The single-group Control belongs to the trigger that OWNS the roles —
    // the primary, the only one whose editor surfaces `singleGroupControl`.
    // Being STANDARD isn't enough: a Click added alongside a role-grouped Hover
    // is STANDARD too, and its own control (restart/none/…) was merely dormant
    // while the role timelines read `timeline.settings`. Once the roles are
    // gone the lone timeline reads trigger-level playback again, so writing
    // Hover's control onto the Click would silently rewrite it on an
    // add-group/remove-group round trip.
    //
    // Owning the roles NOW isn't enough either: ownership can move. Add-group
    // writes `multiGroupControl` to the owner only and leaves every same-key
    // SIBLING's authored control dormant; delete the owner and the UI promotes a
    // sibling, which then resolves here — so an unconditional write would spend
    // Hover's default on a control the author chose. Restore only what we
    // parked: the current value being the `multiGroupControl` we wrote (or
    // nothing at all, for beta data written before that write existed) means
    // this is our own transition to undo. Anything else is authored state.
    if (
      singleControl &&
      isStandard &&
      trigger === owningTrigger &&
      (config.control === undefined ||
        multiControl === undefined ||
        config.control === multiControl)
    ) {
      config.control = singleControl;
    }
    // Restore the surviving timeline's timing onto STANDARD triggers only — the
    // ones single-timeline playback reads timing from (StandardTriggerStrategy
    // honors a trigger `delay`; getEffectivePlaybackConfig's single branch reads
    // jump/speed from the trigger). LOAD/SCROLL/CONTINUOUS keep their own
    // trigger-level timing. Mirrors collapseGenericGroupsToSingle.
    if (isStandard) {
      if (survivingDelay != null) config.delay = survivingDelay;
      if (survivingJump != null) config.jump = survivingJump;
      if (survivingSpeed != null) config.speed = survivingSpeed;
    }
    trigger[1] = config;
  }
}

/**
 * Generic collapse back to a single timeline (Click/Custom, or a split Hover
 * whose other group was deleted): migrate the surviving group's playback timing
 * back onto the triggers, clear `assignedGroupId`, and restore a sensible
 * single-group Control (directional for pinned Hover halves). Extracted from
 * IX3_TIMELINE_GROUP_REMOVED; mutates the immer draft in place.
 */
function collapseGenericGroupsToSingle(
  triggers: Trigger[],
  remainingTimeline: DraftTimeline | undefined,
  singleControl: Trigger[1]['control'],
  ownerExtensionKey?: string
): void {
  // Playback timing authored in the surviving group's popover. Once we leave
  // grouped mode the runtime reads timing from the trigger config, not from
  // `timeline.settings`, so timing left only on the settings would silently
  // stop affecting playback and vanish from the trigger-level UI. Carry it back
  // onto the triggers below: jump/speed come from the trigger in
  // getEffectivePlaybackConfig's single-timeline branch (for every trigger
  // type), and a STANDARD trigger's `delay` is honored by
  // StandardTriggerStrategy.bind — so delay is migrated back onto STANDARD
  // triggers (LOAD honors its own delay too, but routed group triggers are
  // STANDARD; SCROLL/CONTINUOUS ignore delay entirely).
  let survivingJump: SecondsOrMs | undefined;
  let survivingSpeed: number | undefined;
  let survivingDelay: SecondsOrMs | undefined;
  // The surviving timeline's group id, captured BEFORE it's cleared below. The
  // directional-Control logic needs it to tell which split Hover half was
  // assigned to the survivor (and so should keep playing it forward) from the
  // half whose own group was deleted.
  const survivingGroupId = remainingTimeline?.groupId;

  if (remainingTimeline) {
    remainingTimeline.groupId = undefined;
    remainingTimeline.name = undefined;
    survivingJump = remainingTimeline.settings?.jump;
    survivingSpeed = remainingTimeline.settings?.speed;
    survivingDelay = remainingTimeline.settings?.delay;
    // Strip only the group-era playback fields that single-timeline mode can't
    // honor: the per-timeline `control` (single-timeline reads trigger.control),
    // `delay` (single-timeline ignores `settings.delay`; migrated back onto
    // STANDARD triggers below), and `jump`/`speed` (migrated back onto the
    // triggers below). Keep everything else — repeat/repeatDelay/yoyo/duration/
    // ease/stagger are still applied for a lone timeline because
    // AnimationCoordinator.buildSubTimeline feeds `def.settings` through
    // convertToGsapDefaults regardless of grouping. Wiping all settings would
    // silently erase authored Repeat/easing/stagger from state and playback.
    if (remainingTimeline.settings) {
      const {
        control: _control,
        delay: _delay,
        jump: _jump,
        speed: _speed,
        ...singleTimelineSettings
      } = remainingTimeline.settings;
      remainingTimeline.settings =
        Object.keys(singleTimelineSettings).length > 0
          ? singleTimelineSettings
          : undefined;
    }
  }

  // Clear all assignedGroupId from triggers and restore a sensible single-group
  // Control. Check the key, not truthiness: a trigger may carry the explicit
  // `null` sentinel (set when its group was removed while others remained) and
  // that must be cleared too, or the lone remaining timeline routes to "play
  // nothing" at runtime.
  //
  // Triggers pinned to one side of a Hover split (the Mouse enter / Mouse leave
  // halves left behind after the *other* group is deleted) get a *directional*
  // Control so the pair drives the lone remaining timeline forward/back exactly
  // like an unsplit Hover: each side is absolute, so it stays in sync no matter
  // how an independent trigger (e.g. a Click set to Toggle play/reverse) is
  // interleaved. A stateful toggle on each half would phase-invert when a third
  // toggle fires between them.
  //
  // Which half plays *forward* depends on which group survived, not on the raw
  // event side: the half assigned to the surviving group was already playing
  // that timeline forward, so it keeps `play`; the half whose group was deleted
  // reverses the survivor.
  //   - hover-out removed → hover-in survives: enter=play, leave=reverse
  //   - hover-in removed → hover-out survives: leave=play, enter=reverse
  //     (otherwise `leave` would reverse a timeline sitting at progress 0 and
  //     play nothing from the start).
  // Triggers with no pinned event side fall back to the group's single-group
  // Control (e.g. Hover: togglePlayReverse).
  //
  // A single "Mouse enter" or "Mouse leave" Hover is reachable without a
  // generated split — the Type selector sets `eventMode` directly and groups
  // are added manually — so only treat a half as directional when it actually
  // pairs with an opposite half in a different group (see the per-trigger test
  // below). That rules out two failure modes:
  //   - A lone half (e.g. a Mouse-enter Hover assigned to a group alongside a
  //     Click in another) flipping to `reverse` and reversing the lone timeline
  //     from progress 0, playing nothing.
  //   - A manually authored enter/leave pair sharing ONE group: if that shared
  //     group is the one removed, both halves are non-surviving and both would
  //     be rewritten to `reverse`, so either hover event reverses the survivor
  //     from progress 0. Same-group pairs aren't directional — they keep the
  //     normal single-group Control.
  // Unassigned halves (`undefined`, or the explicit `null` sentinel) can't
  // establish a direction either, so they don't count toward the pair.
  const enterGroupIds = new Set<string>();
  const leaveGroupIds = new Set<string>();
  for (const trigger of triggers) {
    if (!isStandardRoutedTrigger(trigger)) continue;
    const mode = trigger[1].pluginConfig?.eventMode;
    const assigned = trigger[1].assignedGroupId;
    if (assigned == null) continue;
    if (mode === 'enter') enterGroupIds.add(assigned);
    else if (mode === 'leave') leaveGroupIds.add(assigned);
  }
  for (const trigger of triggers) {
    const isStandard = isStandardRoutedTrigger(trigger);
    const eventMode = trigger[1].pluginConfig?.eventMode;
    const isSurvivingHalf =
      survivingGroupId != null &&
      trigger[1].assignedGroupId === survivingGroupId;
    // Pairing is decided per trigger, not once per interaction: this half is
    // directional iff it has a concrete group AND some opposite-side half is
    // assigned to a DIFFERENT concrete group — i.e. it participates in at least
    // one cross-group pair, the shape the split produces. Deciding it globally
    // gets both directions wrong on beta generic-group data:
    //   - An interaction-wide veto on any enter/leave group overlap would let
    //     one stray trigger (an extra Mouse enter on the leave half's group)
    //     strip the ORIGINAL pair of its directional Controls. Both halves then
    //     fall back to the single-group toggle, which the Hover runtime resolves
    //     to `play` for a `multiTimeline: false` leave-only half — so hover-out
    //     replays the survivor forward instead of reversing it.
    //   - Conversely, an interaction-wide grant would rewrite triggers that
    //     established no direction at all (an unassigned extra Mouse enter):
    //     not the surviving half → `reverse`, reversing from progress 0.
    // The per-trigger test keeps both earlier guards intact for free: a lone
    // half has no opposite side to pair with, and a manually authored pair
    // sharing ONE group has no opposite side in a *different* group.
    const assignedGroupId = trigger[1].assignedGroupId;
    const oppositeGroupIds =
      eventMode === 'enter'
        ? leaveGroupIds
        : eventMode === 'leave'
          ? enterGroupIds
          : undefined;
    const establishesPair =
      assignedGroupId != null &&
      oppositeGroupIds != null &&
      [...oppositeGroupIds].some((id) => id !== assignedGroupId);
    const directionalControl = establishesPair
      ? isSurvivingHalf
        ? 'play'
        : 'reverse'
      : undefined;
    // The single-group Control belongs only to triggers that route through
    // group playback — STANDARD triggers, the ones coerced to `play` on entry.
    // A non-STANDARD trigger added alongside the group-owning trigger (e.g. a
    // Page Load saved as `none`, or a Scroll trigger) was never coerced and
    // ignores group routing, so forcing the group's `singleGroupControl` (e.g.
    // Hover's `togglePlayReverse`) onto it would clobber its own authored
    // control semantics. Leave those untouched — mirrors the entry-path gate in
    // coerceTriggerControlsToPlay.
    // ...and only to the trigger type the config was resolved FROM. The group
    // owner isn't always the trigger whose groups these are: an interaction can
    // hold generic `groupId` groups authored by a Click and later gain a
    // new-model Hover, which `findTimelineGroupOwner` then reports as the owner.
    // Its `singleGroupControl` (togglePlayReverse) is Hover's semantics, not the
    // Click's — writing it onto the Click would silently retype that trigger on
    // an add/remove round trip. The Click instead falls through to
    // `clearCoercedControl` below and reverts to its own schema default, which
    // is what group entry took from it. Both halves of a split Hover share the
    // owner's key, so they still get it.
    const ownsGroupConfig =
      ownerExtensionKey == null || trigger[0] === ownerExtensionKey;
    const nextControl =
      directionalControl ??
      (isStandard && ownsGroupConfig ? singleControl : undefined);

    // Group entry coerced every STANDARD routed trigger's Control to `play`
    // (see coerceTriggerControlsToPlay). When there's no directional or
    // single-group Control to restore on collapse (e.g. a generic Click/Custom
    // interaction whose `groupConfig` has no `singleGroupControl`), drop that
    // coerced value instead of leaving it stuck on `play` — otherwise a trigger
    // that resolved as the schema default (`restart`) before grouping would stay
    // `play` after an add/remove round trip and stop replaying once the timeline
    // is at its end. Only clear it for STANDARD routed triggers, the only ones
    // that were coerced; LOAD/SCROLL/CONTINUOUS triggers keep their authored
    // Control.
    const clearCoercedControl = !nextControl && isStandard;
    // Only STANDARD triggers honor a trigger-level delay
    // (StandardTriggerStrategy.bind); SCROLL/CONTINUOUS ignore it, so don't
    // push a stale delay onto them.
    const restoreDelay = survivingDelay != null && isStandard;
    // Jump/Speed are migrated into group settings only for STANDARD triggers on
    // entry (see collectMigratedTriggerTiming); a non-STANDARD trigger (e.g.
    // Page Load) keeps its own authored jump/speed while grouped. Only push the
    // collapsed group's jump/speed back onto STANDARD triggers, or a routed
    // group with different timing would silently overwrite the non-STANDARD
    // trigger's own values — which restConfig already preserves.
    const restoreJump = survivingJump != null && isStandard;
    const restoreSpeed = survivingSpeed != null && isStandard;

    if (
      'assignedGroupId' in trigger[1] ||
      nextControl ||
      clearCoercedControl ||
      restoreJump ||
      restoreSpeed ||
      restoreDelay
    ) {
      const {
        assignedGroupId: _,
        control: prevControl,
        ...restConfig
      } = trigger[1];
      // Prefer a directional/single-group Control; otherwise drop the coerced
      // `play` (clearCoercedControl) so the trigger reverts to its schema
      // default, and keep the prior Control in every other case (e.g. a LOAD
      // trigger that entered this block only to pick up surviving timing).
      const restoredControl = nextControl
        ? nextControl
        : clearCoercedControl
          ? undefined
          : prevControl;
      trigger[1] = {
        ...restConfig,
        ...(restoredControl ? {control: restoredControl} : {}),
        // Preserve the collapsed group's timing on the trigger so single-timeline
        // playback keeps honoring it.
        ...(restoreDelay ? {delay: survivingDelay} : {}),
        ...(restoreJump ? {jump: survivingJump} : {}),
        ...(restoreSpeed ? {speed: survivingSpeed} : {}),
      };
    }
  }
}

/**
 * Still-multi removal: the interaction keeps 2+ groups after this one is
 * removed. Park any trigger that pointed at the removed group at the explicit
 * `null` "unassigned" sentinel (undefined would mean "play all groups"), and
 * clear any conditional-outcome target still pointing at the deleted timeline.
 * Extracted from IX3_TIMELINE_GROUP_REMOVED; mutates the immer draft in place.
 */
function orphanTriggersForRemovedGroup(
  triggers: Trigger[],
  removedGroupId: string,
  removedTimelineId: string,
  survivingTimelineIds: readonly string[],
  timelines: Record<string, Timeline | undefined>
): void {
  // Keep the trigger explicitly unassigned while the interaction still has
  // multiple groups. Undefined means "play all groups" at runtime.
  for (const trigger of triggers) {
    if (trigger[1].assignedGroupId === removedGroupId) {
      trigger[1] = {...trigger[1], assignedGroupId: null};
    }
  }

  clearDeletedOutcomeTargets(
    triggers,
    removedTimelineId,
    survivingTimelineIds,
    timelines
  );
}

/**
 * The surviving timeline a statically narrowed trigger actually routes to, or
 * `undefined` when it routes to none (or narrows to nothing at all). Mirrors
 * `IX3Engine.resolveAssignedTimelineIds`: an explicit `null` group plays
 * nothing, a concrete `assignedGroupId` selects its group, and an absent group
 * falls through to `assignedTimelineRole`. Callback-routed triggers never reach
 * here — their pin is cleared instead.
 */
function routedSurvivor(
  trigger: Trigger,
  timelines: Record<string, Timeline | undefined>,
  survivingTimelineIds: readonly string[]
): string | undefined {
  const {assignedGroupId, assignedTimelineRole} = trigger[1];
  if (assignedGroupId === null) return undefined;
  const matches = (id: string): boolean => {
    const timeline = timelines[id];
    // Existence is not enough: `interaction.timelineIds` tolerates drift, and a
    // soft-deleted row is excluded by the selectors and by playback — so
    // "routing" a repair onto one leaves the outcome just as dead as the stale
    // pin it replaced.
    if (!timeline || timeline.deleted) return false;
    return assignedGroupId
      ? timeline.groupId === assignedGroupId
      : timeline.triggerMetadata?.role === assignedTimelineRole;
  };
  if (!assignedGroupId && !assignedTimelineRole) return undefined;
  return survivingTimelineIds.find(matches);
}

/**
 * A surviving trigger's conditionalLogic may still target the timeline just
 * deleted — in a 3+ group interaction a condition can point its
 * `targetTimelineId` at the group being removed. Once that timeline is off
 * `interaction.timelineIds`, executeConditionalOutcome rejects the stale target
 * and returns, so the outcome silently becomes a no-op with no UI to re-select
 * a target. Unlike the single-timeline collapse there's no lone survivor to
 * repoint at automatically, so the repair depends on what an ABSENT target
 * means for the trigger holding it:
 *
 *  - Callback-routed (Navbar/Dropdown, role-mode Hover): absent means "the
 *    timeline of whichever role fired", which is both what the runtime does and
 *    the one shape `findOutcomeError` accepts without a target
 *    (`allowsRoleRelativeTarget`). Clear it.
 *  - Everything else: absent means "every remaining timeline in the
 *    interaction" at runtime, and `findOutcomeError` rejects it outright. So
 *    clearing a plain Click's pin would BOTH widen a one-timeline condition to
 *    all survivors and leave authoring state the Designer refuses to validate —
 *    from an edit the author only asked to remove one row. Repoint it at a
 *    deliberate survivor instead: the interaction's first remaining timeline,
 *    the same "primary" the role transitions use. A guess at intent, but a
 *    valid, visible, single-timeline one the author can re-aim in the
 *    conditions editor — unlike a silent broadening with no UI to undo it.
 *    When the trigger narrows to a group or role of its own, that survivor is
 *    picked over the interaction's first: `resolveAssignedTimelineIds` routes a
 *    statically assigned trigger to exactly one timeline, so a pin anywhere else
 *    intersects to nothing at runtime. Repointing inside the route can only turn
 *    a dead pin live.
 *
 * With no survivors at all the pin is simply cleared; the single-timeline
 * collapse path handles that case before this one runs.
 *
 * Independent of the `assignedGroupId` orphaning it was extracted from: a
 * ROLE-tagged row carries no `groupId`, so it never reaches that path, yet a
 * pin to it goes just as stale. Mutates the immer draft.
 */
function clearDeletedOutcomeTargets(
  triggers: Trigger[],
  removedTimelineId: string,
  survivingTimelineIds: readonly string[],
  timelines: Record<string, Timeline | undefined>
): void {
  // Same reasoning as `routedSurvivor`'s liveness test: the first id in
  // `timelineIds` can be one the store no longer has, or a soft-deleted row the
  // selectors hide, and repointing at either is indistinguishable from leaving
  // the outcome broken.
  const liveSurvivingIds = survivingTimelineIds.filter((id) => {
    const timeline = timelines[id];
    return timeline !== undefined && !timeline.deleted;
  });
  const fallbackTimelineId = liveSurvivingIds[0];
  for (const trigger of triggers) {
    const cl = trigger[1].conditionalLogic;
    if (!cl) continue;
    const nextTarget = triggerRoutesByCallbackRole(trigger[0], trigger[1])
      ? undefined
      : (routedSurvivor(trigger, timelines, liveSurvivingIds) ??
        fallbackTimelineId);
    const repairDeletedOutcomeTarget = <O extends {targetTimelineId?: string}>(
      outcome: O
    ): O =>
      outcome.targetTimelineId === removedTimelineId
        ? {
            ...outcome,
            // A `none` outcome is the one case where the repoint reasoning
            // above inverts: it plays nothing whatever it points at, so there
            // is no widening to avoid, and `findOutcomeError` REJECTS a target
            // on `none`. Repointing would turn persisted junk into persisted
            // junk the next DE/API edit fails on; clearing is both valid and
            // what the pre-multi-group reducer did.
            targetTimelineId:
              'control' in outcome && outcome.control === 'none'
                ? undefined
                : nextTarget,
          }
        : outcome;
    trigger[1] = {
      ...trigger[1],
      conditionalLogic: mapConditionalOutcomes(cl, repairDeletedOutcomeTarget),
    };
  }
}

/**
 * Runs all target repair and migration steps in the correct order:
 * 1. Structural repair (fix corrupt filterContext missing filterBy;
 *    also clears firstMatchOnly on relationship none)
 * 2. Incorrectly-migrated-target repair (fix non-functional scope bug)
 * 3. Legacy migration (2-tuple → 3-tuple with filterContext)
 * 4. ID migration (wf:id → wf:selector)
 *
 * Scroll pin / endTrigger / scroller use migrateScrollConfigTargetDefinition
 * instead — they skip step 2 (trigger-only any-element rewrite).
 */
function migrateTargetDefinition(
  target: TargetDefinition,
  context: 'trigger' | 'action'
): TargetDefinition {
  let result = target;

  result = repairMissingFilterBy(result);

  if (detectTargetFormat(result) === 'filtered') {
    result = repairIncorrectlyMigratedTarget(result, context);
  }

  if (detectTargetFormat(result) === 'legacy' && shouldAutoMigrate(result)) {
    result = migrateTarget(result);
  }

  if (result[0] === IX3_WF_EXTENSION_KEYS.ID) {
    result = migrateFilterByRecursive(migrateIdToSelector(result));
  }

  return result;
}

/**
 * Load-repair path for scrollTriggerConfig.pin / endTrigger / scroller.
 * Same structural + legacy + id migrations as primary targets, but skips
 * repairIncorrectlyMigratedTarget: that repair assumes every trigger
 * wf:any-element came from an old scope migration, which is false for
 * intentionally authored scroll-config targets (e.g. any-element + within).
 */
function migrateScrollConfigTargetDefinition(
  target: TargetDefinition
): TargetDefinition {
  let result = repairMissingFilterBy(target);

  if (detectTargetFormat(result) === 'legacy' && shouldAutoMigrate(result)) {
    result = migrateTarget(result);
  }

  if (result[0] === IX3_WF_EXTENSION_KEYS.ID) {
    result = migrateFilterByRecursive(migrateIdToSelector(result));
  }

  return result;
}

/**
 * Migrate pin / endTrigger / scroller target tuples inside scrollTriggerConfig.
 * Validation walks these fields; load repair must too so sticky
 * `firstMatchOnly` on `relationship: none` is cleared before a later RMW.
 */
function migrateScrollConfigTargets(
  config: Trigger[1] | undefined
): Trigger[1] | undefined {
  if (!config || typeof config !== 'object') return config;
  const scroll = config.scrollTriggerConfig;
  if (!scroll || typeof scroll !== 'object') return config;

  let changed = false;
  const nextScroll = {...scroll};

  const migrateField = (field: 'pin' | 'endTrigger' | 'scroller') => {
    const val = scroll[field];
    // pin may be a boolean; only target-shaped arrays migrate. Non-arrays
    // (null / string / number from dirty snapshot restores) are left alone
    // so one bad field cannot abort the whole IX3 load.
    if (val === undefined || typeof val === 'boolean' || !Array.isArray(val)) {
      return;
    }
    const repaired = migrateScrollConfigTargetDefinition(
      val as TargetDefinition
    );
    if (repaired !== val) {
      nextScroll[field] = repaired as typeof val;
      changed = true;
    }
  };

  migrateField('pin');
  migrateField('endTrigger');
  migrateField('scroller');

  return changed ? {...config, scrollTriggerConfig: nextScroll} : config;
}

/**
 * Converts raw IX3 data from the server into the format expected by the store.
 * Migrates legacy 2-tuple targets to 3-tuple filtered format.
 */
export function processRawInteractionsSiteData(
  rawInteractions: RawInteraction[],
  rawTimelines: RawTimeline[],
  rawActionPresets: RawActionPreset[]
) {
  const ix3Interactions: IX3State['interactions'] = {};
  const ix3Timelines: IX3State['timelines'] = {};
  const ix3ActionPresets: IX3State['actionPresets'] = {};

  for (const rawInteraction of rawInteractions) {
    const interaction: Interaction =
      interactionTransforms.rawInteractionToClient(rawInteraction);

    const migratedTriggers = interaction.triggers.map((trigger) => {
      const targetDef = trigger[2];
      const migratedConfig = migrateScrollConfigTargets(trigger[1]);
      return (
        targetDef
          ? [
              trigger[0],
              migratedConfig,
              migrateTargetDefinition(targetDef, 'trigger'),
            ]
          : [trigger[0], migratedConfig]
      ) as Trigger;
    });

    ix3Interactions[interaction.id] = {
      ...interaction,
      triggers: migratedTriggers,
    };
  }

  const migratedTimelines = rawTimelines.map((rawTimeline) => {
    const timeline: Timeline =
      timelineTransforms.rawTimelineToClient(rawTimeline);

    const migratedActions = timeline.actions?.map((action) => ({
      ...action,
      targets: action.targets?.map((target) =>
        migrateTargetDefinition(target, 'action')
      ),
    }));

    return {
      ...timeline,
      actions: migratedActions,
    };
  });

  for (const timeline of applyOnTimelinesLoadedHooks({
    timelines: migratedTimelines,
  })) {
    ix3Timelines[timeline.id] = timeline;
  }

  for (const rawActionPreset of rawActionPresets) {
    const actionPreset: ActionPreset =
      actionPresetTransforms.rawActionPresetToClient(rawActionPreset);
    ix3ActionPresets[actionPreset.id] = {
      ...actionPreset,
      action: applyOnActionPresetActionStoredHooks({
        action: actionPreset.action,
      }),
    };
  }

  return {
    ix3Interactions,
    ix3Timelines,
    ix3ActionPresets,
  };
}

export const ix3Reducer = (
  state: IX3State = initialState,
  action: AllActions
): IX3State => {
  switch (action.type) {
    case RAW_BRANCHED_DATA_RECEIVED:
    case RAW_SITE_DATA_RECEIVED: {
      const siteData =
        action.type === RAW_BRANCHED_DATA_RECEIVED
          ? action.payload.branchedData
          : action.siteData;

      if (
        siteData &&
        siteData.ix3Interactions &&
        siteData.ix3Timelines &&
        siteData.ix3ActionPresets
      ) {
        const {ix3Interactions, ix3Timelines, ix3ActionPresets} =
          processRawInteractionsSiteData(
            siteData.ix3Interactions,
            siteData.ix3Timelines,
            siteData.ix3ActionPresets
          );

        return {
          ...state,
          interactions: ix3Interactions,
          timelines: ix3Timelines,
          actionPresets: ix3ActionPresets,
        };
      }
      return state;
    }

    // ============================================================================
    // Action Presets State
    // ============================================================================
    case ix3ActionTypes.IX3_ACTION_PRESET_CREATED: {
      return produce(state, (draft) => {
        const {
          id,
          name,
          description,
          group,
          deleted,
          action: actionConfig,
        } = action.payload;

        draft.actionPresets[id] = {
          type: 'custom',
          id,
          name,
          deleted,
          description: description ?? '',
          action: applyOnActionPresetActionStoredHooks({
            action: actionConfig,
          }),
        };

        if (group) {
          draft.actionPresets[id].groupId = group.id;

          if (group.newName) {
            draft.actionPresetGroups[group.id] = {
              id: group.id,
              name: group.newName,
            };
          }
        }
      });
    }

    case ix3ActionTypes.IX3_ACTION_PRESET_SETTINGS_EDITED: {
      return produce(state, (draft) => {
        const {id, name, description} = action.payload;
        const preset = draft.actionPresets[id];
        if (preset == null) return;

        preset.name = name ?? preset.name;
        preset.description = description ?? preset.description;
      });
    }

    case ix3ActionTypes.IX3_ACTION_PRESET_DUPLICATED: {
      return produce(state, (draft) => {
        const {id, newId, newName} = action.payload;
        const preset = draft.actionPresets[id];
        if (preset == null) return;

        draft.actionPresets[newId] = {
          ...preset,
          id: newId,
          name: newName ?? `${preset.name} Copy`,
          action: applyOnActionPresetActionStoredHooks({
            action: preset.action,
          }),
        };
      });
    }

    case ix3ActionTypes.IX3_ACTION_PRESET_ACTION_EDITED: {
      return produce(state, (draft) => {
        const {id, action: presetAction} = action.payload;
        const preset = draft.actionPresets[id];
        if (preset == null) return;

        preset.action = applyOnActionPresetActionStoredHooks({
          action: presetAction,
        });
      });
    }

    case ix3ActionTypes.IX3_ACTION_PRESET_DELETED: {
      return produce(state, (draft) => {
        const {id} = action.payload;
        const actionPreset = draft.actionPresets[id];
        if (actionPreset == null) return;

        actionPreset.deleted = true;
      });
    }

    // ============================================================================
    // Trigger State
    // ============================================================================

    case ix3ActionTypes.IX3_TRIGGER_DELETED: {
      return produce(state, (draft) => {
        const {index, interactionId, componentPatch} = action.payload;

        const interaction = draft.interactions[interactionId];

        // A tombstone is immutable: the cleanup below writes the timelines it
        // lists, which another live interaction may still retain.
        if (!interaction || interaction.deleted) {
          return;
        }

        // The UI handles deleting the last trigger, but added here as backup.
        // Shared with the Designer's reconcile path, which has to know whether
        // the delete will actually land — see `triggersAfterDelete`.
        const remaining = triggersAfterDelete(interaction.triggers, index);
        if (remaining != null) {
          interaction.triggers = remaining;

          // A sibling trigger may have been narrowed to an instance this delete
          // just removed. Left alone, that path routes to nothing and the API
          // invariant rejects the interaction outright.
          pruneAssignedInstances(interaction);

          // The first-wins checkbox is hidden once fewer than two one-shots
          // remain, so a survivor's `firstWins` could no longer be seen or
          // cleared, and the next one-shot added would inherit it already
          // checked. Drop it with the sibling it was racing.
          if (!isOneShotFirstWinsShown(interaction.triggers)) {
            for (const trigger of interaction.triggers) {
              if (!ONE_SHOT_TRIGGER_KEYS.has(trigger[0])) continue;
              const pluginConfig = trigger[1].pluginConfig;
              if (pluginConfig?.firstWins === undefined) continue;
              const {firstWins: _, ...rest} = pluginConfig;
              trigger[1] = {
                ...trigger[1],
                pluginConfig: Object.keys(rest).length ? rest : undefined,
              };
            }
          }

          // Deleting the LAST role-owning trigger (Popover, Navbar, Dropdown,
          // or a role-form Hover) strands every sibling assigned to one of its roles. The timeline
          // would still animate the stored target, but no role observer is left
          // to put the popover in the top layer, so Open runs against a
          // `display: none` element and the interaction silently stops working.
          // It is also unfixable from the panel: `IX3TriggerSection` builds its
          // Assign options from the role-owning trigger, so the select
          // disappears along with it and the stale value cannot be seen or
          // cleared. Reset those siblings to unassigned — the same shape the
          // role-assign reducer uses for "back to unassigned" — which the panel
          // does surface.
          // Checked per role, not per owner: with a Popover and a role-form
          // Hover on one interaction, deleting the Popover leaves an owner but
          // strands a sibling assigned to `open` / `close`, which only the
          // Popover registered.
          const remainingRoles = new Set<string>();
          for (const [remainingKey, remainingConfig] of interaction.triggers) {
            const roles = getMultiTimelineRoles(remainingKey, remainingConfig);
            if (roles) for (const role of roles) remainingRoles.add(role);
          }
          for (const remainingTrigger of interaction.triggers) {
            const role = remainingTrigger[1]?.assignedTimelineRole;
            if (role == null || remainingRoles.has(role)) continue;
            const strandedLogic = remainingTrigger[1].conditionalLogic;
            remainingTrigger[1] = {
              ...remainingTrigger[1],
              assignedTimelineRole: undefined,
              assignedGroupId: null,
              ...(strandedLogic
                ? {
                    conditionalLogic: pinRoleRelativeOutcomes(
                      strandedLogic,
                      liveRoleTimelineId(interaction, draft.timelines, role)
                    ),
                  }
                : {}),
            };
          }

          // Deleting the last `wf:popover` trigger also strands every target
          // bound to the popover or its backdrop — a sibling Click pointed at
          // Backdrop, an action animating Popover. Both resolve from the
          // popovers the interaction's `wf:popover` triggers name, so they now
          // bind to nothing, and the code-view lane rejects them outright,
          // failing every later file commit of the interaction. Keyed on
          // `wf:popover` alone rather than `remainingRoles`: a Navbar or
          // Dropdown left behind names no popover. Drop them to unset shapes:
          // an action without that target row (the state deleting its last
          // row leaves), and a trigger that needs a target back on the
          // Designer's own unset target, the one a new Click gets with
          // nothing selected. That keeps the panel's picker and its "No target
          // elements found" warning, and the DE invariants accept it. The
          // lane flags it only when this interaction is next edited there,
          // as it does any unset Click. Anything else keeps no target.
          // The same holds for every owner in INSTANCE_TRIGGER_OWNERS (a Modal
          // and its backdrop too), so each owner with no trigger left strands
          // its own bound targets.
          const strandedTargetKeys = new Set(
            INSTANCE_TRIGGER_OWNERS.filter(
              ({triggerKey}) =>
                !interaction.triggers.some(([key]) => key === triggerKey)
            ).flatMap(({boundTargetKeys}) => [...boundTargetKeys])
          );
          const isStrandedTarget = (target: unknown) =>
            Array.isArray(target) && strandedTargetKeys.has(target[0]);
          const deletedTrigger =
            state.interactions[interactionId]!.triggers[index];
          const deletedOwner = INSTANCE_TRIGGER_OWNERS.find(
            ({triggerKey}) => triggerKey === deletedTrigger?.[0]
          );
          if (strandedTargetKeys.size > 0) {
            interaction.triggers.forEach((remainingTrigger, triggerIndex) => {
              if (!isStrandedTarget(remainingTrigger[2])) return;
              interaction.triggers[triggerIndex] =
                TRIGGER_REQUIRES_TARGET_KEYS.has(remainingTrigger[0])
                  ? [
                      remainingTrigger[0],
                      remainingTrigger[1],
                      [
                        IX3_WF_EXTENSION_KEYS.CLASS,
                        [],
                        {...DEFAULT_FILTER_CONTEXT},
                      ],
                    ]
                  : [remainingTrigger[0], remainingTrigger[1]];
            });
            // No `assignedInstances` is left to clear here: with no component
            // trigger remaining, `pruneAssignedInstances` above already
            // dropped every narrowing, so none sits at `[]` with no panel UI
            // left to reset it.
            for (const timelineId of interaction.timelineIds) {
              const timeline = draft.timelines[timelineId];
              if (timeline == null || timeline.deleted) continue;
              for (const timelineAction of timeline.actions) {
                if (!timelineAction.targets.some(isStrandedTarget)) {
                  continue;
                }
                timelineAction.targets = timelineAction.targets.filter(
                  (target) => !isStrandedTarget(target)
                );
              }
            }
          }
          if (
            // The deleted trigger's own owner paths, not
            // `getComponentInstancePaths`: the body only resets that owner's
            // element paths, so another owner's trigger must not trip it.
            // Skipped when that owner has no trigger left, as the branch above
            // already stripped its targets.
            deletedOwner?.elementKey !== undefined &&
            deletedTrigger !== undefined &&
            interaction.triggers.some(
              ([key]) => key === deletedOwner.triggerKey
            ) &&
            getOwnerPathsFromTriggers(deletedOwner, [deletedTrigger]).length >
              0 &&
            getOwnerPathsFromTriggers(deletedOwner, interaction.triggers)
              .length === 0
          ) {
            const {elementKey, triggerKey: ownerTriggerKey} = deletedOwner;
            // The deleted trigger named the last popover (or modal), and the
            // same-owner trigger that survives names none yet, so nothing
            // replaces an element path that named the deleted one.
            // A Click, Page load, Exit intent or Duration falls back to that
            // path and would keep animating the unbound popover. Reset it to
            // the unassigned seed's empty value, which assigning a popover
            // backfills. A sibling trigger bound to Popover resolves the same
            // way, so it would keep firing from the deleted popover.
            interaction.triggers.forEach((remainingTrigger, triggerIndex) => {
              const target = remainingTrigger[2];
              if (target?.[0] !== elementKey || target[1] === '') {
                return;
              }
              const cleared = [...target] as typeof target;
              cleared[1] = '';
              interaction.triggers[triggerIndex] = [
                remainingTrigger[0],
                remainingTrigger[1],
                cleared,
              ];
            });
            for (const timelineId of interaction.timelineIds) {
              const timeline = draft.timelines[timelineId];
              if (timeline == null || timeline.deleted) continue;
              // Unlike the last-Popover branch above, which strips shared
              // timelines too (the interaction losing its trigger must not keep
              // referencing popover targets), this branch only resets the
              // stored path: another live popover interaction on this timeline
              // still resolves its non-popover triggers from it.
              if (
                timelineSharedWithLiveOwner(
                  draft.interactions,
                  timelineId,
                  interactionId,
                  ownerTriggerKey
                )
              ) {
                continue;
              }
              for (const timelineAction of timeline.actions) {
                timelineAction.targets.forEach((target, targetIndex) => {
                  if (target[0] !== elementKey || target[1] === '') {
                    return;
                  }
                  const cleared = [...target] as typeof target;
                  cleared[1] = '';
                  timelineAction.targets[targetIndex] = cleared;
                });
              }
            }
          }

          // Mirror the IX3_TRIGGER_UPDATED sync: if the deleted trigger was
          // the last scroll-scrub trigger, clear the percent-canvas marker on
          // all role-less timelines so they stop reading as percent canvases.
          const hasScrollScrub = interaction.triggers.some(
            ([, triggerConfig]) =>
              triggerConfig?.controlType === TimelineControlType.SCROLL &&
              triggerConfig?.scrollTriggerConfig?.scrub != null
          );
          if (!hasScrollScrub) {
            for (const timelineId of interaction.timelineIds) {
              const timeline = draft.timelines[timelineId];
              if (timeline == null || timeline.triggerMetadata?.role != null) {
                continue;
              }
              if (timeline.canvasDuration != null) {
                // eslint-disable-next-line webflow/no-delete
                delete timeline.canvasDuration;
              }
            }
          }

          // The reconcile this delete calls for, in the same undo step — see
          // `applyComponentInteractionPatch`. Only when the delete landed.
          if (componentPatch != null) {
            applyComponentInteractionPatch(draft, interaction, componentPatch);
          }
        }
      });
    }

    /**
     * Bring a component interaction back in line with its component's settings.
     *
     * The Designer derives the patch, because the input it turns on — a
     * Popover's `::backdrop` declarations — lives outside this store. Applied as
     * one action so the whole adjustment is a single step. See
     * `ComponentInteractionPatch`.
     */
    case ix3ActionTypes.IX3_COMPONENT_INTERACTION_RECONCILED: {
      return produce(state, (draft) => {
        const {interactionId, patch} = action.payload;

        const interaction = draft.interactions[interactionId];

        // A peer's delete is a soft one (the record stays, `deleted: true`), so
        // a patch derived before it arrived still has to be dropped.
        if (!interaction || interaction.deleted) {
          return;
        }

        applyComponentInteractionPatch(draft, interaction, patch);
      });
    }

    /**
     * The bookkeeping half of `IX3_COMPONENT_INTERACTION_RECONCILED`: records a
     * baseline with no patch to apply. Kept as its own action type (rather than
     * a reconcile with an empty patch) because it records the seed
     * unconditionally, where the reconcile holds it back when a timeline edit of
     * its patch was skipped — see `ix3ComponentBaselineRecorded`.
     */
    case ix3ActionTypes.IX3_COMPONENT_BASELINE_RECORDED: {
      return produce(state, (draft) => {
        const {interactionId, componentSeed} = action.payload;
        const interaction = draft.interactions[interactionId];
        if (!interaction || interaction.deleted) return;
        interaction.componentSeed = componentSeed;
      });
    }

    case ix3ActionTypes.IX3_TRIGGER_UPDATED: {
      return produce(state, (draft) => {
        const {index, interactionId, trigger, componentPatch} = action.payload;

        const interaction = draft.interactions[interactionId];

        if (!interaction) {
          return;
        }

        const hasScrollScrubConfig = (
          triggers: typeof interaction.triggers
        ): boolean =>
          triggers.some(
            ([, triggerConfig]) =>
              triggerConfig?.controlType === TimelineControlType.SCROLL &&
              triggerConfig?.scrollTriggerConfig?.scrub != null
          );

        // Capture scrub state *before* applying the edit so we can tell a genuine
        // off→on scrub toggle apart from an unrelated edit to an already-scrubbed
        // trigger (e.g. start/end offsets or scrub smoothness).
        const hadScrollScrub = hasScrollScrubConfig(interaction.triggers);

        // Update trigger at the specified index. Shared with the Designer's
        // reconcile path — see `triggersAfterUpdate`.
        const updated = triggersAfterUpdate(
          interaction.triggers,
          index,
          trigger
        );
        if (updated != null) {
          interaction.triggers = updated;
          // Retargeting a component trigger renames the instance it points at,
          // so a sibling narrowed to the old one has to be re-narrowed. See
          // `pruneAssignedInstances`.
          pruneAssignedInstances(interaction);
        }

        // Keep the percent-canvas marker in sync with the interaction's
        // scroll-scrub state. The trigger editor can toggle scrub on/off after
        // a timeline (and its actions) already exist; without this the marker
        // goes stale — a newly scrubbed timeline never enters percent mode (no
        // marker, no runtime pad) and a de-scrubbed one keeps padding at runtime
        // and reading as percent in the editor. Scope to scroll-driven
        // (role-less) timelines: mouse-move timelines carry a role and own their
        // marker from creation.
        const hasScrollScrub = hasScrollScrubConfig(interaction.triggers);
        // Only seed the marker on the off→on transition. Seeding on every
        // scroll-trigger edit would convert a legacy (pre-feature) scrub
        // timeline with no marker to percent/padded playback the first time the
        // user tweaks any unrelated trigger setting — the editor deliberately
        // keeps such marker-less timelines time-based.
        const scrubNewlyEnabled = !hadScrollScrub && hasScrollScrub;
        for (const timelineId of interaction.timelineIds) {
          const timeline = draft.timelines[timelineId];
          if (timeline == null || timeline.triggerMetadata?.role != null) {
            continue;
          }
          if (hasScrollScrub) {
            if (scrubNewlyEnabled && timeline.canvasDuration == null) {
              timeline.canvasDuration = PERCENT_CANVAS_DURATION_S;
              clearTimelinePlaybackDefaults(timeline);
              // Normalize starts/durations authored on the old seconds canvas
              // (including relative GSAP positions) so nothing reads or plays
              // past the freshly-seeded 100% marker.
              clampTimelineActionsToPercentCanvas(
                timeline,
                PERCENT_CANVAS_DURATION_S
              );
            }
          } else if (timeline.canvasDuration != null) {
            // Clear the marker (every reader treats `== null` as "not a percent
            // canvas", and serialization drops the undefined key).
            // eslint-disable-next-line webflow/no-delete
            delete timeline.canvasDuration;
          }
        }

        // The reconcile this retarget calls for, in the same undo step — see
        // `applyComponentInteractionPatch`. Only when the update landed.
        if (updated != null && componentPatch != null) {
          applyComponentInteractionPatch(draft, interaction, componentPatch);
        }
      });
    }

    case ix3ActionTypes.IX3_TRIGGER_CREATED: {
      return produce(state, (draft) => {
        const {interactionId, trigger, componentPatch} = action.payload;
        // finds the current interaction we're in using the interactionId
        const interaction = draft.interactions[interactionId];
        if (interaction == null) return;
        // In multi-timeline mode a newly added STANDARD trigger needs two
        // adjustments before it joins the interaction:
        //   1. Only Play is supported — coerce the incoming trigger so a schema
        //      default (e.g. Click's) can't introduce a non-Play control the
        //      locked dropdown would otherwise have to display.
        //   2. Pin `assignedGroupId` to the explicit `null` sentinel. A new
        //      trigger arrives with `assignedGroupId` undefined, which
        //      StandardTriggerStrategy treats as the legacy "play every group"
        //      default — so without this the trigger would silently drive all
        //      action groups on preview/publish. `null` means "explicitly
        //      unassigned" (plays nothing) and surfaces the "not assigned to an
        //      action group" warning until the user routes it via the Assign
        //      select. Mirrors the group-removal path, which also parks
        //      orphaned triggers at `null`.
        // Skip both adjustments for non-STANDARD triggers (LOAD/SCROLL/
        // CONTINUOUS): their strategies ignore `assignedGroupId` and play every
        // timeline, and the Designer hides the Assign control for them — so
        // parking them at `null` would only surface an orphan warning the user
        // can't resolve. They join unchanged (play-all). See
        // isStandardRoutedTrigger.
        // Role-based groups (Hover's Mouse enter / Mouse leave) are the other
        // multi-timeline shape, and they need the OPPOSITE treatment: no
        // `assignedGroupId` (role timelines carry no `groupId`, so pinning the
        // `null` sentinel would make the trigger play nothing) and no Control
        // coercion (a role timeline reads its own `settings.control`). What a
        // new same-plugin trigger does need is the role-mode discriminator: a
        // Hover added alongside an existing role-grouped Hover arrives with the
        // schema default `multiTimeline: false`, so `buildHover` emits role-LESS
        // callbacks and each mouse event drives BOTH role timelines. Flip it,
        // and only for new-model data, so a legacy Hover (no discriminator)
        // stays on its legacy runtime path.
        // Two ways a new trigger lands in an interaction that already owns role
        // groups: alongside a sibling still in role mode, or after every owner
        // was deleted (the timelines outlive their Hover as long as another
        // trigger keeps the interaction alive). Both need the discriminator —
        // in the reclaim case there is no sibling to read it from, so accept
        // "no same-key sibling that could own the roles" too. A NEW-MODEL
        // same-key sibling that is not in role mode is deliberately left out:
        // that interaction is single-group by its owner's own config, and the
        // role tags are the stale half. A LEGACY sibling (no boolean
        // discriminator) expresses no such opinion — it can't own roles at all,
        // which is exactly what `isNewModelRoleOwner` decides everywhere else
        // (`findTimelineGroupOwner`, `triggerOwnsRoles`, `clearRoleRouting`), so
        // counting it here would block the reclaim the Designer already reports
        // as orphaned and leave the replacement Hover driving BOTH roles off one
        // role-less callback.
        const sameKeySiblings = interaction.triggers.filter(
          (existing) =>
            existing[0] === trigger[0] &&
            isNewModelRoleOwner(existing[1].pluginConfig)
        );
        const roleModeSibling = sameKeySiblings.find(
          (existing) => existing[1].pluginConfig?.multiTimeline === true
        );
        const joinsRoleGroups =
          (roleModeSibling != null || sameKeySiblings.length === 0) &&
          // New-model only: `buildHover` takes the role path for a BOOLEAN
          // discriminator, so promoting a legacy Hover (undefined) would write
          // new-model data the legacy runtime still reads by `type`/`hover`.
          // Same boundary as the Designer's `isNewModelGroupTrigger`.
          isNewModelRoleOwner(trigger[1].pluginConfig) &&
          // The persisted roles must be ones THIS key emits, not merely some
          // role. Mixed/imported data can leave another plugin's roles behind —
          // a Navbar's `open`/`close` outliving its trigger — and promoting a
          // Hover on the strength of those writes `multiTimeline: true` for a
          // trigger that emits `mouseEnter`/`mouseLeave`:
          // `StandardTriggerStrategy` resolves an empty subset, the Hover drives
          // nothing at all, and the Designer reads it as the role owner and
          // stops offering the repair controls. Leaving it unpromoted is the
          // recoverable failure — the legacy role-less callback still plays the
          // timelines, over-firing rather than going silently dead.
          //
          // EVERY role-tagged timeline must be in the contract, not merely one:
          // partially mixed data (`[mouseEnter, close]`, a Navbar's role left
          // behind next to a Hover's) satisfies "some" on the `mouseEnter` row
          // while the `close` row stays unreachable — and promoting on that
          // strength is worse than not promoting, because the Designer then
          // reads the Hover as the role owner and stops offering the repair
          // controls for the row it can't drive. Untagged rows are ignored;
          // they're routed by whatever plain trigger drives them.
          (() => {
            const contract = timelineRolesForTriggerKey(trigger[0]);
            if (!contract) return false;
            let sawOwnRole = false;
            for (const id of interaction.timelineIds) {
              const timeline = draft.timelines[id];
              // Live rows only — a deleted row's leftover role can't be driven
              // by anything, so letting one block promotion would strand the
              // interaction on the legacy path over a tombstone.
              if (!timeline || timeline.deleted) continue;
              const role = timeline.triggerMetadata?.role;
              if (role == null) continue;
              if (!contract.has(role)) return false;
              sawOwnRole = true;
            }
            return sawOwnRole;
          })();

        // Role-routed interactions get the same parking. Only a CONTINUOUS
        // owner is excluded: mouse-move legitimately drives every one of its
        // roles at once, where open/close and hover in/out do not. Deliberately
        // NOT `every(isStandardRoutedTrigger)` — that also excluded LOAD and
        // SCROLL, so adding a Page load to a popover interaction and then a
        // Click left the Click with both routing fields undefined, which
        // StandardTriggerStrategy reads as "play every timeline": one click
        // running Open and Close together, the exact failure parking exists to
        // prevent.
        const hasContinuousTrigger = interaction.triggers.some(
          ([, config]) => config?.controlType === TimelineControlType.CONTINUOUS
        );
        const needsParking =
          isMultiTimelineInteraction(interaction, draft.timelines) ||
          (isRoleRoutedInteraction(interaction, draft.timelines) &&
            !hasContinuousTrigger);

        // A trigger that arrives already routed keeps its route. Parking it at
        // the unassigned sentinel would drop the assignment on the floor —
        // `resolveAssignedTimelineIds` reads `assignedGroupId` first, so the
        // `null` would keep winning. The Play coercion still applies either way.
        const isPreRouted =
          trigger[1].assignedGroupId != null ||
          trigger[1].assignedTimelineRole != null;

        const nextTrigger: Trigger = joinsRoleGroups
          ? createTrigger(
              trigger[0],
              {
                ...trigger[1],
                pluginConfig: {...trigger[1].pluginConfig, multiTimeline: true},
              },
              trigger[2]
            )
          : isStandardRoutedTrigger(trigger) && needsParking
            ? createTrigger(
                trigger[0],
                isPreRouted
                  ? {...trigger[1], control: 'play'}
                  : {...trigger[1], control: 'play', assignedGroupId: null},
                trigger[2]
              )
            : trigger;

        const hasScrollScrubConfig = (
          triggers: typeof interaction.triggers
        ): boolean =>
          triggers.some(
            ([, triggerConfig]) =>
              triggerConfig?.controlType === TimelineControlType.SCROLL &&
              triggerConfig?.scrollTriggerConfig?.scrub != null
          );

        // Capture scrub state *before* the push so we only seed on the off→on
        // transition (mirrors IX3_TRIGGER_UPDATED). Detection reads the trigger
        // configs, which the multi-timeline coercion above leaves untouched for
        // scroll triggers (they're not standard-routed), so `nextTrigger` and
        // the original `trigger` are interchangeable here.
        const hadScrollScrub = hasScrollScrubConfig(interaction.triggers);

        // pushes the new trigger in the correct shape
        interaction.triggers.push(nextTrigger);

        // The default Scroll trigger config already enables scrub, so adding a
        // Scroll trigger to an interaction that already has role-less timelines
        // must seed the percent-canvas marker — otherwise the new scrub trigger
        // stays on the seconds canvas and the runtime never pads it. Mirrors the
        // IX3_TRIGGER_UPDATED seed: only on off→on, marker absent, and scoped to
        // scroll-driven (role-less) timelines.
        const scrubNewlyEnabled =
          !hadScrollScrub && hasScrollScrubConfig(interaction.triggers);
        if (scrubNewlyEnabled) {
          for (const timelineId of interaction.timelineIds) {
            const timeline = draft.timelines[timelineId];
            if (timeline == null || timeline.triggerMetadata?.role != null) {
              continue;
            }
            if (timeline.canvasDuration == null) {
              timeline.canvasDuration = PERCENT_CANVAS_DURATION_S;
              clearTimelinePlaybackDefaults(timeline);
              // Normalize starts/durations authored on the old seconds canvas
              // (including relative GSAP positions) so nothing reads or plays
              // past the freshly-seeded 100% marker.
              clampTimelineActionsToPercentCanvas(
                timeline,
                PERCENT_CANVAS_DURATION_S
              );
            }
          }
        }

        // The reconcile this create calls for, in the same undo step — see
        // `applyComponentInteractionPatch`.
        if (componentPatch != null) {
          applyComponentInteractionPatch(draft, interaction, componentPatch);
        }
      });
    }

    case ix3ActionTypes.IX3_TRIGGER_TIMELINE_ASSIGNED: {
      return produce(state, (draft) => {
        const {interactionId, triggerIndex, groupId} = action.payload;
        const interaction = draft.interactions[interactionId];
        if (!interaction) return;

        const trigger = interaction.triggers[triggerIndex];
        if (!trigger) return;

        // Both writes below also clear the role. The two routing axes are
        // mutually exclusive, so the role is cleared here the same way
        // IX3_TRIGGER_TIMELINE_ROLE_ASSIGNED clears the group.
        // `resolveAssignedTimelineIds` reads `assignedGroupId` first, so a role
        // left behind never routes, and `findAssignedTimelineRoleError`
        // rejects a newly written pair.
        trigger[1] = resolveTriggerGroupAssignment(
          trigger,
          groupId,
          interaction.timelineIds.map((id) => draft.timelines[id])
        );
      });
    }

    case ix3ActionTypes.IX3_TRIGGER_TIMELINE_ROLE_ASSIGNED: {
      return produce(state, (draft) => {
        const {interactionId, triggerIndex, role} = action.payload;
        const interaction = draft.interactions[interactionId];
        if (!interaction || interaction.deleted) return;

        const trigger = interaction.triggers[triggerIndex];
        if (!trigger) return;
        // A non-STANDARD trigger (LOAD, SCROLL) ignores both routing fields and
        // its Assign control is hidden, so neither a role nor the unassigned
        // sentinel can be written or cleared from the UI. Only reachable
        // through a stale index.
        if (!isStandardRoutedTrigger(trigger)) return;
        // A stale trigger index can now point at a role owner, which supplies
        // roles rather than consuming them. The schema invariant rejects an
        // assignment on one, so writing it here would make the interaction
        // unsaveable. An unassign is no different: with no role to write it
        // would fall through to the unassigned sentinel below and park the
        // owner itself, so a role owner is never written through this reducer.
        if (getMultiTimelineRoles(trigger[0], trigger[1]) != null) {
          return;
        }

        // A trigger pointed at the Backdrop can only dismiss, and the schema
        // invariant (`findComponentBackdropRoleError`) rejects any other role on
        // it, and any role at all on a Focus, Blur or Change trigger, which the
        // backdrop never raises (those stay parked). An assign that was in
        // flight while a peer retargeted the trigger would otherwise write one
        // and leave the interaction unsaveable. Asked of the invariant itself,
        // with the config this assign would write, so the two cannot drift.
        // A Modal's backdrop takes Click alone; the same check covers it.
        const target = trigger[2];
        if (role != null && Array.isArray(target)) {
          const assigned: Interaction['triggers'] = [
            [
              trigger[0],
              {
                ...trigger[1],
                assignedGroupId: undefined,
                assignedTimelineRole: role,
              },
              target,
            ],
          ];
          if (findComponentBackdropRoleError(assigned) != null) {
            return;
          }
        }

        // Same multiplayer-race guard the group path uses: a peer may have
        // removed the role's timeline while this assign was in flight. Fall
        // back to the unassigned sentinel rather than persisting a role that
        // routes to nothing and can't be cleared from the Assign select. The
        // role also has to still belong to a trigger: deleting the last owner
        // clears its assignments but leaves the role's timelines behind, so a
        // stale assign arriving after it would otherwise restore one.
        const roleExists =
          role != null &&
          interaction.triggers.some(([key, config]) =>
            getMultiTimelineRoles(key, config)?.has(role)
          ) &&
          interaction.timelineIds.some((id) => {
            const tl = draft.timelines[id];
            return (
              tl != null && !tl.deleted && tl.triggerMetadata?.role === role
            );
          });

        if (role != null && roleExists) {
          // Any conditional outcome still pointing at the OLD role's timeline
          // has to be released. `executeConditionalOutcome` intersects the
          // outcome's `targetTimelineId` with the newly assigned role and
          // silently does nothing on an empty set, so a reassigned trigger
          // would look like it had stopped working. Mirrors the group axis
          // above.
          const roleTimelineIds = new Set<string>(
            interaction.timelineIds.filter((id) => {
              const tl = draft.timelines[id];
              return (
                tl != null && !tl.deleted && tl.triggerMetadata?.role === role
              );
            })
          );
          const clearForeignOutcomeTarget = <
            O extends {targetTimelineId?: string},
          >(
            outcome: O
          ): O =>
            outcome.targetTimelineId != null &&
            !roleTimelineIds.has(outcome.targetTimelineId)
              ? {...outcome, targetTimelineId: undefined}
              : outcome;

          const conditionalLogic = trigger[1].conditionalLogic;
          // The two routing axes are mutually exclusive (see
          // timelineGroupConfig.groupRoles), and `resolveAssignedTimelineIds`
          // reads `assignedGroupId` first — so it has to be cleared, or the
          // parked `null` would keep winning and the trigger would still drive
          // nothing.
          trigger[1] = {
            ...trigger[1],
            assignedTimelineRole: role,
            assignedGroupId: undefined,
            ...(conditionalLogic
              ? {
                  conditionalLogic: mapConditionalOutcomes(
                    conditionalLogic,
                    clearForeignOutcomeTarget
                  ),
                }
              : {}),
          };
          return;
        }

        // Back to unassigned: `assignedGroupId: null` is the sentinel the
        // runtime already resolves to "plays nothing", and the one a newly
        // added trigger arrives with.
        const previousRole = trigger[1].assignedTimelineRole;
        const unassignedLogic = trigger[1].conditionalLogic;
        trigger[1] = {
          ...trigger[1],
          assignedTimelineRole: undefined,
          assignedGroupId: null,
          // A targetless outcome was relative to the role this just removed.
          ...(unassignedLogic
            ? {
                conditionalLogic: pinRoleRelativeOutcomes(
                  unassignedLogic,
                  liveRoleTimelineId(interaction, draft.timelines, previousRole)
                ),
              }
            : {}),
        };
      });
    }

    case ix3ActionTypes.IX3_TRIGGER_INSTANCES_ASSIGNED: {
      return produce(state, (draft) => {
        const {interactionId, triggerIndex, instances} = action.payload;
        const interaction = draft.interactions[interactionId];
        if (!interaction || interaction.deleted) return;

        const trigger = interaction.triggers[triggerIndex];
        if (!trigger) return;
        // A component-instance trigger already IS one instance, and the schema
        // invariant rejects `assignedInstances` on it — so writing one here
        // would make the interaction unsaveable. The sibling group and role
        // reducers both validate before writing; this one has to as well.
        if (isComponentInstanceTriggerKey(trigger[0])) return;

        trigger[1] = {
          ...trigger[1],
          assignedInstances: narrowAssignedInstances(
            instances,
            interaction.triggers
          ),
        };
      });
    }

    case ix3ActionTypes.IX3_ONE_SHOT_FIRST_WINS_CHANGED: {
      return produce(state, (draft) => {
        const {interactionId, firstWins} = action.payload;
        const interaction = draft.interactions[interactionId];
        if (!interaction || interaction.deleted) return;

        for (const trigger of interaction.triggers) {
          if (!ONE_SHOT_TRIGGER_KEYS.has(trigger[0])) continue;
          // Already in the state asked for: left alone, so a config that never
          // carried the key is not rewritten.
          if ((trigger[1].pluginConfig?.firstWins === true) === firstWins) {
            continue;
          }
          // Off removes the key rather than storing `false`, so turning it off
          // leaves the config as it was before it was ever on.
          const {firstWins: _, ...rest} = trigger[1].pluginConfig ?? {};
          const pluginConfig = firstWins ? {...rest, firstWins: true} : rest;
          trigger[1] = {
            ...trigger[1],
            pluginConfig: Object.keys(pluginConfig).length
              ? pluginConfig
              : undefined,
          };
        }
      });
    }

    // ============================================================================
    // Timeline Groups
    // ============================================================================

    case ix3ActionTypes.IX3_TIMELINE_GROUP_ADDED: {
      return produce(state, (draft) => {
        const {interactionId, groupConfig} = action.payload;
        const interaction = draft.interactions[interactionId];
        if (!interaction) return;

        const currentPageId = selectCurrentPageId(action.state);

        // Role-based transition (e.g. Hover): tag timelines via
        // `triggerMetadata.role` instead of `groupId` so the runtime routes
        // by role (mouse enter/leave) and labels derive from the trigger's
        // role definitions (non-editable). Flip the trigger into
        // multi-timeline mode and apply the multi-group control to the
        // trigger and every timeline's settings.
        const roles = groupConfig?.roles;
        // Beta Hover interactions authored before Hover declared `groupRoles`
        // already carry `groupId`-tagged timelines and `assignedGroupId` on
        // their triggers. Role tagging doesn't clear either, so mixing the two
        // models would leave a timeline that routes by role while its trigger
        // still narrows to a group id. Keep any interaction that already has
        // generic groups on the generic path for the rest of its life; only
        // interactions that reach the transition ungrouped become role-based.
        // Live rows only: a soft-deleted timeline's leftover groupId can't be
        // routed, so it must not pin the interaction to the generic path.
        const hasGenericGroups = interaction.timelineIds.some((id) => {
          const timeline = draft.timelines[id];
          return !timeline?.deleted && timeline?.groupId != null;
        });
        if (roles && roles.length > 0 && !hasGenericGroups) {
          const existingIds = interaction.timelineIds;
          const nextIndex = existingIds.length;
          // Defensive cap: only as many groups as declared roles. The
          // Designer hook gates the button at `timelineGroupConfig.maxGroups`.
          if (nextIndex >= roles.length) return;

          // Snapshot the pre-transition timelines: `existingIds` aliases the
          // draft array we push the new timeline onto below.
          const priorTimelineIds = new Set<string>(existingIds);

          // Roles are assigned to existing timelines in declaration order, so
          // by default the authored timeline becomes roles[0]. When the plugin
          // declares which role a `pluginConfig` value corresponds to, honor it:
          // a Hover already narrowed to "Mouse leave" has authored its actions
          // for the hover-OUT event, and promoting them to the hover-in role
          // would silently move every action to the opposite mouse event.
          // Rotating that role to the front keeps the existing timeline on the
          // event it was authored for and gives the new (empty) group the other
          // one. Index 0 stays the original timeline either way, so the
          // non-removable-primary rule in the group UI is unaffected.
          // The trigger the Designer resolved this group config from — not
          // necessarily `triggers[0]` (see resolveRoleOwnerTrigger). Its
          // `pluginConfig` is the one that says which role the existing
          // timeline should keep, and its extension key scopes the
          // multi-timeline flip below.
          const ownerTrigger = resolveRoleOwnerTrigger(
            interaction.triggers,
            groupConfig?.ownerExtensionKey
          );
          // The named owner is gone — a concurrent edit deleted it between
          // dispatch and reduce. Adding role groups now would tag the timelines
          // with a deleted plugin's roles and route them through a trigger that
          // never declared them, leaving groups nothing can drive. No-op
          // instead; the Designer re-derives the affordance from live state.
          if (groupConfig?.ownerExtensionKey != null && !ownerTrigger) return;
          const primaryRoleConfig = groupConfig?.primaryRoleByPluginConfig;
          const primaryRoleValue =
            ownerTrigger?.[1].pluginConfig?.[primaryRoleConfig?.key ?? ''];
          const pluginConfigRole =
            primaryRoleConfig && typeof primaryRoleValue === 'string'
              ? primaryRoleConfig.roles[primaryRoleValue]
              : undefined;
          // A role the existing timeline ALREADY carries outranks the
          // pluginConfig mapping. The Designer's own transition clears the
          // narrowing pluginConfig (`clearPluginConfigOnMultiGroup`) as it
          // writes the roles, so a lone timeline that arrives here already
          // tagged `mouseLeave` with no `eventMode` left to read is
          // imported/API-authored data whose persisted role is the only record
          // of which event its actions were written for. Falling through to
          // declaration order would rewrite it to `mouseEnter` and silently
          // move every action to the opposite mouse event — the exact swap the
          // pluginConfig mapping exists to prevent. Only a role the plugin
          // still declares counts; a stale one falls back as before.
          const existingRole =
            draft.timelines[existingIds[0] ?? '']?.triggerMetadata?.role;
          const preferredRole =
            existingRole != null && roles.includes(existingRole)
              ? existingRole
              : pluginConfigRole;
          const preferredIndex = preferredRole
            ? roles.indexOf(preferredRole)
            : -1;
          const orderedRoles =
            preferredIndex > 0
              ? [
                  roles[preferredIndex]!,
                  ...roles.filter((_, i) => i !== preferredIndex),
                ]
              : roles;

          const multiControl = groupConfig?.multiGroupControl;

          // Assign roles + multi-group control to existing timelines in order.
          existingIds.forEach((tid, i) => {
            const tl = draft.timelines[tid];
            if (!tl || i >= orderedRoles.length) return;
            tl.triggerMetadata = {...tl.triggerMetadata, role: orderedRoles[i]};
            if (multiControl) {
              tl.settings = {...tl.settings, control: multiControl};
            }
          });

          // A single-timeline interaction reads Delay/Jump/Speed from its
          // trigger config, but a role-based timeline reads them from
          // `timeline.settings` — getEffectivePlaybackConfig's role branch
          // never falls back to the trigger. Migrate the STANDARD triggers'
          // authored timing onto the primary role timeline (the first existing
          // one, promoted to roles[0]) and strip it from the triggers so it
          // keeps affecting playback after the transition. Mirrors the generic
          // group path (see migrateStandardTriggerTimingToTimeline). On later
          // adds (already
          // multi-timeline) the triggers carry no timing, so this no-ops.
          const primaryTimelineId = existingIds[0];
          const primaryTimeline = primaryTimelineId
            ? draft.timelines[primaryTimelineId]
            : undefined;
          migrateStandardTriggerTimingToTimeline(
            interaction.triggers,
            primaryTimeline
          );

          // Create the new group's timeline with the next role.
          const newTimelineId = generateTimelineId();
          draft.timelines[newTimelineId] = {
            id: newTimelineId,
            deleted: false,
            pageId: currentPageId,
            actions: [],
            triggerMetadata: {role: orderedRoles[nextIndex]},
            ...(multiControl ? {settings: {control: multiControl}} : {}),
          };
          interaction.timelineIds.push(newTimelineId);

          // Flip the group-owning triggers into multi-timeline mode so the
          // runtime emits per-role callbacks, and seed the multi-group control.
          //
          // `multiTimeline` is the group-owning plugin's own discriminator
          // (only `buildHover` reads it), so this is scoped to that plugin: the
          // primary trigger always, plus any other trigger of the SAME
          // extension key that already carries the field. Exactly the rule
          // `clearRoleRouting` uses on the way back out, and the two have to
          // agree. Hover is `exclusivity: 'normal'` and only Page load is
          // capped at one per interaction, so a second Hover is reachable
          // straight from the UI; left on the `multiTimeline: false` path it
          // emits a role-less callback, `StandardTriggerStrategy` resolves no
          // route, and that Hover plays BOTH role timelines on a single mouse
          // event. Writing the field onto every trigger instead would put a
          // foreign `multiTimeline` on a Click or Page load that never had one,
          // and the `isNewModelRoleOwner` guard keeps a legacy sibling on its
          // legacy runtime path. Boolean, not merely defined: `buildHover`
          // takes the role path only for a boolean, so a JSON `null` from
          // saved/imported data is still legacy — promoting it here would
          // switch it onto role-callback routing (and drop its `eventMode`)
          // off data the runtime reads by `type`/`hover`. Same boundary as the
          // Designer's `isNewModelGroupTrigger`.
          const primaryKey = ownerTrigger?.[0];
          const roleOwningTriggers = interaction.triggers.filter(
            (trigger) =>
              trigger[0] === primaryKey &&
              (trigger === ownerTrigger ||
                isNewModelRoleOwner(trigger[1].pluginConfig))
          );
          for (const owningTrigger of roleOwningTriggers) {
            // Drop the single-group-only settings the plugin declares (Hover:
            // `eventMode`). Both events must fire to drive both role timelines,
            // so a narrowed Type would leave one group permanently dead. Park
            // the values rather than discarding them: removing the second group
            // restores single-group playback, and without the stash a Hover
            // narrowed to "Mouse leave" would come back as enter-and-leave —
            // firing on an event the author had deliberately turned off.
            //
            // OWNER only. A sibling Hover keeps its own `eventMode`: the group
            // it would leave dead is the owner's problem, not its own, and the
            // second group already has the owner driving both events. Clearing
            // the sibling's filter instead makes it start emitting the role it
            // was explicitly narrowed away from — a "Mouse enter" Hover would
            // begin running the hover-out actions. Siblings still get the
            // `multiTimeline` flip below, which is what keeps their callbacks
            // role-routed.
            const clearedKeys = new Set(
              owningTrigger === ownerTrigger
                ? (groupConfig?.clearPluginConfigOnMultiGroup ?? [])
                : []
            );
            const entries = Object.entries(owningTrigger[1].pluginConfig ?? {});
            const keptPluginConfig = Object.fromEntries(
              entries.filter(([key]) => !clearedKeys.has(key))
            );
            const stash = Object.fromEntries(
              entries.filter(([key]) => clearedKeys.has(key))
            );
            owningTrigger[1] = {
              ...owningTrigger[1],
              pluginConfig: {
                ...keptPluginConfig,
                ...(Object.keys(stash).length > 0
                  ? {[MULTI_GROUP_PLUGIN_CONFIG_STASH]: stash}
                  : {}),
                multiTimeline: true,
              },
            };

            // A single-timeline interaction's conditional outcome usually pins
            // `targetTimelineId` to that lone timeline — the outcome editor
            // auto-fills the first available timeline as soon as the user picks
            // a control. Once roles exist, this trigger fires once per role with
            // only that role's timeline allowed, and `executeConditionalOutcome`
            // intersects the pin with that set: the pin (a pre-transition
            // timeline, now one role's) makes every OTHER role's event resolve
            // to nothing, so the new group's actions never run under conditions
            // no matter what the author puts in it. Drop the stale pin so each
            // role event drives its own timeline; the outcome editor now lists
            // both, so re-pinning stays available. Only the role owners are
            // role-narrowed — a Click added alongside still plays every timeline
            // and its pin remains a valid, deliberate choice. The reassign-side
            // `retargetForeignOutcome` repoints instead of clearing, because
            // there the trigger stays generic and a targetless outcome would be
            // invalid; a role owner is exactly the case that accepts one.
            const conditionalLogic = owningTrigger[1].conditionalLogic;
            if (conditionalLogic) {
              owningTrigger[1] = {
                ...owningTrigger[1],
                conditionalLogic: mapConditionalOutcomes(
                  conditionalLogic,
                  (outcome) =>
                    outcome.targetTimelineId != null &&
                    priorTimelineIds.has(outcome.targetTimelineId)
                      ? {...outcome, targetTimelineId: undefined}
                      : outcome
                ),
              };
            }
          }
          // The multi-group Control belongs only to the triggers that OWN the
          // roles, and only when they route through group playback. Two gates:
          //   - Non-STANDARD (e.g. a Page load saved as `none`) ignores role
          //     routing, so overwriting its Control would clobber its own
          //     semantics — mirrors the generic path's
          //     coerceTriggerControlsToPlay gate and the collapse-side gate.
          //   - Any trigger other than the resolved OWNER — a Click added
          //     alongside the Hover, or a second Hover — has its own authored
          //     Control, and while the timelines are role-tagged that field is
          //     DORMANT: getEffectivePlaybackConfig's role branch reads
          //     `timeline.settings.control` and never falls back to the trigger.
          //     So writing the group's Control there changes nothing at runtime
          //     and everything on the way back out — the collapse restores
          //     `singleGroupControl` to the owner only, so an add/remove round
          //     trip would silently rewrite the sibling's playback. Note this is
          //     narrower than the `multiTimeline` flip above, which every
          //     same-key Hover needs for ROUTING (a non-flipped sibling emits
          //     role-less callbacks and drives both role timelines).
          if (
            multiControl &&
            ownerTrigger &&
            isStandardRoutedTrigger(ownerTrigger)
          ) {
            ownerTrigger[1] = {...ownerTrigger[1], control: multiControl};
          }

          // Focus the newly added group.
          draft.uiSelectedTimelineIndex = interaction.timelineIds.length - 1;
          draft.selectedTimelineActionIds = [];
          return;
        }

        // Never mix the two routing models. Role-tagged timelines route by
        // `triggerMetadata.role`; the generic path below stamps `groupId` on
        // every existing timeline and narrows the triggers with
        // `assignedGroupId`, which would leave a timeline routed both ways at
        // once. This is reachable: Hover is `exclusivity: 'normal'`, so an
        // author can add a Click alongside a role-grouped Hover and then delete
        // the Hover — the group config is resolved from the new primary trigger
        // (the Click, which declares no roles), and its "Add action group" would
        // otherwise corrupt the orphaned role timelines. Bail instead; the
        // Designer hook hides the button for the same state.
        // Live timelines only, matching the Designer hook: a soft-deleted
        // role timeline can't be driven, so it must not block the add.
        const hasRoleTaggedTimelines = interaction.timelineIds.some((id) => {
          const timeline = draft.timelines[id];
          return !timeline?.deleted && timeline?.triggerMetadata?.role != null;
        });
        if (hasRoleTaggedTimelines) return;

        // Defensive cap. The Designer hook gates the "Add action group" button
        // at the trigger's own `maxGroups` (e.g. Hover's 2), falling back to the
        // global IX3_MAX_TIMELINE_GROUPS. Enforce the effective cap here too so a
        // stale double-dispatch (e.g. concurrent multiplayer adds, or direct
        // callers that bypass the hook) can't push the trigger past its cap.
        const effectiveMaxGroups = Math.min(
          groupConfig?.maxGroups ?? IX3_MAX_TIMELINE_GROUPS,
          IX3_MAX_TIMELINE_GROUPS
        );
        if (interaction.timelineIds.length >= effectiveMaxGroups) {
          return;
        }

        const existingTimelineIds = interaction.timelineIds;

        // If transitioning from single-timeline to multi-timeline,
        // assign group ids and names to existing timelines and triggers
        const hasExistingGroupIds = existingTimelineIds.some((id) => {
          const timeline = draft.timelines[id];
          return timeline?.groupId != null;
        });

        if (!hasExistingGroupIds && existingTimelineIds.length > 0) {
          for (let i = 0; i < existingTimelineIds.length; i++) {
            const timelineId = existingTimelineIds[i];
            if (!timelineId) continue;
            const timeline = draft.timelines[timelineId];
            if (!timeline) continue;

            const groupId = generateGroupId();
            timeline.groupId = groupId;
            timeline.name =
              groupConfig?.groupNames?.[i] ?? `Action group ${i + 1}`;

            // Auto-assign existing triggers to the first group. Only STANDARD
            // triggers route via `assignedGroupId`; LOAD/SCROLL/CONTINUOUS
            // ignore it and their Assign control is hidden, so stamping a
            // group id on them creates an orphan that group-removal later
            // converts to the `null` "play nothing" sentinel with no UI to
            // clear it. Mirrors the split-path gate (see isStandardRoutedTrigger).
            if (i === 0) {
              for (const trigger of interaction.triggers) {
                if (
                  isStandardRoutedTrigger(trigger) &&
                  !trigger[1].assignedGroupId
                ) {
                  trigger[1] = {...trigger[1], assignedGroupId: groupId};
                }
              }
            }
          }

          // Move the STANDARD triggers' Delay/Jump/Speed onto the first group
          // so grouped playback keeps honoring it, then strip it from the
          // triggers (see migrateStandardTriggerTimingToTimeline for why).
          const firstTimelineId = existingTimelineIds[0];
          const firstTimeline = firstTimelineId
            ? draft.timelines[firstTimelineId]
            : undefined;
          migrateStandardTriggerTimingToTimeline(
            interaction.triggers,
            firstTimeline
          );

          // Entering multi-timeline mode: the only supported trigger Control
          // across action groups is `play`. Coerce any non-Play control so
          // stale values don't survive (the trigger Control dropdown is locked
          // to Play in multi-timeline mode — see IX3TriggerEditor).
          coerceTriggerControlsToPlay(interaction.triggers);
        } else if (existingTimelineIds.length === 1) {
          // Beta Hover data can already carry ONE `groupId`-tagged timeline, so
          // the block above is skipped and its Control coercion never runs — yet
          // this add is exactly the single→multi crossing that locks the editor
          // to Play and that `findGroupedTriggerControlError` enforces. Without
          // it a coexisting trigger keeps Restart/Toggle and the runtime restarts
          // or toggles groups the editor says can only play. Later adds (2+
          // groups already) were coerced on their own crossing.
          coerceTriggerControlsToPlay(interaction.triggers);
        }

        const newGroupId = generateGroupId();
        const newTimelineId = generateTimelineId();
        const groupCount = existingTimelineIds.length + 1;

        draft.timelines[newTimelineId] = {
          id: newTimelineId,
          deleted: false,
          pageId: currentPageId,
          actions: [],
          name:
            groupConfig?.groupNames?.[groupCount - 1] ??
            `Action group ${groupCount}`,
          groupId: newGroupId,
        };

        interaction.timelineIds.push(newTimelineId);
      });
    }

    case ix3ActionTypes.IX3_TIMELINE_GROUP_REMOVED: {
      return produce(state, (draft) => {
        const {interactionId, timelineId, groupConfig} = action.payload;
        const interaction = draft.interactions[interactionId];
        if (!interaction) return;

        // Prevent removing the last timeline group
        if (interaction.timelineIds.length <= 1) return;

        const timeline = draft.timelines[timelineId];
        if (!timeline) return;

        // Defensive: ignore a payload whose timeline isn't part of this
        // interaction (a stale multiplayer op, or a direct caller). Without
        // this the `timelineIds` filter below is a no-op, yet we'd still
        // soft-delete a timeline owned by a *different* interaction and reset
        // this interaction's UI/assignments. Mirrors the membership discipline
        // used by isMultiTimelineInteraction / selectTimelineGroups.
        if (!interaction.timelineIds.includes(timelineId)) return;

        const removedGroupId = timeline.groupId;
        const removedRole = timeline.triggerMetadata?.role;

        // Soft-delete the timeline
        timeline.deleted = true;

        // Remove from interaction's timelineIds
        interaction.timelineIds = interaction.timelineIds.filter(
          (id) => id !== timelineId
        );

        const roles = groupConfig?.roles;

        // Read after the filter above, so the deleted row is already out.
        const survivingTimelineIds = interaction.timelineIds;

        // If only 1 timeline remains, revert to single-timeline mode
        if (interaction.timelineIds.length === 1) {
          const remainingId = interaction.timelineIds[0];
          const remainingTimeline = remainingId
            ? draft.timelines[remainingId]
            : undefined;

          // Pick the collapse that matches how this interaction's groups are
          // actually tagged, not just what the trigger now declares. Beta Hover
          // data authored before Hover had `groupRoles` is `groupId`-tagged, so
          // a `removedGroupId` means generic groups regardless of the roles
          // config — the role collapse would leave its `groupId`/
          // `assignedGroupId` behind. Mirrors the entry-side guard.
          //
          // `removedRole` covers the ORPHANED-role removal the action editor now
          // offers: the role-owning Hover was deleted out from under its role
          // timelines, so the config resolves from the surviving Click and
          // arrives with no `roles`. The data is still role-tagged, and the
          // generic collapse would treat the Click's own authored Control as a
          // coerced group value and clear it (`clearCoercedControl`), silently
          // rewriting playback to the schema default. The role collapse only
          // writes a Control when it has a `singleGroupControl` to restore, so
          // routing by the tag leaves the non-owner trigger alone.
          if (
            ((roles && roles.length > 0) || removedRole != null) &&
            removedGroupId == null
          ) {
            collapseRoleGroupsToSingle(
              interaction.triggers,
              remainingTimeline,
              groupConfig?.singleGroupControl,
              groupConfig?.multiGroupControl,
              groupConfig?.ownerExtensionKey
            );
            // Heterogeneous tags ACROSS the two timelines: the removed one was
            // role-only — so `removedGroupId` is null and this branch ran —
            // while the survivor is `groupId`-tagged. The role collapse
            // migrated the survivor's timing onto the triggers and stripped
            // `settings`, so leaving the group id would have
            // `getEffectivePlaybackConfig` still read it as grouped and take
            // jump/speed from the settings just stripped. Clear the other axis
            // too. (The mirror of the `triggerMetadata.role` check below.)
            if (remainingTimeline?.groupId != null) {
              clearGenericRouting(interaction.triggers, remainingTimeline);
            }
          } else {
            // Decided BEFORE the collapse, because the answer changes what the
            // collapse is allowed to strip. `collapseGenericGroupsToSingle`
            // moves `settings.control/delay/jump/speed` onto the triggers,
            // which is right for a survivor that leaves role mode — but a
            // survivor whose role we keep (below) stays on
            // `getEffectivePlaybackConfig`'s ROLE branch, and that branch reads
            // playback ONLY from `timeline.settings` and never falls back to
            // the trigger config. Migrated timing would be unreachable there:
            // Delay/Jump/Speed silently dropped and `control: undefined`
            // defaulting to `restart`. Capture the role-read fields here and
            // re-seed them after the collapse.
            const survivorRole = remainingTimeline?.triggerMetadata?.role;
            const keepsFixedRole =
              survivorRole != null &&
              interaction.triggers.some((trigger) => {
                // The trigger must actually be able to route this role...
                if (
                  !timelineRolesForTriggerKey(trigger[0])?.has(survivorRole)
                ) {
                  return false;
                }
                // ...and route it by a SCHEMA-FIXED contract the survivor can't
                // leave, so clearing the role would strand it. Two strategies
                // qualify: callback-routed Navbar/Dropdown, and continuous
                // Mouse Move (whose channel registers by exact timeline role via
                // getTimelineIdsForRole — drop the role and it never resolves or
                // plays). A role-mode Hover is deliberately excluded: it CAN
                // leave role mode, so the generic collapse should clear it.
                return (
                  (ROLE_ROUTED_NO_PLAYBACK_KEYS.has(trigger[0]) &&
                    triggerRoutesByCallbackRole(trigger[0], trigger[1])) ||
                  triggerRoutesByContinuousRole(trigger[0], trigger[1])
                );
              });
            const rolePlayback = keepsFixedRole
              ? {
                  // The group's own authored Control, or the single-group
                  // Control the collapse would otherwise have restored onto the
                  // trigger — the role branch can't see the trigger copy.
                  control:
                    remainingTimeline?.settings?.control ??
                    groupConfig?.singleGroupControl,
                  delay: remainingTimeline?.settings?.delay,
                  jump: remainingTimeline?.settings?.jump,
                  speed: remainingTimeline?.settings?.speed,
                }
              : undefined;

            collapseGenericGroupsToSingle(
              interaction.triggers,
              remainingTimeline,
              groupConfig?.singleGroupControl,
              groupConfig?.ownerExtensionKey
            );
            // Mixed data: a schema-valid imported timeline may carry BOTH a
            // `groupId` and a role, and the branch above is chosen by
            // `groupId`. The generic collapse clears group routing only, so
            // without this the survivor keeps its role and the trigger keeps
            // `multiTimeline: true` — a single-timeline Hover still locked in
            // role mode, with only the matching mouse event able to drive it.
            // `!= null`, not truthiness: an EMPTY role is schema-valid, and
            // every runtime role check is a `role != null` presence test
            // (`getEffectivePlaybackConfig`, `getTimelineIdsForRole`). A
            // falsy-skipped `''` leaves the survivor role-routed under a
            // `multiTimeline: true` Hover that emits `mouseEnter`/`mouseLeave`
            // — neither matches, so the interaction goes inert.
            //
            // Unless a SCHEMA-FIXED role trigger still needs that role.
            // Navbar/Dropdown carry no `multiTimeline` discriminator — role
            // routing is their contract, not a mode they can leave — so
            // stripping the survivor's `open`/`close` would leave them emitting
            // role callbacks `StandardTriggerStrategy` can match to nothing and
            // the interaction goes inert. Their rows reach this branch at all
            // only through mixed data (a row carrying BOTH a `groupId` and the
            // fixed role), which `isUserCreatedGroup` reads as removable. Clear
            // the generic axis and leave the role alone.
            if (remainingTimeline?.triggerMetadata?.role != null) {
              if (keepsFixedRole) {
                // Put back what the role branch is the only reader of. The
                // trigger copies the collapse just wrote stay as they are —
                // inert while the role is present, and already correct if a
                // later edit does strip it.
                if (rolePlayback) {
                  const settings = {
                    ...remainingTimeline.settings,
                    ...(rolePlayback.control !== undefined && {
                      control: rolePlayback.control,
                    }),
                    ...(rolePlayback.delay !== undefined && {
                      delay: rolePlayback.delay,
                    }),
                    ...(rolePlayback.jump !== undefined && {
                      jump: rolePlayback.jump,
                    }),
                    ...(rolePlayback.speed !== undefined && {
                      speed: rolePlayback.speed,
                    }),
                  };
                  remainingTimeline.settings =
                    Object.keys(settings).length > 0 ? settings : undefined;
                }
              } else {
                clearRoleRouting(
                  interaction.triggers,
                  remainingTimeline,
                  groupConfig?.ownerExtensionKey
                );
              }
            }
          }

          // A surviving trigger's conditionalLogic may still target the group
          // timeline that was just deleted — e.g. a trigger whose outcome was
          // assigned to the removed group's timeline. In single-timeline
          // mode executeConditionalOutcome intersects `targetTimelineId` with
          // `interaction.timelineIds`, so a target pointing at the removed
          // timeline resolves to nothing and the outcome never plays. Repoint
          // any dangling outcome target at the surviving timeline so the kept
          // trigger keeps driving it under that condition. Runs for both the
          // role and non-role collapse branches.
          //
          // An ABSENT target is repointed too. At runtime it already resolves
          // to the lone survivor, but it only exists because the group-add
          // cleared the pin so each role could drive its own timeline — and
          // single-timeline mode is where the conditions editor requires a
          // target again, so leaving it absent makes an untouched add-then-
          // remove round trip surface "Timeline is required unless control is
          // none" on a configuration the author never edited. `none` outcomes
          // are exempt: the editor doesn't ask for a target there, and pinning
          // one would resurrect a field that control ignores.
          if (remainingId) {
            const remapOutcomeTarget = <
              O extends {targetTimelineId?: string; control?: string},
            >(
              outcome: O
            ): O =>
              outcome.targetTimelineId !== remainingId &&
              (outcome.targetTimelineId != null || outcome.control !== 'none')
                ? {...outcome, targetTimelineId: remainingId}
                : outcome;
            for (const trigger of interaction.triggers) {
              const cl = trigger[1].conditionalLogic;
              if (!cl) continue;
              trigger[1] = {
                ...trigger[1],
                conditionalLogic: mapConditionalOutcomes(
                  cl,
                  remapOutcomeTarget
                ),
              };
            }
          }
        } else if (removedGroupId) {
          orphanTriggersForRemovedGroup(
            interaction.triggers,
            removedGroupId,
            timelineId,
            survivingTimelineIds,
            draft.timelines
          );
        } else if (removedRole != null) {
          // Role-only row removed while 2+ timelines remain — the orphan-role
          // removal the action editor now offers on an imported interaction
          // with three role timelines and no role-owning trigger. There is no
          // `assignedGroupId` to orphan (a role row carries no `groupId`, which
          // is why the branch above is skipped), but a surviving trigger pinned
          // to the deleted timeline is left just as dead.
          clearDeletedOutcomeTargets(
            interaction.triggers,
            timelineId,
            survivingTimelineIds,
            draft.timelines
          );
          // The STATIC pin goes stale the same way, and only the outcome half
          // was repaired above. A trigger left holding
          // `assignedTimelineRole: removedRole` now narrows to a role no
          // surviving timeline carries, so `resolveAssignedTimelineIds` returns
          // an empty set on EVERY activation and the trigger goes silently
          // inert — with no Designer control for the legacy field, the author
          // can't see or undo it. Reachable on imported data: a Click statically
          // pinned to a secondary role beside an unassigned Click driving the
          // other rows is schema-valid, and the orphan-role UI offers exactly
          // that row for removal. Clear the pin rather than remap it — the role
          // it named is gone, and `undefined` is the field's own "no role
          // narrowing" value, the same repair `clearRoleRouting` makes on the
          // collapse path. Every trigger, not just one key: the pin is
          // schema-valid on any of them.
          for (const trigger of interaction.triggers) {
            if (trigger[1].assignedTimelineRole !== removedRole) continue;
            const {assignedTimelineRole: _removedRolePin, ...config} =
              trigger[1];
            trigger[1] = config;
          }
        }

        // Reset UI state
        draft.uiSelectedTimelineIndex = 0;
        draft.selectedTimelineActionIds = [];
      });
    }

    case ix3ActionTypes.IX3_TIMELINE_GROUP_RENAMED: {
      return produce(state, (draft) => {
        const {timelineId, name} = action.payload;
        const timeline = draft.timelines[timelineId];
        if (!timeline) return;
        timeline.name = name;
      });
    }

    case ix3ActionTypes.IX3_TRIGGER_CONTROL_CHANGED: {
      return produce(state, (draft) => {
        const {interactionId, ownerExtensionKey, ownerTriggerIndex, control} =
          action.payload;
        const interaction = draft.interactions[interactionId];
        if (!interaction) return;

        // Set the trigger-level control (source of truth for display and for
        // single-timeline runtime playback) on the OWNING trigger only — the
        // one whose Control the author just edited.
        //
        // Not every STANDARD trigger: Hover is `exclusivity: 'normal'`, so a
        // Click can sit beside it in the same interaction carrying its own
        // authored restart/toggle behavior. While the timelines are role/group
        // tagged that sibling's `control` is DORMANT (getEffectivePlaybackConfig
        // reads `timeline.settings.control` and never falls back to the
        // trigger), so writing the group's Control there changes nothing at
        // runtime and everything on the way back out — the collapse restores
        // playback from the trigger field, so the sibling would come back
        // silently rewritten. In the common SINGLE-group state the field is
        // live and the clobber is immediate. Same rule, and the same reasoning,
        // as the owner-only Control write in IX3_TIMELINE_GROUP_ADDED.
        //
        // Still gated on isStandardRoutedTrigger: the group Control fans out
        // through group/role playback, which LOAD/SCROLL/CONTINUOUS strategies
        // ignore, so a non-STANDARD owner would have its own control semantics
        // clobbered for no runtime effect.
        const ownerTrigger = resolveControlOwnerTrigger(
          interaction.triggers,
          ownerExtensionKey,
          ownerTriggerIndex
        );
        if (ownerTrigger && isStandardRoutedTrigger(ownerTrigger)) {
          ownerTrigger[1] = {...ownerTrigger[1], control};
        }

        // Fan out to grouped/role timelines: the runtime reads per-timeline
        // `settings.control` for any timeline with a `groupId` or role, so
        // the trigger control alone wouldn't reach them. Skip soft-deleted
        // timelines (mirrors isMultiTimelineInteraction / selectTimelineGroups)
        // so a removed group's stale settings aren't rewritten.
        for (const id of interaction.timelineIds) {
          const timeline = draft.timelines[id];
          if (!timeline || timeline.deleted) continue;
          if (timeline.groupId != null || timeline.triggerMetadata?.role) {
            timeline.settings = {...timeline.settings, control};
          }
        }
      });
    }
    case ix3ActionTypes.IX3_TIMELINE_TRIGGER_METADATA_PATCHED: {
      return produce(state, (draft) => {
        const {timelineId, patch} = action.payload;
        const timeline = draft.timelines[timelineId];
        if (!timeline) return;

        // triggerMetadata is optional on freshly-created timelines; initialize
        // an empty object so the first patch can land.
        const existing = timeline.triggerMetadata ?? {};
        const {pluginConfig: patchPluginConfig, ...topLevelPatch} = patch;
        timeline.triggerMetadata = {
          ...existing,
          ...topLevelPatch,
        };
        // Deep-merge pluginConfig so callers can update a single plugin
        // field without clobbering siblings.
        if (patchPluginConfig) {
          timeline.triggerMetadata.pluginConfig = {
            ...existing.pluginConfig,
            ...patchPluginConfig,
          };
        }
      });
    }

    // ============================================================================
    // Interactions State
    // ============================================================================
    case ix3ActionTypes.IX3_INTERACTION_CREATED: {
      return produce(state, (draft) => {
        const {
          id,
          name,
          triggers,
          scope,
          multiTimelineConfig,
          timelineGroupNames,
        } = action.payload;
        const currentPageId = selectCurrentPageId(action.state);

        /**
         * Auto-increment new interaction names, if there's a duplicate.
         */
        const existingInteractions = Object.values(selectInteractions(draft));

        const existingInteractionNames = existingInteractions.map(
          ({name: interactionName}) => interactionName
        );

        const nextName = increment(existingInteractionNames, name);

        /**
         * Handle multi-timeline triggers (e.g., Mouse Move with X/Y timelines,
         * Navbar/Dropdown with Open/Close timelines).
         *
         * Multi-timeline triggers create multiple timelines but only ONE trigger.
         * The trigger controls all timelines, and the UI shows accordion sections
         * for each timeline's actions.
         */
        if (multiTimelineConfig) {
          const {triggerExtensionKey, roles} = multiTimelineConfig;

          const baseTrigger = triggers[0];

          // Multi-timeline interactions require at least one trigger
          if (!baseTrigger) return;

          // Create timeline IDs and metadata for each role
          const multiTimelineSet = createMultiTimelineSet(
            triggerExtensionKey,
            roles
          );

          // Resolve timeline IDs by role name for quick lookup when wiring
          // `autoReusesRole` (e.g., Close reuses Open; future Hide reuses
          // Show, etc.). This generalizes to any pair a trigger declares.
          const timelineIdByRole = new Map<string, TimelineId>();
          for (let i = 0; i < multiTimelineSet.timelineIds.length; i++) {
            const mtId = multiTimelineSet.timelineIds[i];
            const meta = multiTimelineSet.metadata[i];
            if (mtId && meta) timelineIdByRole.set(meta.role, mtId);
          }

          // Any role with a valid `autoReusesRole` pointing at another role
          // in this set lands in a reusing state at creation, which means it
          // gets `defaultControlWhenReusing` instead of `defaultControl`.
          const reusingRoleToSourceId = new Map<string, TimelineId>();
          for (const roleDef of roles) {
            const sourceRole = roleDef.autoReusesRole;
            if (!sourceRole) continue;
            const sourceId = timelineIdByRole.get(sourceRole);
            if (sourceId) {
              reusingRoleToSourceId.set(roleDef.role, sourceId);
            } else if (process.env.NODE_ENV !== 'production') {
              // `autoReusesRole` is a stringly-typed pointer to another role
              // in the same set. A typo or dangling reference creates a role
              // without reuse wiring and without `defaultControlWhenReusing`
              // seeding, surfacing as a silent Control-stays-at-default bug.
              // Surface the mismatch loudly in development.
              console.warn(
                `[IX3] Role "${roleDef.role}" declares autoReusesRole: "${sourceRole}" ` +
                  `but no sibling role with that name exists in this trigger's roles.`
              );
            }
          }

          // Create timelines for each role, seeding any role-declared
          // `defaultControl` (or `defaultControlWhenReusing` when the
          // timeline starts in a reusing state) so new timelines land with
          // their expected playback control.
          for (let i = 0; i < multiTimelineSet.timelineIds.length; i++) {
            const timelineId = multiTimelineSet.timelineIds[i];
            const metadata = multiTimelineSet.metadata[i];
            const roleDef = roles[i];

            if (!timelineId || !metadata) continue;

            const sourceId = reusingRoleToSourceId.get(metadata.role);
            const willReuse = sourceId != null;
            const seededControl = willReuse
              ? (roleDef?.defaultControlWhenReusing ?? roleDef?.defaultControl)
              : roleDef?.defaultControl;

            draft.timelines[timelineId] = {
              id: timelineId,

              deleted: false,
              pageId: currentPageId,
              actions: [],
              triggerMetadata: {
                role: metadata.role,
              },
              ...(seededControl ? {settings: {control: seededControl}} : {}),
              ...(sourceId ? {reuse: {sourceTimelineId: sourceId}} : {}),
              // Position-scrubbed roles (mouse X/Y) author on a fixed 0–100%
              // percent canvas. The velocity-driven interval role opts out
              // (no `usePercentCanvas`).
              ...(roleDef?.usePercentCanvas
                ? {canvasDuration: PERCENT_CANVAS_DURATION_S}
                : {}),
            };
          }

          // For multi-timeline triggers, we keep ONE trigger that controls all timelines.
          // Timeline routing is handled via triggerMetadata.role at runtime.
          const [extKey, baseConfig, targetDef] = baseTrigger;
          // Preserve existing pluginConfig without modification
          const updatedTrigger: Trigger = targetDef
            ? [extKey, baseConfig, targetDef]
            : [extKey, baseConfig];

          draft.interactions[id] = {
            id,
            deleted: false,
            name: nextName,
            pageId: currentPageId,
            triggers: [updatedTrigger], // Single trigger
            timelineIds: multiTimelineSet.timelineIds,
            scope: scope ?? {
              type: 'pages',
              value: [currentPageId],
            },
          };
        } else {
          // Triggers whose group set is fixed (`timelineGroupConfig.fixedGroups`,
          // added to `DesignBuilder` in #122805,
          // e.g. Scroll direction) get every group up front, as `groupId`
          // groups — the same shape the user-managed add-group flow produces,
          // so renaming, per-group playback settings and the runtime's
          // positional routing all work on them unchanged (the Designer
          // withholds only add/remove). Everything else starts at zero
          // timelines and gets its first one with its first action
          // (IX3_TIMELINE_ACTION_CREATED).
          //
          // No `assignedGroupId` is stamped on the triggers: only STANDARD
          // triggers route by it, and a fixed-group trigger is SCROLL, which
          // drives every timeline (see isStandardRoutedTrigger).
          const seededTimelineIds: TimelineId[] = [];
          for (const groupName of timelineGroupNames ?? []) {
            const timelineId = generateTimelineId();
            draft.timelines[timelineId] = {
              id: timelineId,
              deleted: false,
              pageId: currentPageId,
              actions: [],
              name: groupName,
              groupId: generateGroupId(),
            };
            seededTimelineIds.push(timelineId);
          }

          // Standard single-timeline interaction
          draft.interactions[id] = {
            id,
            deleted: false,
            name: nextName,
            pageId: currentPageId,
            triggers,
            timelineIds: seededTimelineIds,
            scope: scope ?? {
              type: 'pages',
              value: [currentPageId],
            },
          };
        }

        /**
         * In addition to creating the new interaction, we also select it.
         * This will progress the interaction to the next step (editing).
         */
        draft.selectedInteractionId = id;
        draft.uiShowAddAction = true;
        draft.uiSelectedTimelineIndex = 0;
      });
    }

    case ix3ActionTypes.IX3_INTERACTION_UPSERTED: {
      const {interaction, timelines} = action.payload;
      const previous = state.interactions[interaction.id];
      const next = applyUpsertInteraction(state, interaction, timelines);
      if (isUpsertCreate(previous)) {
        // Create (including a resurrection — see `isUpsertCreate`) — auto-
        // select the new interaction (shows the canvas timeline bar, like
        // the UI) and clear any stale action selection from a prior one.
        // The panel handler reads the same predicate, so a withheld
        // selection also leaves the open left panel alone.
        if (!shouldSelectUpsertedInteraction(action.state, interaction)) {
          return next;
        }
        return {
          ...next,
          selectedInteractionId: interaction.id,
          selectedTimelineActionIds: [],
          uiShowAddAction: true,
          uiSelectedTimelineIndex: 0,
        };
      }
      // Only reconcile when the edited interaction is the selected one: its
      // timeline set may have shifted under the current selection. Clamp the
      // index, then reconcile actions against only the timeline now selected.
      if (state.selectedInteractionId !== interaction.id) return next;

      const reconciledIndex =
        state.uiSelectedTimelineIndex < interaction.timelineIds.length
          ? state.uiSelectedTimelineIndex
          : 0;
      const selectedTimelineId = interaction.timelineIds[reconciledIndex];
      const prevSelectedTimelineId =
        previous.timelineIds[state.uiSelectedTimelineIndex];

      // If the selected timeline's identity changed, clear the action selection
      // (matches IX3_UI_TIMELINE_INDEX_SELECTED); else keep only ids still on it.
      let reconciledActionIds = state.selectedTimelineActionIds;
      if (selectedTimelineId !== prevSelectedTimelineId) {
        reconciledActionIds = [];
      } else {
        const selectedTimeline = timelines.find(
          (timeline) => timeline.id === selectedTimelineId
        );
        const validActionIds = new Set(
          selectedTimeline?.actions.map(
            (timelineAction) => timelineAction.id
          ) ?? []
        );
        reconciledActionIds = state.selectedTimelineActionIds.filter((id) =>
          validActionIds.has(id)
        );
      }

      if (
        reconciledActionIds.length === state.selectedTimelineActionIds.length &&
        reconciledIndex === state.uiSelectedTimelineIndex
      ) {
        return next;
      }
      return {
        ...next,
        selectedTimelineActionIds: reconciledActionIds,
        uiSelectedTimelineIndex: reconciledIndex,
      };
    }

    case ix3ActionTypes.IX3_INTERACTION_RENAMED: {
      const {id, name} = action.payload;

      if (state.interactions[id] == null) return state;
      return produce(state, (draft) => {
        const interaction = draft.interactions[id];
        if (interaction) {
          interaction.name = name;
        }
      });
    }

    case ix3ActionTypes.IX3_INTERACTION_DELETED: {
      const {id} = action.payload;
      return applyDeleteInteraction(state, id);
    }

    case ROOT_SYMBOL_NODE_REMOVED: {
      const componentId = action.payload;
      if (!componentId) {
        return state;
      }

      return produce(state, (draft) => {
        const interactionIdsToDelete = new Set<string>();
        const candidateTimelineIds = new Set<string>();

        for (const interaction of Object.values(draft.interactions)) {
          if (
            interaction.deleted ||
            interaction.scope?.type !== 'component' ||
            interaction.scope.componentId !== componentId
          ) {
            continue;
          }

          interactionIdsToDelete.add(interaction.id);
          interaction.timelineIds.forEach((timelineId) => {
            if (timelineId) {
              candidateTimelineIds.add(timelineId);
            }
          });
        }

        cascadeDeleteInteractions(
          state,
          draft,
          interactionIdsToDelete,
          candidateTimelineIds
        );
      });
    }

    case COMPONENT_ROOT_STYLE_VARIANT_DELETED: {
      const {variantId} = action.payload;
      if (variantId === 'base') return state;

      return produce(state, (draft) => {
        const {toDelete, toUpdate} = getInteractionsAffectedByVariantDeletion(
          draft.interactions,
          variantId
        );

        for (const interaction of toUpdate) {
          const draftInteraction = draft.interactions[interaction.id];
          if (draftInteraction?.scope?.type === 'component') {
            draftInteraction.scope.variants =
              draftInteraction.scope.variants?.filter((v) => v !== variantId);
          }
        }

        const interactionIdsToDelete = new Set<string>();
        const candidateTimelineIds = new Set<string>();
        for (const interaction of toDelete) {
          interactionIdsToDelete.add(interaction.id);
          interaction.timelineIds.forEach((timelineId: string) => {
            if (timelineId) candidateTimelineIds.add(timelineId);
          });
        }

        cascadeDeleteInteractions(
          state,
          draft,
          interactionIdsToDelete,
          candidateTimelineIds
        );
      });
    }

    case ix3ActionTypes.IX3_INTERACTION_DUPLICATED: {
      const {id, newId, newName} = action.payload;

      return produce(state, (draft) => {
        const originalInteraction = draft.interactions[id];
        if (originalInteraction == null) return;

        /**
         * Auto-increment new interaction names, if there's a duplicate.
         */
        const existingInteractions = Object.values(selectInteractions(draft));
        const existingInteractionNames = existingInteractions.map(
          ({name: interactionName}) => interactionName
        );
        const finalName = increment(existingInteractionNames, newName);

        // Generate new timeline IDs for the duplicated interaction
        const newTimelineIds = originalInteraction.timelineIds.map(() =>
          generateTimelineId()
        );
        const actionIdMap: Record<string, string> = {};

        // Map old timeline IDs to their duplicates so `reuse.sourceTimelineId`
        // (e.g. Navbar/Dropdown's Close reusing Open) can be remapped to the
        // corresponding duplicate rather than pointing back at the original.
        const timelineIdMap = new Map<TimelineId, TimelineId>();
        originalInteraction.timelineIds.forEach((originalTimelineId, index) => {
          const newTimelineId = newTimelineIds[index];
          if (originalTimelineId && newTimelineId) {
            timelineIdMap.set(originalTimelineId, newTimelineId);
          }
        });

        // Create the duplicated interaction
        draft.interactions[newId] = {
          ...originalInteraction,
          id: newId,
          name: finalName,
          timelineIds: newTimelineIds,
        };

        // Duplicate associated timelines with new IDs
        const duplicatedTimelines: Timeline[] = [];
        originalInteraction.timelineIds.forEach((originalTimelineId, index) => {
          const originalTimeline = draft.timelines[originalTimelineId];
          if (originalTimeline) {
            const newTimelineId = newTimelineIds[index];
            if (newTimelineId == null) return;
            const remappedSourceId = originalTimeline.reuse
              ? (timelineIdMap.get(originalTimeline.reuse.sourceTimelineId) ??
                originalTimeline.reuse.sourceTimelineId)
              : undefined;
            duplicatedTimelines.push({
              ...originalTimeline,
              id: newTimelineId,
              actions: originalTimeline.actions.map((a) => {
                const nextActionId = generateTimelineActionId();
                actionIdMap[a.id] = nextActionId;
                return {
                  ...a,
                  id: nextActionId,
                };
              }),
              ...(remappedSourceId
                ? {reuse: {sourceTimelineId: remappedSourceId}}
                : {}),
            });
          }
        });
        for (const timeline of applyOnTimelinesDuplicatedHooks({
          timelines: duplicatedTimelines,
          actionIdMap,
        })) {
          draft.timelines[timeline.id] = timeline;
        }

        // Auto-select the duplicated interaction
        draft.selectedInteractionId = newId;
        draft.selectedTimelineActionIds = [];
        draft.uiShowAddAction = newTimelineIds.length === 0;
      });
    }

    case ix3ActionTypes.IX3_INTERACTION_PAGE_SCOPE_UPDATED: {
      const {interactionId, pageScope} = action.payload;
      return produce(state, (draft) => {
        const interaction = draft.interactions[interactionId];
        if (interaction == null) return;
        interaction.scope = pageScope;
      });
    }

    case ix3ActionTypes.IX3_INTERACTION_VARIANT_SCOPE_UPDATED: {
      const {interactionId, variants} = action.payload;
      return produce(state, (draft) => {
        const interaction = draft.interactions[interactionId];
        if (interaction?.scope?.type !== 'component') return;
        if (variants === undefined) {
          // eslint-disable-next-line webflow/no-delete
          delete interaction.scope.variants;
        } else {
          interaction.scope.variants = variants;
        }
      });
    }

    case ix3ActionTypes.IX3_INTERACTION_TIMELINE_DEFAULTS_UPDATED: {
      const {interactionId, timelineDefaults} = action.payload;

      return produce(state, (draft) => {
        const interaction = draft.interactions[interactionId];
        if (interaction == null) return;

        interaction.timelineDefaults = timelineDefaults ?? undefined;
      });
    }

    case ix3ActionTypes.IX3_INTERACTION_CONDITIONAL_PLAYBACK_UPDATED: {
      const {interactionId, conditionalPlayback} = action.payload;

      return produce(state, (draft) => {
        const interaction = draft.interactions[interactionId];
        if (interaction == null) return;

        interaction.conditionalPlayback =
          sortConditionalPlayback(conditionalPlayback) ?? null;
      });
    }

    case ix3ActionTypes.IX3_EDIT_INTERACTION_SELECTED: {
      const {id} = action.payload;

      if (state.selectedInteractionId === id) {
        return state;
      }

      // Action ids and the timeline index belong to the interaction being
      // left. Kept, the action editor stays open while the selector falls
      // back to the new timeline's first action.
      return {
        ...state,
        selectedInteractionId: id,
        selectedTimelineActionIds: [],
        uiSelectedTimelineIndex: 0,
      };
    }

    case ix3ActionTypes.IX3_TIMELINE_ACTION_CREATED: {
      const {
        id: actionId,
        timelineId,
        presetId,
        pluginPresetData,
        position,
        index,
        target,
        effectiveCanvasDuration,
      } = action.payload;

      return produce(state, (draft) => {
        const {selectedInteractionId} = draft;
        if (selectedInteractionId == null) return;
        const selectedInteraction = draft.interactions[selectedInteractionId];
        if (selectedInteraction == null) return;
        const currentPageId = selectCurrentPageId(action.state);

        // If the timeline doesn't exist, create it (single-timeline)
        if (!selectedInteraction.timelineIds.includes(timelineId)) {
          // Percent-canvas: a scroll-scrub timeline gets a fixed 0–100% canvas
          // (see PERCENT_CANVAS_DURATION_S). Detect scrub the same way the
          // linear-ease logic below does.
          const hasScrollScrub = selectedInteraction.triggers.some(
            ([, triggerConfig]) =>
              triggerConfig?.controlType === TimelineControlType.SCROLL &&
              triggerConfig?.scrollTriggerConfig?.scrub != null
          );
          selectedInteraction.timelineIds.push(timelineId);
          draft.timelines[timelineId] = {
            id: timelineId,

            deleted: false,
            pageId: currentPageId,
            actions: [],
            ...(hasScrollScrub
              ? {canvasDuration: PERCENT_CANVAS_DURATION_S}
              : {}),
          };
        }

        const timeline = draft.timelines[timelineId];
        if (timeline == null) return;

        // Check plugin preset first, then saved presets, then built-in
        const savedPreset = presetId
          ? pluginPresetData ||
            draft.actionPresets[presetId] ||
            BUILT_IN_PRESETS[presetId] ||
            BUILT_IN_DEFAULTS[presetId]
          : undefined;

        // Use preset data if available, otherwise default to an empty action
        const baseName = savedPreset
          ? savedPreset.action.name
          : 'Custom animation';
        const existingActionNames = timeline.actions.map(
          ({name: existingActionName}) => existingActionName
        );
        const nextName = increment(existingActionNames, baseName);
        const nextColor = getColorOfNextTimelineAction(timeline.actions);
        let nextProperties: TimelineAction['properties'] =
          savedPreset?.action.properties ||
          (IX3_ACTION_ANIMATION_PROPS as TimelineAction['properties']);

        nextProperties = applyOnTimelineActionCreatedHooks({
          properties: nextProperties,
          timelineRole: timeline.triggerMetadata?.role,
        });

        // Targets are always provided by UI layer with proper format
        const nextTargets = target;

        // Continuous actions (e.g. Follow mouse) have no meaningful timeline
        // duration, so they must not push the next action's start time forward.
        const calculatedPosition = getNextPositionForTimelineAction(
          timeline.actions.filter(
            (existingAction) => !isContinuousTimelineAction(existingAction)
          )
        );
        // Clone the preset's timing instead of aliasing it: the position
        // assignment below mutates `nextTiming`, and `savedPreset.action.timing`
        // is a shared object (a built-in preset constant, a stored
        // `actionPresets` entry, or the `pluginPresetData.action` from the
        // registry reused across every create). Mutating it in place leaked the
        // last auto-calculated position back into the preset, so the next new
        // interaction's first action inherited it (e.g. showing 0.5 instead of
        // an unset 0) even though its timeline was empty.
        const nextTiming: TimelineAction['timing'] = savedPreset?.action.timing
          ? {...savedPreset.action.timing}
          : {
              duration: IX3_ACTION_DURATION,
            };
        // Write position when explicitly provided in payload (user/UI chose
        // a specific position), or when auto-calculated and non-zero (system
        // sequencing after existing actions). Omitting zero keeps the label
        // in the "unset" state for first actions — and explicitly clear any
        // position copied from the preset when this is the first action on
        // the timeline, so a polluted preset (from older builds) can't leak
        // a non-zero position into a fresh group.
        if (position !== undefined) {
          nextTiming.position = position;
        } else if (calculatedPosition !== 0) {
          nextTiming.position = calculatedPosition;
        } else {
          // eslint-disable-next-line webflow/no-delete
          delete nextTiming.position;
        }

        // Percent canvas: a newly created action fills the whole gesture
        // (0–100%) by default, and can never spill past the Scroll/Move end
        // marker. Gated on the canvasDuration marker, which only percent-canvas
        // timelines carry.
        if (timeline.canvasDuration != null) {
          // Prefer the UI's content-aware canvas (includes stagger/split-text
          // extent). The `calculatedPosition` fallback only sums base durations,
          // so it underestimates the canvas and would slide a new action back
          // even when it fits within the staggered content.
          const effectiveCanvas =
            effectiveCanvasDuration ??
            Math.max(timeline.canvasDuration, calculatedPosition);
          // Fill the gesture: a non-Set action added through the default flow
          // (no explicit drop position) spans the whole 0–100% canvas from the
          // start. The repeat/stagger clamp below trims the base back down so a
          // repeated/staggered footprint still ends at 100% (e.g. a repeat: 2
          // preset lands at ~canvas/3 across three cycles). Set actions stay
          // instantaneous, so they're excluded and keep their authored timing.
          // A double-click that carries an explicit `position` keeps that
          // placement and is only clamped to fit, below.
          if (
            position === undefined &&
            savedPreset?.action.tt !== TweenType.Set
          ) {
            nextTiming.position = 0;
            nextTiming.duration = effectiveCanvas;
          }
          // The first-action label logic above may delete a position the preset
          // authored (to keep a fresh timeline's Start "unset"). On a percent
          // canvas the Start must still resolve to a concrete in-range value, so
          // clamp against the preset's authored position when the label logic
          // cleared it — otherwise an out-of-range authored Start (e.g. a Set
          // preset at 2.0 on a 1s canvas) reads as unsupported and collapses to
          // 0 instead of clamping to the 100% end marker.
          const clampSourcePosition =
            nextTiming.position !== undefined
              ? nextTiming.position
              : savedPreset?.action.timing?.position;
          const rawPosition =
            typeof clampSourcePosition === 'string' &&
            clampSourcePosition.endsWith('ms')
              ? parseFloat(clampSourcePosition) / 1000
              : Number(clampSourcePosition);
          // The schema allows relative/alignment starts (e.g. '+=0.8', '<') that
          // parse to NaN here. They aren't representable on a percent canvas, and
          // GSAP would resolve them at build time — a '+=0.8' start with a 0.5s
          // duration ends at 130% of a 1s canvas. Treat them as start 0 and
          // always persist a concrete numeric start so the string can't overflow.
          const positionUnsupported = !Number.isFinite(rawPosition);
          const newStart = positionUnsupported ? 0 : rawPosition;
          // Match the block/runtime fallback: a non-Set action with no explicit
          // duration renders at DEFAULTS.DURATION, so clamp against that. Using
          // `Number(duration) || 0` here let a `timing: {}` preset (undefined
          // duration) skip the clamp and overflow the 100% marker.
          const rawDuration =
            typeof nextTiming.duration === 'string' &&
            nextTiming.duration.endsWith('ms')
              ? parseFloat(nextTiming.duration) / 1000
              : Number(nextTiming.duration);
          const newExtent =
            savedPreset?.action.tt === TweenType.Set
              ? 0
              : Number.isFinite(rawDuration)
                ? rawDuration
                : DEFAULTS.DURATION;
          // Stagger spread we can bound without resolving targets here. A total
          // ('amount') spread is fixed regardless of the resolved target count;
          // a per-item ('each') offset scales with that count, which isn't known
          // at create time — so an 'each' stagger is treated as unbounded and the
          // start is pinned to 0 below. 'amount' (and a stagger with neither) is
          // folded into the footprint so the full bar fits within 100%.
          const staggerEachUnbounded = nextTiming.stagger?.each != null;
          const staggerSpreadSeconds =
            !staggerEachUnbounded && nextTiming.stagger?.amount != null
              ? typeof nextTiming.stagger.amount === 'string' &&
                nextTiming.stagger.amount.endsWith('ms')
                ? parseFloat(nextTiming.stagger.amount) / 1000
                : Number(nextTiming.stagger.amount) || 0
              : 0;
          // The action's full footprint (repeats/stagger folded in). A
          // zero-duration Set action is instantaneous, so its base footprint is
          // 0 — but GSAP still applies `timing.repeat`/`repeatDelay` to a
          // `tl.set` (a `repeat: 2` + `repeatDelay: 0.6` set spans a 1.2s tail),
          // so a Set carrying a repeat — or only a bounded total stagger — enters
          // the folding branch below too, where its footprint (and, if needed,
          // its stagger spread) is capped to fit the canvas.
          let fullExtent = 0;
          // Fold a finite repeat tail into the extent before clamping: GSAP
          // plays `repeat + 1` cycles with `repeatDelay` between each, so a
          // preset with `timing.repeat`/`repeatDelay` occupies far more than
          // its base duration. Clamping against the base alone would let, e.g.
          // a 30% action with `repeat: 2` sit at 50% (30% fits) while its
          // repeated footprint runs past 100% and grows the canvas. An
          // infinite repeat (`-1`) never fits, so pin the start to 0.
          const repeats = nextTiming.repeat ?? 0;
          if (newExtent > 0 || repeats !== 0 || staggerSpreadSeconds > 0) {
            let repeatDelaySeconds =
              typeof nextTiming.repeatDelay === 'string' &&
              nextTiming.repeatDelay.endsWith('ms')
                ? parseFloat(nextTiming.repeatDelay) / 1000
                : Number(nextTiming.repeatDelay) || 0;
            // If the repeat-delay budget alone overruns the canvas, capping the
            // base to 0 (below) still leaves a delay tail that spills past 100%
            // (e.g. repeat 2 + repeatDelay 0.6 on a 1s canvas → a 1.2s
            // footprint). Clamp the delay — rounded down to the persisted
            // precision — so the repeated footprint fits even at start 0.
            if (repeats > 0 && repeatDelaySeconds * repeats > effectiveCanvas) {
              repeatDelaySeconds =
                Math.floor((effectiveCanvas / repeats) * 100) / 100;
              nextTiming.repeatDelay = repeatDelaySeconds;
            }
            // The largest single cycle (base + stagger spread) whose full
            // repeated footprint still fits the canvas at start 0. An infinite
            // repeat (-1) pins the start to 0 (its tail never ends), but its
            // FIRST cycle still renders, so it gets the whole canvas as its cycle
            // budget; a finite repeat divides the canvas (minus the delay tail)
            // across `repeat + 1` cycles.
            const maxSingleCycle =
              repeats < 0
                ? effectiveCanvas
                : (effectiveCanvas - repeatDelaySeconds * repeats) /
                  (repeats + 1);
            // Cap the stored base so a single cycle (base + stagger spread) fits
            // `maxSingleCycle`. Sliding Start back (below) can't rescue a base
            // that already overflows — e.g. a 1.5s preset on a 1s canvas, or a
            // repeat tail that runs past the gesture end. Mirrors the percent
            // Duration editor's `couplePercentDurationToCanvas` materialize-on-cap.
            const maxBase = Math.max(0, maxSingleCycle - staggerSpreadSeconds);
            // Round the cap DOWN to the persisted 2-decimal precision so the
            // saved footprint never exceeds effectiveCanvas. `toFixed(2)` rounds
            // half-up, which can round the cap up (e.g. maxBase 2/3 → 0.67,
            // whose repeat-2 footprint 0.67 * 3 = 2.01 overflows a 2s canvas)
            // and reintroduce the overflow this cap is meant to prevent.
            const cappedExtent =
              newExtent > maxBase ? Math.floor(maxBase * 100) / 100 : newExtent;
            if (cappedExtent < newExtent) {
              nextTiming.duration = cappedExtent;
            }
            // Cap the bounded stagger spread to whatever the single-cycle budget
            // leaves after the (capped) base, so base + spread fits a cycle even
            // when the base is already 0 — a Set. Without this, a repeated or
            // staggered Set whose spread alone exceeds the per-cycle budget (e.g.
            // repeat 2 + stagger amount 0.4 on a 1s canvas → 0.4 * 3 = 1.2)
            // persists a footprint past 100% that Start-slide can't rescue.
            // Materialize the reduced amount so the saved bar matches the clamp.
            const maxSpread = Math.max(
              0,
              Math.floor((maxSingleCycle - cappedExtent) * 100) / 100
            );
            const cappedSpread = Math.min(staggerSpreadSeconds, maxSpread);
            if (
              cappedSpread < staggerSpreadSeconds &&
              nextTiming.stagger?.amount != null
            ) {
              nextTiming.stagger = {
                ...nextTiming.stagger,
                amount: cappedSpread,
              };
            }
            // Fold the (capped) stagger spread into the single cycle so the
            // repeated footprint reflects the full bar, not just the base. An
            // infinite repeat reports the whole canvas to pin the start to 0.
            fullExtent =
              repeats < 0
                ? effectiveCanvas
                : (cappedExtent + cappedSpread) * (repeats + 1) +
                  repeatDelaySeconds * repeats;
          }
          // Clamp/normalize the start for ALL percent-canvas actions — including
          // zero-duration Set presets, which otherwise bypass the guard above and
          // let an out-of-range or relative ('+=1.2') start resolve its `tl.set`
          // past the 100% marker and grow/relabel the canvas. An 'each' stagger's
          // spread can't be bounded without the target count, so pin it to 0.
          const maxStart = staggerEachUnbounded
            ? 0
            : Math.max(0, effectiveCanvas - fullExtent);
          if (newStart > maxStart || positionUnsupported) {
            // Floor to the persisted 2dp precision (not half-up `toFixed`):
            // `maxStart` is the safe ceiling, so rounding it up (e.g. 0.665 →
            // 0.67) would store an end past the 100% marker and grow the canvas.
            // The `1e-9` epsilon absorbs binary float noise so a clean 2dp bound
            // isn't dropped a whole cent (mirrors `floorToHundredth` in the
            // Designer; inlined here to avoid a designer-flux import).
            nextTiming.position =
              Math.floor(Math.min(newStart, maxStart) * 100 + 1e-9) / 100;
          }
        }

        // If interaction has scroll triggers with scrub OR continuous triggers (mouse move),
        // set ease to 0 (linear) for better UX
        const hasScrollTriggerWithScrub = selectedInteraction.triggers.some(
          ([, triggerConfig]) =>
            triggerConfig?.controlType === TimelineControlType.SCROLL &&
            triggerConfig?.scrollTriggerConfig?.scrub != null
        );

        const hasContinuousTrigger = selectedInteraction.triggers.some(
          ([, triggerConfig]) =>
            triggerConfig?.controlType === TimelineControlType.CONTINUOUS
        );

        const shouldUseLinearEase =
          hasScrollTriggerWithScrub || hasContinuousTrigger;

        const nextAction: TimelineAction = {
          id: actionId,
          name: nextName,
          color: nextColor,
          properties: nextProperties,
          targets: nextTargets,
          timing: {
            ...nextTiming,
            // Only force linear ease for scroll/continuous triggers.
            // Otherwise, let the preset's ease flow through via nextTiming
            // spread (or remain undefined for default presets — runtime
            // defaults to power1.out).
            ...(shouldUseLinearEase ? {ease: 0 as const} : {}),
          },
          tt: savedPreset?.action.tt,
          splitText: savedPreset?.action.splitText,
          ...(presetId && {presetId}),
          pluginConfig: savedPreset?.action.pluginConfig,
        };

        if (!index) {
          timeline.actions.push(nextAction);
        } else {
          timeline.actions.splice(index, 0, nextAction);
        }

        // Run post-create draft hooks so plugins (e.g. Mouse Follow auto-pair)
        // can atomically push companion actions onto sibling timelines inside
        // this same `produce` block.
        applyOnTimelineActionCreatedDraftHooks({
          draft,
          createdAction: nextAction,
          timelineId,
        });

        draft.selectedTimelineActionIds = [actionId];

        draft.uiShowAddAction = false;
      });
    }

    case ix3ActionTypes.IX3_TIMELINE_ACTION_EDITED: {
      const {
        timelineId,
        timelineAction: nextTimelineAction,
        canvasDurationAnchor,
      } = action.payload;

      return produce(state, (draft) => {
        const timeline = draft.timelines[timelineId];
        if (timeline == null) return;

        const timelineActionIndex = timeline.actions.findIndex(
          (actionItem) => actionItem.id === nextTimelineAction.id
        );
        if (timelineActionIndex === -1) return;

        pinCanvasDuration(timeline, canvasDurationAnchor);
        timeline.actions[timelineActionIndex] = nextTimelineAction;
        applyOnTimelineActionEditedDraftHooks({
          draft,
          editedAction: nextTimelineAction,
          timelineId,
        });
      });
    }

    case ix3ActionTypes.IX3_TIMELINE_ACTION_HIDDEN_TOGGLED: {
      const {timelineId, actionId} = action.payload;

      return produce(state, (draft) => {
        const timeline = draft.timelines[timelineId];
        if (timeline == null) return;

        const timelineAction = timeline.actions.find(
          (actionItem) => actionItem.id === actionId
        );
        if (timelineAction == null) return;

        timelineAction.hidden = !timelineAction.hidden;
      });
    }

    case ix3ActionTypes.IX3_TIMELINE_ACTION_PLUGIN_CONFIG_PATCHED: {
      const {timelineId, actionId, pluginConfig} = action.payload;

      return produce(state, (draft) => {
        const timeline = draft.timelines[timelineId];
        if (timeline == null) return;

        const timelineAction = timeline.actions.find(
          (actionItem) => actionItem.id === actionId
        );
        if (timelineAction == null) return;

        timelineAction.pluginConfig = {
          ...timelineAction.pluginConfig,
          ...pluginConfig,
        };

        // Fire edit hooks for parity with IX3_TIMELINE_ACTION_EDITED and
        // IX3_TIMELINE_ACTIONS_BULK_EDITED. Without this, a plugin patching
        // its own slice (e.g. Mouse Follow toggling velocityInfluence on
        // one axis) can't notify a paired sibling action — and paired
        // X/Y actions silently drift.
        applyOnTimelineActionEditedDraftHooks({
          draft,
          editedAction: timelineAction,
          timelineId,
        });
      });
    }

    case ix3ActionTypes.IX3_TIMELINE_ACTIONS_BULK_EDITED: {
      const {timelineId, timelineActions, canvasDurationAnchor} =
        action.payload;

      return produce(state, (draft) => {
        const timeline = draft.timelines[timelineId];

        if (timeline == null) return;

        pinCanvasDuration(timeline, canvasDurationAnchor);

        const updatesById = new Map(
          timelineActions.map((timelineAction) => [
            timelineAction.id,
            timelineAction,
          ])
        );

        // Update all actions in the batch
        timeline.actions = timeline.actions.map(
          (timelineAction) =>
            updatesById.get(timelineAction.id) ?? timelineAction
        );
        for (const timelineAction of timelineActions) {
          applyOnTimelineActionEditedDraftHooks({
            draft,
            editedAction: timelineAction,
            timelineId,
          });
        }
      });
    }

    case ix3ActionTypes.IX3_TIMELINE_ACTION_SELECTED: {
      const {id} = action.payload;

      return produce(state, (draft) => {
        draft.selectedTimelineActionIds = [id];
      });
    }

    case ix3ActionTypes.IX3_TIMELINE_ACTIONS_MULTI_SELECTED: {
      const {ids, replace = true} = action.payload;

      return produce(state, (draft) => {
        if (replace) {
          draft.selectedTimelineActionIds = ids;
        } else {
          // Add to existing selection
          draft.selectedTimelineActionIds = Array.from(
            new Set(draft.selectedTimelineActionIds.concat(ids))
          );
        }
      });
    }

    case ix3ActionTypes.IX3_TIMELINE_ACTION_SELECTION_CLEARED: {
      if (state.selectedTimelineActionIds.length === 0) {
        return state;
      }

      return produce(state, (draft) => {
        draft.selectedTimelineActionIds = [];
      });
    }

    case ix3ActionTypes.IX3_TIMELINE_ACTION_RENAMED: {
      const {timelineId, id, name} = action.payload;

      return produce(state, (draft) => {
        const timeline = draft.timelines[timelineId];
        if (timeline == null) return;

        const timelineAction = timeline.actions.find(
          (actionItem) => actionItem.id === id
        );
        if (timelineAction == null) return;

        timelineAction.name = name;
      });
    }

    case ix3ActionTypes.IX3_TIMELINE_ACTION_DUPLICATED: {
      const {timelineId, id, newId, newName} = action.payload;

      return produce(state, (draft) => {
        const timeline = draft.timelines[timelineId];
        if (timeline == null) return;

        // Find the action to duplicate
        const actionIndex = timeline.actions.findIndex(
          (timelineAction) => timelineAction.id === id
        );
        if (actionIndex === -1) return;

        // Get the original action
        const originalAction = timeline.actions[actionIndex];
        if (!originalAction) return;

        /**
         * Auto-increment new interaction names, if there's a duplicate.
         */
        const existingActionNames = timeline.actions.map(
          ({name: actionName}) => actionName
        );

        const nextNameBase = newName ?? `${originalAction.name} Copy`;
        const nextName = increment(existingActionNames, nextNameBase);

        // Create a duplicate with a new ID (using the original ID as a base)
        const duplicateAction = applyOnTimelineActionDuplicatedHooks({
          action: {
            ...omit(originalAction, ['hidden']),
            id: newId,
            name: nextName,
          },
          timelineRole: timeline.triggerMetadata?.role,
        });

        // Insert the duplicate right after the original
        timeline.actions.splice(actionIndex + 1, 0, duplicateAction);

        // Auto-select the duplicated action
        draft.selectedTimelineActionIds = [newId];
      });
    }

    case ix3ActionTypes.IX3_TIMELINE_ACTIONS_BULK_DUPLICATED: {
      const {timelineId, duplications} = action.payload;

      return produce(state, (draft) => {
        const timeline = draft.timelines[timelineId];
        if (timeline == null) return;

        // Get existing action names for auto-increment
        const existingActionNames = timeline.actions.map(
          ({name: actionName}) => actionName
        );

        // Create all duplicates first to avoid index shifting issues
        const duplicatesToInsert: Array<{
          duplicateAction: TimelineAction;
          insertIndex: number;
        }> = [];

        duplications.forEach(({originalId, newId, newName}) => {
          // Find the original action
          const actionIndex = timeline.actions.findIndex(
            (timelineAction) => timelineAction.id === originalId
          );
          if (actionIndex === -1) return;

          const originalAction = timeline.actions[actionIndex];
          if (!originalAction) return;

          // Generate unique name
          const nextNameBase = newName ?? `${originalAction.name} Copy`;
          const nextName = increment(existingActionNames, nextNameBase);
          existingActionNames.push(nextName); // Add to list to avoid duplicates

          // Create duplicate action
          const duplicateAction = applyOnTimelineActionDuplicatedHooks({
            action: {
              ...omit(originalAction, ['hidden']),
              id: newId,
              name: nextName,
            },
            timelineRole: timeline.triggerMetadata?.role,
          });

          duplicatesToInsert.push({
            duplicateAction,
            insertIndex: actionIndex + 1,
          });
        });

        // Sort by insert index in descending order to avoid index shifting
        duplicatesToInsert.sort((a, b) => b.insertIndex - a.insertIndex);

        // Insert all duplicates
        duplicatesToInsert.forEach(({duplicateAction, insertIndex}) => {
          timeline.actions.splice(insertIndex, 0, duplicateAction);
        });

        // Auto-select the duplicated actions
        draft.selectedTimelineActionIds = duplicatesToInsert.map(
          (d) => d.duplicateAction.id
        );
      });
    }

    case ix3ActionTypes.IX3_TIMELINE_ACTION_DELETED: {
      const {timelineId, id} = action.payload;

      return produce(state, (draft) => {
        // Check if timeline exists
        const timeline = draft.timelines[timelineId];
        if (timeline == null) return;

        const actionIndex = timeline.actions.findIndex(
          (timelineAction) => timelineAction.id === id
        );
        if (actionIndex === -1) return;
        const deletedAction = timeline.actions[actionIndex];
        timeline.actions.splice(actionIndex, 1);

        applyOnTimelineActionDeletedDraftHooks({
          draft,
          deletedActionIds: new Set([id]),
          deletedActions: deletedAction
            ? new Map([[id, deletedAction]])
            : undefined,
        });

        draft.selectedTimelineActionIds = [];
      });
    }

    case ix3ActionTypes.IX3_TIMELINE_ACTIONS_BULK_DELETED: {
      const {timelineId, ids} = action.payload;

      return produce(state, (draft) => {
        // Check if timeline exists
        const timeline = draft.timelines[timelineId];
        if (timeline == null) return;

        // Create a set for faster lookup
        const idsToDelete = new Set(ids);
        const deletedActions = new Map(
          timeline.actions
            .filter((timelineAction) => idsToDelete.has(timelineAction.id))
            .map((timelineAction) => [timelineAction.id, timelineAction])
        );

        // Filter out the actions to delete
        timeline.actions = timeline.actions.filter(
          (timelineAction) => !idsToDelete.has(timelineAction.id)
        );

        applyOnTimelineActionDeletedDraftHooks({
          draft,
          deletedActionIds: idsToDelete,
          deletedActions,
        });

        // Clear selection if any of the deleted actions were selected
        if (
          draft.selectedTimelineActionIds.length > 0 &&
          draft.selectedTimelineActionIds.some((id) => idsToDelete.has(id))
        ) {
          draft.selectedTimelineActionIds = [];
        }

        // Clear multi-selection of deleted actions
        draft.selectedTimelineActionIds =
          draft.selectedTimelineActionIds.filter((id) => !idsToDelete.has(id));
      });
    }

    case ix3ActionTypes.IX3_TIMELINE_ACTION_SORTED_BY_START_TIME: {
      const {timelineId} = action.payload;

      return produce(state, (draft) => {
        const timeline = draft.timelines[timelineId];
        if (timeline == null) return;
        timeline.actions = sortActionsByStartTime(timeline.actions);
      });
    }

    case ix3ActionTypes.IX3_TIMELINE_ACTION_REORDERED: {
      const {timelineId, ids, newIndex} = action.payload;

      return produce(state, (draft) => {
        const timeline = draft.timelines[timelineId];
        if (timeline == null) return;

        timeline.actions = sortActionsByIndex(timeline.actions, ids, newIndex);
      });
    }

    case ix3ActionTypes.IX3_TIMELINE_EDITED: {
      const {id, previewSpeed, playInReverse, immediate} = action.payload;

      return produce(state, (draft) => {
        const timeline = draft.timelines[id];
        if (timeline == null) return;

        timeline.previewSpeed = previewSpeed ?? timeline.previewSpeed;
        timeline.playInReverse = playInReverse ?? timeline.playInReverse;
        timeline.immediate = immediate ?? timeline.immediate;
      });
    }

    case ix3ActionTypes.IX3_TIMELINE_SETTINGS_UPDATED: {
      const {timelineId, settings} = action.payload;

      return produce(state, (draft) => {
        const timeline = draft.timelines[timelineId];
        if (timeline == null) return;

        timeline.settings = settings ?? undefined;

        // A percent-canvas timeline must never carry timeline-level
        // delay/repeat/repeatDelay/stagger: the runtime applies them to every
        // tween via `convertToGsapDefaults`, but the percent clamps/ruler only
        // budget per-action timing, so a default delay/repeat would push a
        // sub-100% action past the end marker and a default stagger would spread
        // a multi-target action past it — rescaling the scrubbed/published
        // timeline. Seeding clears them once; strip them here too so the
        // playback-settings gear (`IX3_TIMELINE_SETTINGS_UPDATED`) can't
        // reintroduce them after Scrub is toggled on.
        if (timeline.canvasDuration != null) {
          clearTimelinePlaybackDefaults(timeline);
        }
      });
    }

    // ============================================================================
    // Target State
    // ============================================================================
    case ix3ActionTypes.IX3_TIMELINE_TARGET_ADDED: {
      const {timelineId, actionId, target} = action.payload;

      return produce(state, (draft) => {
        const timeline = draft.timelines[timelineId];
        if (timeline == null) return;

        const timelineAction = timeline.actions.find(
          (actionItem) => actionItem.id === actionId
        );

        if (timelineAction == null) return;

        timelineAction.targets.push(target);

        // Fire edit hooks so plugins that mirror `targets` (e.g. paired
        // Mouse Follow X/Y actions) keep their sibling in sync. Without
        // this, target add/delete bypasses the same pairing path used by
        // IX3_TIMELINE_ACTION_EDITED and the two halves drift.
        applyOnTimelineActionEditedDraftHooks({
          draft,
          editedAction: timelineAction,
          timelineId,
        });
      });
    }

    case ix3ActionTypes.IX3_TIMELINE_TARGET_DELETED: {
      const {timelineId, actionId, targetIndex} = action.payload;

      return produce(state, (draft) => {
        const timeline = draft.timelines[timelineId];
        if (timeline == null) return;

        const timelineAction = timeline.actions.find(
          (actionItem) => actionItem.id === actionId
        );

        if (timelineAction == null) return;

        timelineAction.targets.splice(targetIndex, 1);

        applyOnTimelineActionEditedDraftHooks({
          draft,
          editedAction: timelineAction,
          timelineId,
        });
      });
    }

    /**
     * If the target type is changed, we need to update the target definition
     * with the extension key from the registry
     */
    case ix3ActionTypes.IX3_TIMELINE_TARGET_TYPE_CHANGED: {
      const {timelineId, actionId, targetIndex, extensionKey} = action.payload;

      return produce(state, (draft) => {
        const timeline = draft.timelines[timelineId];
        if (timeline == null) return;

        const timelineAction = timeline.actions.find(
          (actionItem) => actionItem.id === actionId
        );

        if (timelineAction == null) return;
        const target = timelineAction.targets[targetIndex];
        if (target == null) return;

        // Use the extension key directly from the payload
        timelineAction.targets[targetIndex] = [extensionKey, ''];

        applyOnTimelineActionEditedDraftHooks({
          draft,
          editedAction: timelineAction,
          timelineId,
        });
      });
    }

    // ============================================================================
    // UI State
    // ============================================================================

    case ix3ActionTypes.IX3_UI_TOGGLE_CANVAS_TARGET_OUTLINES: {
      const {payload: {showOutlines} = {}} = action;

      const nextShowOutlines =
        showOutlines !== undefined
          ? showOutlines
          : !state.uiShowCanvasTargetOutlines;

      if (state.uiShowCanvasTargetOutlines === nextShowOutlines) {
        return state;
      }

      return {
        ...state,
        uiShowCanvasTargetOutlines: nextShowOutlines,
      };
    }

    case ix3ActionTypes.IX3_UI_TOGGLE_SCROLL_PREVIEW_MODE: {
      return {
        ...state,
        uiScrollPreviewMode: !state.uiScrollPreviewMode,
      };
    }

    case ix3ActionTypes.IX3_UI_TIMELINE_RESIZED: {
      const {
        payload: {timelineHeight},
      } = action;

      const nextTimelineHeight = clamp(
        timelineHeight,
        IX3_TIMELINE_HEIGHT_MIN,
        IX3_TIMELINE_HEIGHT_MAX
      );

      if (nextTimelineHeight === state.uiTimelineHeight) {
        return state;
      }

      return {...state, uiTimelineHeight: nextTimelineHeight};
    }

    case ix3ActionTypes.IX3_UI_TIMELINE_TOGGLED: {
      const {payload: {showTimeline} = {}} = action;

      const nextShowTimeline =
        showTimeline !== undefined ? showTimeline : !state.uiShowTimeline;

      if (state.uiShowTimeline === nextShowTimeline) {
        return state;
      }

      return {
        ...state,
        uiShowTimeline: nextShowTimeline,
      };
    }

    case ix3ActionTypes.IX3_EDIT_INTERACTION_CLOSED: {
      if (state.selectedInteractionId == null) {
        return state;
      }

      return resetAllUiStates(state);
    }

    case ix3ActionTypes.IX3_EDIT_TIMELINE_ACTION_CLOSED: {
      if (
        state.selectedTimelineActionIds.length === 0 &&
        !state.uiShowAddAction
      ) {
        return state;
      }

      return {
        ...state,
        selectedTimelineActionIds: [],
        uiShowAddAction: false,
      };
    }

    case ix3ActionTypes.IX3_UI_SHOW_ADD_ACTION: {
      const {payload: {showAddAction} = {}} = action;
      return {...state, uiShowAddAction: showAddAction ?? true};
    }

    case TAB_SELECTED:
    case TAB_SELECTED_WITH_HIGHLIGHT: {
      if (
        state.selectedInteractionId &&
        action.tab !== RIGHT_TABS.INTERACTIONS
      ) {
        return resetAllUiStates(state);
      }

      return state;
    }

    case ix3ActionTypes.IX3_UI_SCRUB_CHANGED: {
      return {...state, uiIsScrubbing: action.payload.isScrubbing};
    }

    case ix3ActionTypes.IX3_UI_TIMELINE_INDEX_SELECTED: {
      const {index} = action.payload;

      // Validate index is non-negative
      if (index < 0) {
        return state;
      }

      // Skip if already selected
      if (state.uiSelectedTimelineIndex === index) {
        return state;
      }

      return {
        ...state,
        uiSelectedTimelineIndex: index,
        // Clear action selection when switching timelines
        selectedTimelineActionIds: [],
      };
    }

    // Add more actions to reset interaction state here...
    case DESIGNER_LOADED_IN_VIEW_MODE:
    case DESIGNER_LOADED_IN_EDIT_MODE:
    case PAGE_SWITCHED: {
      if (!state.selectedInteractionId) {
        return state;
      }

      return resetAllUiStates(state);
    }

    case SYMBOL_NODE_UNFOCUSED: {
      if (!state.selectedInteractionId) {
        return state;
      }

      // Check if we're completely exiting focus (not just switching components)
      const {UiNodeStore} = action.state;
      const currentFocusedPath = UiNodeStore.focusedSymbolNativeIdPath;
      // If the current focused path will become empty after unfocusing, then reset
      // (unfocusing removes the last element from the path)
      const willBeCompletelyUnfocused = currentFocusedPath.length <= 1;
      if (willBeCompletelyUnfocused) {
        return resetAllUiStates(state);
      }

      return state;
    }

    case NODE_CLICKED: {
      // Check if this NODE_CLICKED action will cause unfocusing (occurs on right clicks outside the focused symbol)
      const {UiNodeStore} = action.state;
      const {focusedSymbolNativeIdPath} = UiNodeStore;
      const {nativeIdPath} = action.payload;

      // If there's a focused symbol and the click is outside it
      if (focusedSymbolNativeIdPath.length > 0 && nativeIdPath) {
        const relativePath = getRelativePath(
          nativeIdPath,
          focusedSymbolNativeIdPath
        );
        const willUnfocus = !relativePath.length;

        if (willUnfocus && state.selectedInteractionId) {
          return resetAllUiStates(state);
        }
      }

      return state;
    }
    case PARENT_SYMBOL_OF_NODE_IN_INSTANCE_FOCUSED: {
      const {ix3InteractionId, clearIx3Selection} = action.payload;
      if (!ix3InteractionId) {
        if (!clearIx3Selection) {
          return state;
        }
        return resetAllUiStates(state);
      }

      return produce(state, (draft) => {
        draft.selectedInteractionId = ix3InteractionId;
      });
    }

    // ============================================================================
    // Analytics-only actions
    // ============================================================================
    case ix3ActionTypes.IX3_TIMELINE_PLAYBACK_TOGGLED: {
      // Analytics only, no state changes
      return state;
    }

    // ============================================================================
    // AI Site Builder
    // ============================================================================
    case ix3ActionTypes.IX3_AISB_ANIMATION_APPLIED: {
      const {animationConfigs, aisbInteractionsToRemove, scope} =
        action.payload;

      return produce(state, (draft) => {
        // Delete existing interactions on the site to prepare for new ones
        if (aisbInteractionsToRemove && aisbInteractionsToRemove.length > 0) {
          aisbInteractionsToRemove.forEach((id) => {
            const interaction = draft.interactions[id];

            if (!interaction) return;
            const timelineIds = interaction.timelineIds || [];
            interaction.deleted = true;

            if (timelineIds.length > 0) {
              timelineIds.forEach((timelineId) => {
                const timeline = draft.timelines[timelineId];
                if (timeline) {
                  timeline.deleted = true;
                }
              });
            }
          });
        }

        const currentPageId = selectCurrentPageId(action.state);
        const interactionScope: PageScope = scope ?? {type: 'site'};

        animationConfigs.forEach(
          ({
            interactionId,
            name,
            triggers,
            timelineId,
            timelineActions,
            conditionalPlayback,
          }) => {
            // Create interaction
            draft.interactions[interactionId] = {
              id: interactionId,
              name,
              pageId: currentPageId,
              triggers,
              timelineIds: [timelineId],
              scope: interactionScope,
              deleted: false,
              conditionalPlayback,
            };

            // Create timeline (AISB - single timeline)
            draft.timelines[timelineId] = {
              id: timelineId,

              pageId: currentPageId,
              actions: timelineActions,
              deleted: false,
            };
          }
        );
      });
    }

    case UNDO:
    case REDO: {
      // The multiplayer undo/redo diff restores/removes interaction data, but
      // selectedInteractionId is a UI field not tracked by the diff. If it now
      // refers to an interaction that no longer exists or has been soft-deleted,
      // clear the IX3 edit mode UI state so the right panel shows the full
      // Style/Settings/Interactions view.
      const selected = state.interactions[state.selectedInteractionId!];
      if (
        state.selectedInteractionId != null &&
        (selected == null || selected.deleted)
      ) {
        return resetAllUiStates(state);
      }
      return state;
    }

    default: {
      const _exhaustiveCheck: never = action;
      return state;
    }
  }
};

// Helpers

function cascadeDeleteInteractions(
  state: IX3State,
  draft: Draft<IX3State>,
  interactionIdsToDelete: Set<string>,
  candidateTimelineIds: Set<string>
): void {
  if (interactionIdsToDelete.size === 0) return;

  if (
    state.selectedInteractionId &&
    interactionIdsToDelete.has(state.selectedInteractionId)
  ) {
    Object.assign(draft, RESET_UI_FIELDS);
  }

  interactionIdsToDelete.forEach((interactionId) => {
    const interaction = draft.interactions[interactionId];
    if (interaction) {
      interaction.deleted = true;
    }
  });

  const retainedTimelineIds = new Set<string>();
  for (const interaction of Object.values(draft.interactions)) {
    if (interaction.deleted) continue;
    interaction.timelineIds.forEach((timelineId) => {
      if (timelineId) retainedTimelineIds.add(timelineId);
    });
  }

  candidateTimelineIds.forEach((timelineId) => {
    if (retainedTimelineIds.has(timelineId)) return;
    const timeline = draft.timelines[timelineId];
    if (timeline) {
      timeline.deleted = true;
    }
  });
}

/**
 * Soft-delete an interaction and cascade to its timelines, skipping any still
 * referenced by another live interaction (via `cascadeDeleteInteractions`).
 * Shared by the `IX3_INTERACTION_DELETED` reducer and the headless delete
 * responder so live and headless deletes match. Replaces the reducer's old
 * unconditional timeline delete, which could strand a shared timeline.
 */
export function applyDeleteInteraction(
  state: IX3State,
  id: InteractionId
): IX3State {
  if (state.interactions[id] == null) return state;
  return produce(state, (draft) => {
    const interaction = draft.interactions[id];
    if (!interaction) return;
    const candidateTimelineIds = new Set<string>(
      interaction.timelineIds.filter(Boolean)
    );
    cascadeDeleteInteractions(
      state,
      draft,
      new Set([id]),
      candidateTimelineIds
    );
  });
}

/**
 * Timeline ids still referenced by another non-deleted interaction, so a shared
 * timeline isn't soft-deleted out from under one that still uses it.
 */
export function collectRetainedTimelineIds(
  interactions: Record<string, Interaction>,
  excludeInteractionId: string
): Set<string> {
  const retained = new Set<string>();
  for (const interaction of Object.values(interactions)) {
    if (interaction.deleted) continue;
    if (interaction.id === excludeInteractionId) continue;
    for (const timelineId of interaction.timelineIds) {
      if (timelineId) retained.add(timelineId);
    }
  }
  return retained;
}

/**
 * Whether a pages-scoped interaction covers a page; other scopes pass through.
 * Shared with `selectAllInteractionsTimelinesByPageScope` so the panel's page
 * list and the canvas selection below can't drift apart.
 */
export function isInteractionOnPage(
  interaction: Pick<Interaction, 'scope'>,
  pageId: string
): boolean {
  const scope = interaction.scope;
  return scope?.type === 'pages' ? scope.value.includes(pageId) : true;
}

function isInteractionInCurrentContext(
  interaction: Interaction,
  currentPageId: string
): boolean {
  // Whether a component-scoped interaction is on screen depends on the focused
  // component, the nested-component map, and the components flag — all in
  // designer-flux, unreadable from here. Stay conservative instead.
  if (interaction.scope?.type === 'component') return false;
  return isInteractionOnPage(interaction, currentPageId);
}

/**
 * The interaction's own timelines that are still live. A timeline another
 * interaction retained is not this one's to write, and a soft-deleted one is
 * gone, so a patch or baseline naming either is stale. Shared by the patch and
 * the baseline so both mean the same thing by "own live timeline".
 */
function ownLiveTimelineIds(
  draft: Draft<IX3State>,
  interaction: Draft<Interaction>
): Set<string> {
  return new Set<string>(
    interaction.timelineIds.filter((id) => {
      const timeline = draft.timelines[id];
      return timeline != null && !timeline.deleted;
    })
  );
}

/**
 * Applies a `ComponentInteractionPatch` to `interaction` and its timelines.
 *
 * Shared by `IX3_COMPONENT_INTERACTION_RECONCILED` and the trigger create,
 * update and delete actions, which carry the reconcile their edit calls for as
 * `componentPatch`: one action is one undo step, so a retarget and the fallback
 * targets and baseline that follow it must land in the same dispatch or a
 * single Undo reverts only half of the edit.
 */
function applyComponentInteractionPatch(
  draft: Draft<IX3State>,
  interaction: Draft<Interaction>,
  patch: ComponentInteractionPatch
): void {
  // A peer's delete is a soft one, and a timeline the deleted interaction
  // listed can still be live because another interaction retained it. Writing
  // the patch through the tombstone would edit that other interaction's
  // timeline.
  if (interaction.deleted) return;

  // A patch is derived against the state one client saw. Timelines are
  // soft-deleted, and one can be retained for another interaction, so a stale
  // patch arriving after a peer removed or replaced a timeline would otherwise
  // edit a deleted or shared one and still advance the baseline. Only this
  // interaction's own live timelines are written.
  const liveTimelineIds = ownLiveTimelineIds(draft, interaction);
  // Set when an entry named a timeline that is no longer this interaction's
  // live one, or an action that is no longer on it: the edit it asked for never
  // landed, so the baseline below must not claim the settings are handled.
  let skippedTimelineEdit = false;
  const ownedTimeline = (timelineId: string) => {
    if (liveTimelineIds.has(timelineId)) return draft.timelines[timelineId];
    skippedTimelineEdit = true;
    return undefined;
  };

  const removedActionIds = new Set<string>();
  const removedActions = new Map<string, TimelineAction>();
  for (const {timelineId, actionId} of patch.removeActions) {
    const timeline = ownedTimeline(timelineId);
    if (timeline == null) continue;
    const actionIndex = timeline.actions.findIndex(
      (timelineAction) => timelineAction.id === actionId
    );
    if (actionIndex === -1) continue;
    const removedAction = timeline.actions[actionIndex];
    if (removedAction != null) {
      removedActionIds.add(actionId);
      removedActions.set(actionId, removedAction);
    }
    timeline.actions.splice(actionIndex, 1);
  }
  if (removedActionIds.size > 0) {
    applyOnTimelineActionDeletedDraftHooks({
      draft,
      deletedActionIds: removedActionIds,
      deletedActions: removedActions,
    });
    // Matches IX3_TIMELINE_ACTIONS_BULK_DELETED: an automatically removed
    // action (e.g. the backdrop action this reconcile drops) can be the
    // one currently selected, and selection has no other path to notice.
    draft.selectedTimelineActionIds = draft.selectedTimelineActionIds.filter(
      (id) => !removedActionIds.has(id)
    );
  }

  for (const {timelineId, action: newAction} of patch.addActions) {
    const timeline = ownedTimeline(timelineId);
    if (timeline == null) continue;
    timeline.actions.push(newAction);
  }

  for (const {timelineId, actionId, targets} of patch.retargetActions) {
    const timeline = ownedTimeline(timelineId);
    const existingAction = timeline?.actions.find(
      (timelineAction) => timelineAction.id === actionId
    );
    if (existingAction) existingAction.targets = targets;
    // A peer may have replaced the action on a timeline that is still live; the
    // retarget it asked for never landed, so the baseline must not claim it did.
    else if (timeline != null) skippedTimelineEdit = true;
  }

  // Written straight rather than through the percent-canvas clamp: a
  // component interaction's timelines carry a role, and only the
  // mouse-move roles set `usePercentCanvas` — so `canvasDuration` is
  // always absent here and every clamp would no-op. A future component
  // whose roles DO use a percent canvas would need this reconsidered.
  for (const {timelineId, actionId, position} of patch.retimeActions) {
    const timeline = ownedTimeline(timelineId);
    const existingAction = timeline?.actions.find(
      (timelineAction) => timelineAction.id === actionId
    );
    if (existingAction) existingAction.timing.position = position;
    else if (timeline != null) skippedTimelineEdit = true;
  }

  if (patch.renameTo != null) {
    interaction.name = patch.renameTo;
  }

  // The baseline the NEXT reconcile measures against. Recorded here rather
  // than held in Designer memory so a settings change made outside a
  // Designer session is still a transition the next one can see — see
  // `componentSeedSchema`.
  // Left unrecorded after a skipped timeline edit, the baseline lets the next
  // reconcile re-derive the edit instead of treating it as done.
  if (patch.setComponentSeed != null && !skippedTimelineEdit) {
    interaction.componentSeed = patch.setComponentSeed;
  }
}

/**
 * Whether an `IX3_INTERACTION_UPSERTED` with this pre-action record is a
 * create rather than an update — including a resurrection, where the pre-
 * action record exists but is soft-deleted (e.g. a popover interaction
 * deleted then recreated in the same session lands on the same
 * deterministic seed id). The single discriminator every site that needs to
 * tell the two apart shares: the store reducer (selection), the panel
 * handler (`onAction.ts`, closing panels IX3 edit mode can't coexist with),
 * and the analytics reducer (`IX3 Interaction Created` vs `Updated`).
 */
export const isUpsertCreate = (
  previous: Interaction | undefined
): previous is undefined | (Interaction & {deleted: true}) =>
  previous == null || previous.deleted;

/**
 * Whether a create should auto-select the new interaction the way the native
 * flow does. `IX3_INTERACTION_UPSERTED` comes from a Designer Extension write
 * as well as from the native Popover seeding (`useComponentInteractionSeeding`),
 * and the extension write in particular can move the Designer out from under
 * someone who never asked. The interaction is created either way.
 */
export function shouldSelectUpsertedInteraction(
  state: DesignerStores,
  interaction: Interaction
): boolean {
  // Raw, not `selectSelectedIxVersion` — that defaults unset to 'ix2', which
  // would withhold selection from everyone who has never touched the version
  // picker. Only an explicit choice should.
  const {selectedIxVersion} = state.UiStore;
  if (selectedIxVersion === 'ix1' || selectedIxVersion === 'ix2') {
    return false;
  }
  return isInteractionInCurrentContext(interaction, selectCurrentPageId(state));
}

/**
 * Create or replace an interaction and its full timeline set, soft-deleting any
 * dropped timeline no other live interaction uses. Data only (no selection/UI)
 * so page automation and the live reducer stay in sync; the reducer adds
 * create-time selection itself (see the `IX3_INTERACTION_UPSERTED` case).
 */
export function applyUpsertInteraction(
  state: IX3State,
  interaction: Interaction,
  timelines: Timeline[]
): IX3State {
  return produce(state, (draft) => {
    const previous = state.interactions[interaction.id];
    if (previous) {
      const nextTimelineIds = new Set(interaction.timelineIds.filter(Boolean));
      const retained = collectRetainedTimelineIds(
        state.interactions,
        interaction.id
      );
      for (const oldTimelineId of previous.timelineIds) {
        if (
          !oldTimelineId ||
          nextTimelineIds.has(oldTimelineId) ||
          retained.has(oldTimelineId)
        ) {
          continue;
        }
        const timeline = draft.timelines[oldTimelineId];
        if (timeline && !timeline.deleted) {
          timeline.deleted = true;
        }
      }
    }
    for (const timeline of timelines) {
      draft.timelines[timeline.id] = timeline;
    }
    draft.interactions[interaction.id] = interaction;
  });
}

const RESET_UI_FIELDS: Pick<
  IX3State,
  | 'selectedInteractionId'
  | 'selectedTimelineActionIds'
  | 'uiShowAddAction'
  | 'uiIsScrubbing'
  | 'uiSelectedTimelineIndex'
> = {
  selectedInteractionId: null,
  selectedTimelineActionIds: [],
  uiShowAddAction: false,
  uiIsScrubbing: false,
  uiSelectedTimelineIndex: 0,
};

function resetAllUiStates(state: IX3State): IX3State {
  return {...state, ...RESET_UI_FIELDS};
}
